// Customer File page — the central "customer centre" where a collections
import { ukDate } from "@/lib/utils";
import { cur, fmtMoney, isAusDesk, rl, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";
// handler works on ONE case end-to-end. Replaces the old side-drawer.
//
// Layout:
//   ┌─────────────────────────────────────────────────────────────────┐
//   │  Header: customer name · SF# · stage · handler · close          │
//   ├─────────────────────────────────────────────────────────────────┤
//   │  LoanDisk banner (slim, full width)                             │
//   ├──────────┬────────────────────────────────────┬──────────────────┤
//   │  Monday  │   Outlook split-pane (list+read)   │   Aircall calls  │
//   │  card    │                                    │   ─────────────  │
//   │  ──────  │                                    │   Aircall SMS    │
//   │  Legal   │                                    │   ─────────────  │
//   │  docs    │                                    │   Aircall Phone  │
//   ├──────────┴────────────────────────────────────┴──────────────────┤
//   │  Unified Audit Log (calls + SMS + emails + notes + stage diff)  │
//   └─────────────────────────────────────────────────────────────────┘
import { useState, useEffect, useCallback, useMemo, Fragment, useRef } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useRegion } from "@/contexts/RegionContext";
import { toast } from "sonner";
import { CourtReferenceInline } from "@/pages/legal/CourtReferenceInline";
import {
  ArrowLeft, ExternalLink, Loader2, Mail, MessageSquare, Phone, ChevronLeft,
  FileSignature, Filter, Search, ArrowRightCircle, Send, X, FileText,
  PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen,
  Eye, Trash2, Users, Plus, ChevronDown, ChevronRight,
  CalendarClock, CheckCircle2, AlertOctagon, Ban, Banknote, Pencil,
  RefreshCw, XCircle, BarChart3, Download, Scale, ListChecks, Landmark, ChevronUp, Gavel } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusTagsButton, StatusTagChips, useAccountStatuses } from "@/components/collections/StatusTags";
import { TriagePills } from "@/components/collections/TriagePills";
import { JourneyBanner } from "@/components/collections/JourneyBanner";
import { LegalDeskBanner } from "@/pages/legal/legalDesk";
import { LegalProcessMap } from "@/components/legal-map/LegalProcessMap";
import { HandToLegalDialog } from "@/components/collections/JourneyActions";
import { PgInsolvencyBadge } from "@/components/collections/PgInsolvency";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import IssueDocumentComposer from "@/pages/collections/IssueDocumentComposer";
import RestructureDialog from "@/pages/collections/RestructureDialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import {
  getAgeingCase, getAgeingHandlers, sortNames, getAgeingStages, updateAgeingCase, setLegalCaseFlags,
  getAgeingLoanDiskSnapshot,
  getAgeingLoanSchedule,
  getAgeingM365Emails, sendAgeingM365Email, syncAgeingM365History,
  getAgeingM365EmailFull, replyAgeingM365Email,
  syncAgeingAircallCalls, getAgeingCaseFiles, getAgeingUnderwritingNotes,
  getAgeingAircallCalls, getAgeingMondayCard, deleteAgeingNote,
  getAgeingContacts, addAgeingAdditionalContact, deleteAgeingAdditionalContact,
  getAgeingRoleOptions, syncAgeingGuarantors, discoverAgeingEmailContacts,
  getAgeingEngagement,
  syncAircallCallsLog, getAircallCallsLogStatus,
  createAgeingP2P, updateAgeingP2P, deleteAgeingP2P, rescheduleAgeingP2P,
  getAgeingBorrowerLoans, getAgeingBorrowerLoanRepayments, getAgeingBorrowerLoanSchedule, followLoanChain,
  m365SearchMailbox,
  previewLdStatusChange, retryLdStatusPush, setCapitalAtRisk,
  refreshAirtableNotes, getLegalNotesBundle, createLegalNote, updateLegalNote, deleteLegalNote,
  getLegalCaseFiles, getLegalCaseFilePreviewUrl, saveEmailToFiles, downloadCaseFilesZip,
  deleteLegalCaseFile, renameLegalCaseFile, uploadCaseFile,
  listChaserEmailTemplates, renderChaserEmailForCase, previewChaserEmailForCase,
  getCaseContactEmails, pingCaseViewing, listLegalEvents,
  addCaseToPortfolio, removeCaseFromPortfolio, setLegalCourtCaseNumber,
} from "@/lib/api";
import { GenerateLegalDocDialog } from "./GenerateLegalDocWizard";
import WitnessStatementDialog from "@/pages/legal/witness/WitnessStatementDialog";
import N244Dialog from "@/pages/legal/n244/N244Dialog";
import { ServicePackDialog } from "./ServicePackDialog";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { PostageTab } from "./PostageTab";

import {
  LoanDiskSnapshotCard, OnListBadge, Stat, SearchInboxDialog,
} from "./LoanAgeing";
import { LoanDiskTimelineStrip } from "@/components/collections/LoanDiskTimelineStrip";
import { LoanPartiesCard } from "@/components/collections/LoanPartiesCard";
import { ContactTimelineView } from "./ContactTimelineView";
import CompaniesHouseTab from "./CompaniesHouseTab";
import AsicAbrTab from "./AsicAbrTab";
import GoCardlessTab from "./GoCardlessTab";
import CreditorWatchTab from "./CreditorWatchTab";
import CapayAccountTab from "./CapayAccountTab";
import PanelErrorBoundary from "@/components/PanelErrorBoundary";
import { deskDayKey } from "./ContactTimelineView";
import LegalScheduleTab from "@/pages/legal/LegalScheduleTab";
import CloseCaseDialog from "@/pages/legal/CloseCaseDialog";
import { NextActionGuardDialog, caseNeedsNextAction } from "@/pages/legal/NextActionGuard";
import { useAuth } from "@/contexts/AuthContext";
import { CaseComplaintsPanel } from "@/pages/complaints/ComplaintsRegister";
import { useIsPhone } from "@/hooks/useIsPhone";
import PhoneCustomerFile from "./phone/PhoneCustomerFile";

// Stage tones — 2-stage model (Jun 30 2026). Legacy stage values still
// surface in old DB rows mid-migration; tones preserved for those too.
const STAGE_TONES = {
  open_loan:        "bg-amber-100 text-amber-900 border-amber-300",
  legal:            "bg-red-100 text-red-900 border-red-300",
  // legacy values — rendered if a row hasn't been re-normalised yet
  arrears:          "bg-amber-100 text-amber-900 border-amber-300",
  capital_at_risk:  "bg-orange-100 text-orange-900 border-orange-300",
  bad_debt:         "bg-red-100 text-red-900 border-red-300",
  restructured:     "bg-blue-100 text-blue-900 border-blue-300",
  written_off:      "bg-slate-200 text-slate-700 border-slate-300",
};

const fmtGBP = (n) => fmtMoney(n, 0);
const fmtGBPdp = (n) => fmtMoney(n, 2);

export default function CustomerFile() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const { pathname } = useLocation();
  const { currentRegion } = useRegion();
  const [searchParams] = useSearchParams();
  // ?source=legal flips this case view into Legal-Case-Tracker mode:
  //   • Back button returns to /legal/case-tracker instead of /collections/ageing
  //   • Header pill says "Legal" + shows the detailed legal_stage when set
  //   • Tab label "P2Ps" is rendered as "Legal Schedule" (Phase 2 swaps content)
  //   • Tab label "Files" is renamed "Files & Legal Docs"
  // The underlying data is identical — legal cases live in the same
  // collections_cases collection, just with stage in {bad_debt, written_off}
  // or with a `legal_stage` field set.
  const isLegalMode = searchParams.get("source") === "legal";
  // Desk: /aus/… is Australia, the Legal view (/legal/case/:id) is the UK desk (see legal/legalDesk.jsx), else the switcher.
  const desk = pathname.startsWith("/aus/") ? "AUS" : (isLegalMode || pathname.startsWith("/legal/")) ? "UK" : currentRegion;
  setCollectionsRegion(desk);   // module flag → api interceptor + money formatting
  const isPortfolioMode = searchParams.get("source") === "portfolio";
  const portfolioPath = `${ageingBasePath().replace(/\/ageing$/, "")}/portfolio-accounts`;
  const backTo = isLegalMode ? "/legal/case-tracker" : isPortfolioMode ? portfolioPath : ageingBasePath();
  const backLabel = isLegalMode ? "Legal Case Tracker" : isPortfolioMode ? "Portfolio Accounts" : "Loan Arrears Ageing";
  // Optional deep-link to a specific centre tab (e.g. ?tab=p2ps from the
  // Collections Calendar external-link icon).
  const initialTab = searchParams.get("tab") || null;

  const [caseRow, setCaseRow] = useState(null);
  const [handlers, setHandlers] = useState([]);
  const [legalHandlers, setLegalHandlers] = useState([]);
  const [showMore, setShowMore] = useState(() => localStorage.getItem("sf-case-more") === "1");
  useEffect(() => { localStorage.setItem("sf-case-more", showMore ? "1" : "0"); }, [showMore]);   // Legal group roster (Dean, 8 Oct 2026)
  // Emma de31b7a3fd — a live legal file can't be left without a next action in the diary
  const { user: authUser } = useAuth();
  const [guardOpen, setGuardOpen] = useState(false);
  const goBack = async () => {
    if (isLegalMode) {
      try { if (await caseNeedsNextAction(caseRow)) { setGuardOpen(true); return; } } catch { /* fall through */ }
    }
    navigate(backTo);
  };
  const accountStatuses = useAccountStatuses();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const [c, h, lh, s] = await Promise.all([
        getAgeingCase(caseId),
        getAgeingHandlers("collections"),
        getAgeingHandlers("legal").catch(() => ({ handlers: [] })),
        getAgeingStages(),
      ]);
      // /cases/{id} returns {case: {...}} in some versions OR {...} directly
      const found = c.case || c;
      if (!found || !found.case_id) {
        setErr("Case not found");
      } else {
        setCaseRow(found);
        setErr(null);
      }
      setHandlers(h.handlers || []);
      setLegalHandlers(lh.handlers || h.handlers || []);
      setStages(s.stages || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { reload(); }, [reload]);

  // ── Case-viewing heartbeat ─────────────────────────────────────────────
  // Ping the backend every 30s while we're on this case page. The Aircall
  // webhook uses this as a last-resort case-linker for SMS composed
  // inside the dock (the dock is an iframe and knows nothing about our
  // route). Without this, a dock-sent SMS to an "unknown" phone (e.g.
  // ops texting their own mobile) can never be linked back to the case.
  useEffect(() => {
    if (!caseId) return;
    // First ping immediately, then every 30s.
    const ping = () => pingCaseViewing(caseId).catch(() => {/* silent */});
    ping();
    const iv = setInterval(ping, 30_000);
    return () => clearInterval(iv);
  }, [caseId]);

  if (loading && !caseRow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50/50">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-8">
        <div>
          <div className="text-2xl font-semibold mb-2">😕 {err}</div>
          <Button onClick={() => navigate(backTo)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to {backLabel}
          </Button>
        </div>
      </div>
    );
  }
  const stageMeta = stages.find(s => s.key === caseRow.stage);

  if (isPhone) {
    return (
      <>
        {guardOpen && (
          <NextActionGuardDialog caseRow={caseRow} actor={authUser?.name || authUser?.email || "Ops"} onClose={() => setGuardOpen(false)} onLeave={() => { setGuardOpen(false); navigate(backTo); }} />
        )}
        <PhoneCustomerFile caseRow={caseRow} stageMeta={stageMeta} reload={reload} isLegalMode={isLegalMode} desk={desk} onBack={goBack}
          renderTab={(key) => tabContent(key, { caseRow, onChanged: reload, isLegalMode })} />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/40 -mx-3 -my-3 px-3 py-3 sm:-mx-6 sm:-my-6 sm:px-6 sm:py-4 space-y-4" data-testid="customer-file-page">
      {desk === "UK" && currentRegion === "AUS" && <LegalDeskBanner />}
      {/* Header bar — wraps onto a second row when the legal-mode chips don't fit (Dean, 8 Oct 2026) */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-white border rounded-lg px-4 py-3 shadow-sm" data-testid="customer-file-header">
        <div className="flex items-center gap-3 min-w-[280px] flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            data-testid="customer-file-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {guardOpen && (
            <NextActionGuardDialog caseRow={caseRow} actor={authUser?.name || authUser?.email || "Ops"} onClose={() => setGuardOpen(false)} onLeave={() => { setGuardOpen(false); navigate(backTo); }} />
          )}
          <div className="border-l h-6 mx-1" />
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate flex items-center gap-2" data-testid="customer-file-name">
              {caseRow.customer_name || "—"}
              {isLegalMode && (
                <Badge variant="outline" className="text-[10px] bg-red-50 text-red-900 border-red-300" data-testid="customer-file-legal-pill">
                  LEGAL
                </Badge>
              )}
              <PgInsolvencyBadge caseRow={caseRow} testid="customer-file-pg-insolvency-pill" />
              {caseRow.portfolio_tracking?.active && (
                <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-800 border-sky-300" data-testid="customer-file-portfolio-pill" title={`Added by ${caseRow.portfolio_tracking.added_by || "—"}`}>
                  PORTFOLIO{caseRow.not_in_latest_arrears ? " · CURRENT" : ""}
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
              <span className="font-mono">{caseRow.loan_application_id}</span>
              {caseRow.borrower_director_name && <span>· Director: {caseRow.borrower_director_name}</span>}
              {isLegalMode && caseRow.legal_stage && (
                <span className="text-red-700 font-medium">· {humanLegalStage(caseRow.legal_stage)}</span>
              )}
              {caseRow.manual_statuses?.length > 0 && (
                <span className="inline-flex items-center gap-1" data-testid="customer-file-status-tags">
                  <StatusTagChips keys={caseRow.manual_statuses} statuses={accountStatuses} testid="customer-file-status-tag" />
                </span>
              )}
              <TriagePills row={caseRow} size="md" />
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 max-w-full" data-testid="customer-file-header-actions">
          <EngagementBadge caseId={caseRow.case_id} />
          <OnListBadge lastSeen={caseRow.last_arrears_report_at} />
          <Badge variant="outline" className={`text-xs ${STAGE_TONES[caseRow.stage] || ""}`}>
            {stageMeta?.label || caseRow.stage}
          </Badge>
          {caseRow.capital_at_risk && (
            <Badge
              variant="outline"
              className="text-xs bg-orange-100 text-orange-900 border-orange-300"
              data-testid="customer-file-car-badge"
              title={caseRow.capital_at_risk_reason || "Flagged as Capital at Risk"}
            >
              ⚠️ Capital at Risk
            </Badge>
          )}
          {caseRow.handling_marker === "out_of_jurisdiction" && (
            <Badge
              variant="outline"
              className="text-xs bg-sky-100 text-sky-900 border-sky-300"
              data-testid="customer-file-ooj-badge"
              title="Out of jurisdiction (Scottish / NI case) — our direct actions are limited"
            >
              🌍 Out of Jurisdiction
            </Badge>
          )}
          {caseRow.handling_marker === "external" && (
            <Badge
              variant="outline"
              className="text-xs bg-indigo-100 text-indigo-900 border-indigo-300"
              data-testid="customer-file-external-badge"
              title={caseRow.external_firm ? `Handled externally by ${caseRow.external_firm}` : "Handled externally (instructed law firm)"}
            >
              🏛 External{caseRow.external_firm ? ` — ${caseRow.external_firm}` : ""}
            </Badge>
          )}
          {caseRow.legal_closure?.type === "write_off" && (
            <Badge
              variant="outline"
              className="text-xs bg-slate-200 text-slate-800 border-slate-400"
              data-testid="customer-file-writeoff-badge"
              title={caseRow.legal_closure.reason || "Written off — no further action will be taken"}
            >
              ✕ Written Off — Final
            </Badge>
          )}
          {caseRow.legal_closure?.type === "fully_paid" && (
            <Badge variant="outline" className="text-xs bg-emerald-100 text-emerald-900 border-emerald-400"
              data-testid="customer-file-fullypaid-badge" title={caseRow.legal_closure.reason || "Paid in full — legal file closed"}>
              ✓ Closed — Fully Paid {caseRow.legal_closure.paid_at ? new Date(caseRow.legal_closure.paid_at + "T00:00:00").toLocaleDateString("en-GB") : ""}
            </Badge>
          )}
          {caseRow.legal_closure?.type === "closed_review" && (
            <Badge
              variant="outline"
              className="text-xs bg-amber-100 text-amber-900 border-amber-300"
              data-testid="customer-file-closedreview-badge"
              title={caseRow.legal_closure.reason || "Closed — diarised for a future review"}
            >
              ⏰ Closed — Review {new Date(caseRow.legal_closure.review_at + "T00:00:00").toLocaleDateString("en-GB")}
            </Badge>
          )}
          {caseRow.legal_closure?.type === "resolved_collections" && (
            <Badge variant="outline" className="text-xs bg-sky-100 text-sky-900 border-sky-300" data-testid="customer-file-resolved-badge"
              title={`${caseRow.legal_closure.sub_reason_label || "Commitment met outside Legal"}${caseRow.legal_closure.reason ? ` — ${caseRow.legal_closure.reason}` : ""}${caseRow.legal_closure.check_in_at ? ` · check-in ${new Date(caseRow.legal_closure.check_in_at + "T00:00:00").toLocaleDateString("en-GB")}` : ""}`}>
              ↩ Closed — Resolved via Collections{caseRow.legal_closure.sub_reason ? ` · ${(caseRow.legal_closure.sub_reason_label || "").split(" — ")[0].split(" (")[0]}` : ""}
            </Badge>
          )}
          {/* Noise pass (Dean, 13 Oct 2026): owners + stage stay in view; the rest sits behind "More" — the Journey banner drives the file */}
          <HandlerControl caseRow={caseRow} handlers={handlers} onChanged={reload} labelled={isLegalMode} />
          {isLegalMode && <LegalOwnerControl caseRow={caseRow} handlers={legalHandlers} onChanged={reload} />}
          <StageControl caseRow={caseRow} stages={stages} onChanged={reload} />
          {isLegalMode && <CloseCaseButton caseRow={caseRow} onChanged={reload} />}
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-600" onClick={() => setShowMore(v => !v)} data-testid="customer-file-more-toggle" title="Action list · portfolio · capital at risk · markers · status tags">
            {showMore ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} More
          </Button>
        </div>
      </div>
      {showMore && (
        <div className="flex items-center gap-2 flex-wrap -mt-2 pl-1" data-testid="customer-file-more-row">
          {isLegalMode && <ActionListButton caseRow={caseRow} />}
          <PortfolioButton caseRow={caseRow} onChanged={reload} />
          <CapitalAtRiskButton caseRow={caseRow} onChanged={reload} />
          <LegalFlagsButton caseRow={caseRow} onChanged={reload} />
          <StatusTagsButton caseRow={caseRow} onChanged={reload} />
        </div>
      )}

      {/* LoanDisk banner — the loan's figures and progress stay at the very top (Dean, 14 Oct 2026) */}
      <LoanDiskBanner caseRow={caseRow} />

      {/* Case progress — party-by-party process map with the Journey folded in (Dean, 14 Oct 2026) — legal view only */}
      {isLegalMode && <LegalProcessMap caseRow={caseRow} onChanged={reload} />}

      {/* Two-axis Journey banner — collections view only (the legal view carries it inside Case progress) */}
      {!isLegalMode && <JourneyBanner caseRow={caseRow} onChanged={reload} isLegalMode={isLegalMode} />}

      {/* Emma #12 — instructed-externally warning banner */}
      {caseRow.handling_marker === "external" && <ExternalHandlingBanner caseRow={caseRow} />}

      {/* Guarantor bankruptcy / IVA now lives in the Case progress header (Dean, Oct 2026); the badge above keeps the collections view informed */}

      {/* Full-width centre tabs — left & right rails replaced entirely */}
      <MainLayout caseRow={caseRow} onChanged={reload} isLegalMode={isLegalMode} initialTab={initialTab} />
    </div>
  );
}

// Turn a `legal_stage` key (e.g. "sd2_served") into a human-friendly label.
// Used by the header sub-line when in Legal mode.
function humanLegalStage(key) {
  if (!key) return "";
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// ─── Full-width centre tabs (left/right rails removed) ─────────────────────
const VALID_CENTRE_TABS = [
  "timeline", "calls", "sms", "inbox", "contacts", "files",
  "p2ps", "pattern", "complaints", "ch", "gc", "cw", "schedule", "loans", "capay",
];

// One switch for the centre tabs — shared by the desktop MainLayout and the phone layout.
function tabContent(centreView, { caseRow, onChanged, isLegalMode }) {
  return centreView === "timeline" ? <ContactTimelineView caseRow={caseRow} onChanged={onChanged} isLegalMode={isLegalMode} />
       : centreView === "calls"    ? <CallsTab     caseRow={caseRow} />
       : centreView === "sms"      ? <SmsTab       caseRow={caseRow} />
       : centreView === "inbox"    ? <OutlookSplitPane caseRow={caseRow} isLegalMode={isLegalMode} />
       : centreView === "contacts" ? <ContactsTab  caseRow={caseRow} />
       : centreView === "files"    ? <FilesTab     caseRow={caseRow} isLegalMode={isLegalMode} />
       : centreView === "postage"  ? <PostageTab   caseRow={caseRow} />
       : centreView === "p2ps"     ? (isLegalMode
                                        ? <LegalScheduleTab caseRow={caseRow} onChanged={onChanged} />
                                        : <P2PsTab caseRow={caseRow} onChanged={onChanged} />)
       : centreView === "pattern"  ? <ContactPatternView caseRow={caseRow} />
       : centreView === "complaints" ? <CaseComplaintsPanel caseRow={caseRow} />
       : centreView === "ch"       ? (isAusDesk() ? <AsicAbrTab caseRow={caseRow} /> : <CompaniesHouseTab caseRow={caseRow} />)
       : centreView === "gc"       ? (isAusDesk() ? <GoCardlessTab caseRow={caseRow} />
                                        : <ComingSoonPanel title="GoCardless (UK)" note="Mandate, schedule, in-flight and failed-payment tools for UK GoCardless are coming soon — the AUS desk has them today." region="UK" />)
       : centreView === "cw"       ? <CreditorWatchTab caseRow={caseRow} />
       : centreView === "capay"    ? <CapayAccountTab caseRow={caseRow} />
       : centreView === "loans"    ? <BorrowerLoansView caseRow={caseRow} />
       :                            <LoanScheduleView caseRow={caseRow} />;
}

function MainLayout({ caseRow, onChanged, isLegalMode = false, initialTab = null }) {
  // In legal mode, default to Timeline (same as collections) but the P2Ps
  // tab is repurposed as "Legal Schedule" (placeholder until Phase 2 wires
  // DN/SD2/Hearing dates). Files tab is renamed to "Files & Legal Docs".
  const [centreView, setCentreView] = useState(
    initialTab && VALID_CENTRE_TABS.includes(initialTab) ? initialTab : "timeline"
  );

  return (
    <div className="space-y-2">
      <CentreTabs view={centreView} onChange={setCentreView} isLegalMode={isLegalMode} />
      <div data-testid={`centre-tab-content-${centreView}`}>
        <PanelErrorBoundary name={`${centreView} tab`} resetKey={`${centreView}-${caseRow.case_id}`}>
        {tabContent(centreView, { caseRow, onChanged, isLegalMode })}
        </PanelErrorBoundary>
      </div>
    </div>
  );
}

// Phase 2 (shipped): the Legal Schedule tab shows the case's legal calendar
// events with traffic-light priorities + the Issuing Court card. Lives in
// pages/legal/LegalScheduleTab.jsx.

// Full-width tab wrappers — they re-use the existing card components but
// stretch them across the page now that the rails are gone.
function CallsTab({ caseRow }) {
  return <div className="grid grid-cols-1 gap-4"><AircallCallsCompact caseRow={caseRow} /></div>;
}
function SmsTab({ caseRow }) {
  return <div className="grid grid-cols-1 gap-4"><AircallSmsCompact caseRow={caseRow} /></div>;
}
function ContactsTab({ caseRow }) {
  return (
    <div className="grid grid-cols-1 gap-4">
      <LoanPartiesCard caseId={caseRow.case_id} />
      <ContactsCard caseRow={caseRow} />
    </div>
  );
}
function ExternalHandlingBanner({ caseRow }) {
  const firm = caseRow.external_firm;
  const when = caseRow.handling_marker_set_at
    ? new Date(caseRow.handling_marker_set_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : null;
  return (
    <div
      className="w-full rounded-md border-2 border-red-600 bg-red-600 text-white px-4 py-2.5 flex items-start gap-3 shadow-sm"
      role="alert"
      data-testid="customer-file-external-banner"
    >
      <AlertOctagon className="w-5 h-5 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="font-semibold text-sm tracking-wide">
          INSTRUCTED EXTERNALLY{firm ? ` — ${firm}` : ""}
        </div>
        <div className="text-xs text-red-50 mt-0.5">
          This file is being run by an external law firm. Do not action directly — check with {firm || "the instructed firm"} before any call, SMS, email or document goes out.
          {(caseRow.handling_marker_set_by || when) && (
            <span className="text-red-100/90"> Marked{caseRow.handling_marker_set_by ? ` by ${caseRow.handling_marker_set_by}` : ""}{when ? ` on ${when}` : ""}.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function FilesTab({ caseRow, isLegalMode = false }) {
  const [subTab, setSubTab] = useState("notes");
  return (
    <div className="space-y-4">
      {/* Notes | Files sub-tabs — same UI on Collections & Legal streams */}
      <div className="flex items-center gap-1 bg-white border rounded-lg p-1 shadow-sm w-fit">
        {[{ id: "notes", label: "Notes" }, { id: "files", label: "Files" }].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-1.5 text-xs rounded transition ${subTab === t.id ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
            data-testid={`files-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {subTab === "notes" ? (
        <>
          {/* Legal notes — mirrored on both Legal and Collections tabs (Q4b).
              Will silently show empty-state for non-legal cases. */}
          <LegalNotesCard caseRow={caseRow} />
          {/* Item 8: Underwriting notes moved from Contacts tab → Notes section. */}
          <UnderwritingNotesCard caseRow={caseRow} defaultExpanded={true} />
        </>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Unified file list — OneDrive · LoanDisk · Monday · DocuSign · DocuPilot */}
          <FilesCard caseRow={caseRow} isLegalMode={isLegalMode} />
          <LegalDocsCard caseRow={caseRow} />
        </div>
      )}
    </div>
  );
}

// P2Ps tab — calendar visualisation + list of promises with status actions.
function P2PsTab({ caseRow, onChanged }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <P2PCalendar caseRow={caseRow} />
      <P2PCard caseRow={caseRow} onChanged={onChanged} />
    </div>
  );
}

function CentreTabs({ view, onChange, isLegalMode = false }) {
  const tabs = [
    { id: "timeline", label: "Timeline",        testid: "centre-tab-timeline" },
    { id: "calls",    label: "Calls",           testid: "centre-tab-calls" },
    { id: "sms",      label: "SMS",             testid: "centre-tab-sms" },
    { id: "inbox",    label: "Inbox",           testid: "centre-tab-inbox" },
    { id: "contacts", label: "Contacts",        testid: "centre-tab-contacts" },
    { id: "files",    label: "Notes & Files", testid: "centre-tab-files" },
    ...(isAusDesk() ? [] : [{ id: "postage", label: "Postage", testid: "centre-tab-postage" }]),
    { id: "p2ps",     label: isLegalMode ? "Legal Schedule" : "P2Ps", testid: "centre-tab-p2ps" },
    { id: "pattern",  label: "Pattern",         testid: "centre-tab-pattern" },
    { id: "complaints", label: "Complaints",    testid: "centre-tab-complaints" },
    { id: "ch",       label: rl("registry"),   testid: "centre-tab-ch" },
    { id: "gc",       label: "GoCardless",      testid: "centre-tab-gc" },
    ...(isAusDesk() ? [{ id: "cw", label: "CreditorWatch", testid: "centre-tab-cw" }] : []),
    { id: "schedule", label: "Schedule",        testid: "centre-tab-schedule" },
    { id: "loans",    label: "Previous loans",  testid: "centre-tab-loans" },
    ...(isAusDesk() ? [{ id: "capay", label: "Settlement A/C", testid: "centre-tab-capay" }] : []),
  ];
  return (
    <div className="flex items-center gap-1 bg-white border rounded-lg p-1 shadow-sm flex-wrap">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-3 py-1.5 text-xs rounded transition ${view === t.id ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-100"}`}
          data-testid={t.testid}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Engagement badge — case-header pill driven by /engagement endpoint ────
// 🟢 Engaging · 🟠 Cooling · 🔴 Silent (tooltip lists the reasoning).
const ENGAGEMENT_META = {
  engaging: { tone: "border-emerald-300 bg-emerald-50 text-emerald-800", label: "🟢 Engaging" },
  cooling:  { tone: "border-amber-300 bg-amber-50 text-amber-800",       label: "🟠 Cooling"  },
  silent:   { tone: "border-rose-300 bg-rose-50 text-rose-800",          label: "🔴 Silent"   },
};

function EngagementBadge({ caseId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAgeingEngagement(caseId);
        if (!cancelled) setData(r);
      } catch { /* degrade silently — pill simply doesn't render */ }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  if (!data) return null;
  const meta = ENGAGEMENT_META[data.status] || ENGAGEMENT_META.silent;
  const tooltip = [
    `Engagement score ${data.score}/100`,
    `Inbound 30d: ${data.inbound_30d} · Outbound 30d: ${data.outbound_30d}`,
    data.last_inbound_at ? `Last inbound: ${new Date(data.last_inbound_at).toLocaleString("en-GB")}` : "No inbound on record",
    ...(data.reasons || []).map(r => "· " + r),
  ].join("\n");

  return (
    <Badge
      variant="outline"
      className={`text-xs font-medium ${meta.tone}`}
      title={tooltip}
      data-testid={`engagement-badge-${data.status}`}
    >
      {meta.label}
    </Badge>
  );
}



// ─── LoanDisk banner — slim full-width strip with the live numbers ─────────
function LoanDiskBanner({ caseRow }) {
  const [snap, setSnap] = useState(null);
  const [chain, setChain] = useState(null);   // borrower-loans filtered to this SF# family
  const [restructureOpen, setRestructureOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAgeingLoanDiskSnapshot(caseRow.case_id, { repayments_limit: 5 });
        if (!cancelled) setSnap(r);
      } catch (e) { /* fall back to caseRow */ }
      try {
        const c = await getAgeingBorrowerLoans(caseRow.case_id, { count: 50 });
        if (!cancelled) setChain(c);
      } catch (e) { /* progress bar simply won't render */ }
    })();
    return () => { cancelled = true; };
  }, [caseRow.case_id]);

  const s = snap?.summary || {};
  const principal     = s.principal_amount ?? caseRow.principal_amount;
  const principalOut  = s.principal_outstanding ?? caseRow.total_principal_outstanding;
  const balance       = s.balance_amount ?? caseRow.balance_amount;
  const totalPaid     = s.total_paid;
  const dpd           = s.days_past_due ?? caseRow.days_past_due;
  const fees          = s.fees_balance;
  const penalty       = s.penalty_balance;
  const interest      = s.interest_balance;
  const ldLoanId      = s.loan_id;

  // Loan family progress — sums total_paid across THIS SF# chain (e.g.
  // SF-1234 + SF-1234/2 + SF-1234/3 all count as one loan). Falls back
  // to single-iteration totals when not restructured.
  const progress = useMemo(() => {
    const currentSf = (caseRow.loan_application_id || "").trim();
    if (!currentSf) return null;
    const baseSf = currentSf.replace(/\/\d+$/, "");
    const allLoans = chain?.loans || [];
    // Match any iteration: base, base/2, base/3, …
    const family = allLoans.filter(l => {
      const sf = (l.loan_application_id || "").trim();
      return sf === baseSf || sf.startsWith(baseSf + "/");
    });
    // If borrower-loans hasn't loaded or didn't find the chain, synthesise
    // a single-iteration row from the case + LoanDisk snapshot so the bar
    // still renders for normal (non-restructured) loans.
    const rows = family.length > 0
      ? family
      : [{
          loan_application_id: currentSf,
          total_paid: totalPaid || 0,
          balance:    balance   || 0,
          is_current: true,
        }];
    const totalPaidChain = rows.reduce((s, r) => s + (r.total_paid || 0), 0);
    // Outstanding = balance of the CURRENT iteration only (older iterations'
    // balances were rolled into the next iteration's principal, so summing
    // would double-count).
    const currentRow = rows.find(r => r.is_current) || rows[rows.length - 1];
    const outstanding = currentRow?.balance || 0;
    const committed = totalPaidChain + outstanding;
    if (committed <= 0) return null;
    return {
      paid:         totalPaidChain,
      outstanding,
      committed,
      pct:          Math.min(100, (totalPaidChain / committed) * 100),
      iterations:   rows.length,
      base_sf:      baseSf,
      family,
    };
  }, [chain, caseRow.loan_application_id, totalPaid, balance]);

  return (
    <Card className="bg-gradient-to-r from-slate-50 to-blue-50/30 border-blue-100" data-testid="customer-file-loandisk-banner">
      <CardContent className="px-3 py-2">
        <div className="flex items-center gap-2">
          <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <BannerStat label="Principal"  value={principal    != null ? fmtGBPdp(principal)    : "—"} />
            <BannerStat label="Total Paid" value={totalPaid    != null ? fmtGBPdp(totalPaid)    : "—"} tone={(totalPaid || 0) > 0 ? "green" : null} />
            <BannerStat label="Principal Out" value={principalOut != null ? fmtGBPdp(principalOut) : "—"} />
            <BannerStat label="Balance"    value={balance      != null ? fmtGBPdp(balance)      : "—"} />
            <BannerStat label="DPD"        value={dpd ?? "—"} tone={(dpd || 0) >= 60 ? "red" : (dpd || 0) >= 30 ? "amber" : null} />
            <BannerStat label="Arrears"    value={caseRow.arrears_amount != null ? fmtGBPdp(caseRow.arrears_amount) : "—"} tone={(caseRow.arrears_amount || 0) > 0 ? "red" : null} />
            <BannerStat label="Fees"       value={fees    != null ? fmtGBPdp(fees)    : "—"} tone={(fees    || 0) > 0 ? "amber" : null} />
            <BannerStat label="Penalty"    value={penalty != null ? fmtGBPdp(penalty) : "—"} tone={(penalty || 0) > 0 ? "red"   : null} />
            <BannerStat label="Interest"   value={interest != null ? fmtGBPdp(interest) : "—"} />
            <BannerStat label="Last pmt"   value={caseRow.last_payment_date || "—"} sub={caseRow.last_payment_amount ? fmtGBPdp(caseRow.last_payment_amount) : null} />
          </div>
          {ldLoanId && (
            <Button size="sm" variant="outline" asChild className="h-6 text-[10px] px-2 whitespace-nowrap shrink-0">
              <a href={`https://app.loandisk.com/loan/view/${ldLoanId}`} target="_blank" rel="noreferrer">
                LoanDisk <ExternalLink className="w-2.5 h-2.5 ml-1" />
              </a>
            </Button>
          )}
          {ldLoanId && (balance || 0) > 0 && (
            <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 whitespace-nowrap shrink-0 border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => setRestructureOpen(true)} data-testid="customer-file-restructure-btn"
              title="Create a new SF-xxxx/N loan in LoanDisk carrying this balance — the current loan is never edited">
              Restructure
            </Button>
          )}
        </div>
        {progress && <LoanProgressBar progress={progress} />}
        {chain?.family && <LoanFamilyStrip family={chain.family} caseRow={caseRow} />}
        <LoanDiskTimelineStrip caseId={caseRow.case_id} />
        <RestructureDialog open={restructureOpen} onOpenChange={setRestructureOpen} caseRow={caseRow} />
      </CardContent>
    </Card>
  );
}

// Loan family (Dean, 8 Oct 2026): one file per chain, on the latest iteration — warn + fix when it is not.
function LoanFamilyStrip({ family, caseRow }) {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const stale = !family.on_latest;
  const siblings = family.sibling_files || [];
  if (!stale && siblings.length === 0 && (family.iterations || []).length <= 1) return null;
  const move = async () => {
    if (!window.confirm(`Move this file to ${family.latest_ref}${siblings.length ? ` and fold ${siblings.length} sibling file(s) into it` : ""}? Balance and DPD will follow the latest loan; timeline, diary and documents are kept.`)) return;
    setBusy(true);
    try {
      const r = await followLoanChain(caseRow.case_id);
      toast.success(r.changed ? `File now on ${r.ref}${r.merged?.length ? ` · ${r.merged.length} file(s) folded in` : ""}` : r.reason);
      if (r.changed) navigate(0);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`mt-2 pt-2 border-t text-[11px] flex flex-wrap items-center gap-x-3 gap-y-1 ${stale ? "border-amber-200" : "border-blue-100/80"}`} data-testid="customer-file-family-strip">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Loan family</span>
      <span className="font-mono text-slate-700">{family.base}</span>
      <span className="text-slate-500">{(family.iterations || []).map((it) => it.ref).join(" → ") || family.this_ref}</span>
      {stale && (
        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-amber-50 text-amber-900 border-amber-300 font-medium" data-testid="customer-file-family-stale">
          ⚠ This file points at {family.this_ref} — the live loan is {family.latest_ref}
        </span>
      )}
      {siblings.length > 0 && (
        <span className="text-slate-600" data-testid="customer-file-family-siblings">
          {siblings.length} other file{siblings.length === 1 ? "" : "s"} in this family: {siblings.map((s) => s.loan_application_id).join(", ")}
        </span>
      )}
      {(stale || siblings.length > 0) && (
        <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 ml-auto border-amber-300 text-amber-900 hover:bg-amber-50" disabled={busy} onClick={move} data-testid="customer-file-family-move-btn">
          {busy ? "Moving…" : `Move file to ${family.latest_ref}${siblings.length ? " & merge" : ""}`}
        </Button>
      )}
    </div>
  );
}

function LoanProgressBar({ progress }) {
  const { paid, outstanding, committed, pct, iterations, base_sf, family } = progress;
  // Tone based on % paid (used for the bar fill).
  const fillClass = pct >= 80 ? "bg-emerald-500"
                  : pct >= 50 ? "bg-emerald-400"
                  : pct >= 25 ? "bg-amber-400"
                  :              "bg-rose-400";

  const chainLabel = iterations > 1
    ? `${base_sf} + ${family.slice(0, 3).map(f => f.loan_application_id.replace(base_sf, "")).filter(Boolean).join(" + ")}${iterations > 3 ? "…" : ""}`
    : null;

  return (
    <div className="mt-2 pt-2 border-t border-blue-100/80" data-testid="customer-file-progress-bar">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="flex items-baseline gap-1.5">
          <span className="text-[9px] uppercase tracking-wide text-muted-foreground">Loan progress</span>
          {iterations > 1 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 h-3.5 rounded bg-blue-100 text-blue-800 text-[9px] font-semibold"
              title={`Chain: ${family.map(f => f.loan_application_id).join(" → ")}`}
              data-testid="customer-file-progress-chain"
            >
              {iterations}× restructured
            </span>
          )}
          {chainLabel && (
            <span className="text-[9px] text-muted-foreground hidden md:inline truncate" title={`Chain: ${family.map(f => f.loan_application_id).join(" → ")}`}>
              {chainLabel}
            </span>
          )}
        </div>
        <div className="text-[10px] tabular-nums text-slate-600">
          <span className="font-semibold text-emerald-700">{fmtGBPdp(paid)}</span>
          <span className="mx-1 text-slate-400">paid of</span>
          <span className="font-semibold text-slate-800">{fmtGBPdp(committed)}</span>
          <span className="mx-1 text-slate-400">·</span>
          <span className="font-semibold text-rose-700">{fmtGBPdp(outstanding)}</span>
          <span className="ml-1 text-slate-400">outstanding</span>
          <span className="ml-2 inline-block min-w-[34px] text-right font-bold tabular-nums" data-testid="customer-file-progress-pct">
            {pct.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="relative h-2 rounded-full bg-slate-200 overflow-hidden" title={`${pct.toFixed(1)}% paid back across ${iterations} iteration${iterations === 1 ? "" : "s"}`}>
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all ${fillClass}`}
          style={{ width: `${pct}%` }}
        />
        {/* Iteration separators — small ticks below the bar for chain visibility */}
        {iterations > 1 && family.length > 1 && (
          <div className="absolute inset-0 flex">
            {family.map((it, idx) => {
              const itPaid = it.total_paid || 0;
              const itTotal = (it.total_paid || 0) + (it.balance || 0);
              const widthShare = itTotal / committed;
              if (idx === family.length - 1) return null;
              const left = family.slice(0, idx + 1).reduce((sum, f) => sum + ((f.total_paid || 0) + (f.balance || 0)) / committed, 0) * 100;
              return (
                <div
                  key={it.loan_application_id}
                  className="absolute top-0 bottom-0 w-px bg-white/70"
                  style={{ left: `${left}%` }}
                  title={`${it.loan_application_id}: ${fmtGBPdp(itPaid)} / ${fmtGBPdp(itTotal)} (${widthShare > 0 ? Math.round((itPaid / Math.max(1, itTotal)) * 100) : 0}%)`}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function BannerStat({ label, value, sub, tone }) {
  const toneClass = tone === "red"   ? "text-rose-700"
                  : tone === "amber" ? "text-amber-700"
                  : tone === "green" ? "text-emerald-700"
                  : "text-slate-800";
  return (
    <div className="inline-flex items-baseline gap-1 whitespace-nowrap" data-testid={`banner-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`text-xs font-semibold tabular-nums ${toneClass}`}>
        {value}
      </span>
      {sub && <span className="text-[9px] text-muted-foreground">({sub})</span>}
    </div>
  );
}

// ─── Handler + stage controls (inline in header) ───────────────────────────
// Emma #8 — "Action list": every scheduled action for THIS case only,
// soonest first, overdue flagged. Same data as the Legal Schedule tab.
function ActionListButton({ caseRow }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const load = async () => {
    setOpen(true);
    setEvents(null);
    try {
      const r = await listLegalEvents({ case_id: caseRow.case_id, include_notes: false, limit: 500 });
      setEvents((r.events || [])
        .filter(e => e.status === "scheduled")
        .sort((a, b) => String(a.date || "9999").localeCompare(String(b.date || "9999"))));
    } catch (e) {
      toast.error("Failed to load actions: " + (e?.response?.data?.detail || e.message));
      setEvents([]);
    }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <>
      <Button variant="outline" size="sm" className="h-8 text-xs" onClick={load} data-testid="case-action-list-btn"
        title="All upcoming actions for this case only — soonest first">
        <ListChecks className="w-3.5 h-3.5 mr-1.5" /> Action list
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="case-action-list-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Action list — {caseRow.customer_name || caseRow.loan_application_id}
            </DialogTitle>
          </DialogHeader>
          {events === null ? (
            <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
          ) : events.length === 0 ? (
            <p className="text-sm text-muted-foreground italic py-4">No scheduled actions on this case.</p>
          ) : (
            <div className="max-h-[420px] overflow-y-auto space-y-1.5">
              {events.map(ev => {
                const overdue = ev.date && ev.date < today;
                const dueToday = ev.date === today;
                return (
                  <div key={ev.id} className={`flex items-center gap-2 rounded border px-2.5 py-1.5 text-xs ${overdue ? "bg-rose-50 border-rose-200" : dueToday ? "bg-amber-50 border-amber-200" : "bg-white"}`}
                    data-testid="case-action-list-item">
                    <span className="flex-1 min-w-0 truncate font-medium text-slate-800">{ev.label || ev.title || ev.event_type}</span>
                    <span className={`tabular-nums whitespace-nowrap ${overdue ? "text-rose-700 font-semibold" : dueToday ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>
                      {ev.date ? new Date(ev.date + "T00:00:00").toLocaleDateString("en-GB") : "—"}
                      {overdue ? " · overdue" : dueToday ? " · today" : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function PortfolioButton({ caseRow, onChanged }) {
  const [busy, setBusy] = useState(false);
  const active = !!caseRow.portfolio_tracking?.active;
  const toggle = async () => {
    setBusy(true);
    try {
      if (active) { await removeCaseFromPortfolio(caseRow.case_id); toast.success("Removed from Portfolio Accounts"); }
      else { await addCaseToPortfolio(caseRow.case_id); toast.success("Added to Portfolio Accounts — stays workable even when current"); }
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Portfolio update failed"); } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant={active ? "secondary" : "outline"} className="h-8 text-xs" disabled={busy} onClick={toggle}
            title={active ? "Remove this account from the shared Portfolio Accounts list" : "Keep this file workable after it leaves arrears (shared list)"}
            data-testid="customer-file-portfolio-btn">
      {active ? "In Portfolio Accounts ✓" : "Add to Portfolio Accounts"}
    </Button>
  );
}

function HandlerControl({ caseRow, handlers, onChanged, labelled = false }) {
  const [busy, setBusy] = useState(false);
  const options = sortNames(handlers, caseRow.handler);
  const handleChange = async (newHandler) => {
    setBusy(true);
    try {
      await updateAgeingCase(caseRow.case_id, { handler: newHandler === "_unassign" ? "" : newHandler });
      toast.success(`Handler → ${newHandler === "_unassign" ? "Unassigned" : newHandler}`);
      await onChanged();
    } catch (e) {
      toast.error("Update failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  const select = (
    <Select value={caseRow.handler || "_unassign"} onValueChange={handleChange} disabled={busy}>
      <SelectTrigger className={labelled ? "h-8 text-xs w-[180px] border-0 bg-transparent shadow-none focus:ring-0" : "h-8 text-xs w-[180px]"} data-testid="customer-file-handler-select" title="Account handler (Collections) — owns the account and the arrears conversation">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="_unassign">— Unassigned —</SelectItem>
        {options.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  if (!labelled) return select;
  // Legal mode (Dean, 9 Oct 2026): the account handler and the legal owner read as one labelled pair
  return (
    <div className="inline-flex items-center rounded-md border border-slate-300 bg-slate-50" data-testid="customer-file-account-handler">
      <span className="pl-2 pr-1 text-[10px] uppercase tracking-wide text-slate-700 font-semibold select-none">👤 Account</span>
      {select}
    </div>
  );
}

// Legal owner (Dean, 8 Oct 2026): the paralegal who owns this file's legal actions — sits beside the account handler in
// legal mode instead of being buried in Markers. Legal-group roster; blank = same as the account handler.
function LegalOwnerControl({ caseRow, handlers, onChanged }) {
  const [busy, setBusy] = useState(false);
  const options = sortNames(handlers, caseRow.legal_handler);
  const handleChange = async (v) => {
    const next = v === "_same" ? "" : v;
    if (next === (caseRow.legal_handler || "")) return;
    setBusy(true);
    try {
      await setLegalCaseFlags(caseRow.case_id, { legal_handler: next });
      toast.success(next ? `Legal owner → ${next}` : `Legal owner cleared — actions follow ${caseRow.handler || "the account handler"}`);
      await onChanged();
    } catch (e) {
      toast.error("Update failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex items-center rounded-md border border-red-200 bg-red-50/60" data-testid="customer-file-legal-owner">
      <span className="pl-2 pr-1 text-[10px] uppercase tracking-wide text-red-800 font-semibold select-none">⚖ Legal</span>
      <Select value={caseRow.legal_handler || "_same"} onValueChange={handleChange} disabled={busy}>
        <SelectTrigger className="h-8 text-xs w-[180px] border-0 bg-transparent shadow-none focus:ring-0" data-testid="customer-file-legal-owner-select"
          title="Legal owner — the paralegal who owns this file's legal actions (open actions follow them)">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_same">— same as account handler —</SelectItem>
          {options.map(h => <SelectItem key={h} value={h} data-testid={`customer-file-legal-owner-opt-${h}`}>{h}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StageControl({ caseRow, stages, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmStage, setConfirmStage] = useState(null);   // pending stage awaiting confirm
  const [preview, setPreview] = useState(null);              // preview from backend
  const [retrying, setRetrying] = useState(false);

  // Open the confirm dialog for any stage change — even portal-only ones,
  // so ops always see the LD side-effect (or lack thereof) before commit.
  const [handOpen, setHandOpen] = useState(false);
  const requestChange = async (newStage) => {
    if (!newStage || newStage === caseRow.stage) return;
    if (newStage === "legal") { setHandOpen(true); return; }   // one door into Legal — Hand to Legal (two-axis model)
    setBusy(true);
    try {
      const p = await previewLdStatusChange(caseRow.case_id, newStage);
      setPreview(p);
      setConfirmStage(newStage);
    } catch (e) {
      toast.error("Could not preview LD impact: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const confirmCommit = async () => {
    if (!confirmStage) return;
    setBusy(true);
    try {
      const res = await updateAgeingCase(caseRow.case_id, { stage: confirmStage });
      const ld = res?.ld_push;
      const sfNumber = caseRow.loan_application_id || caseRow.case_id;
      const targetLabel = ld?.ld_status_label || (confirmStage === "legal" ? "Legal" : "Open");
      const targetStatusId = ld?.ld_status_id ?? (confirmStage === "legal" ? 3 : 1);
      if (ld?.ok) {
        toast.success(`Stage → ${confirmStage} · LoanDisk synced (${ld.ld_status_label})`);
      } else if (ld && !ld.ok) {
        // LoanDisk's PUT /loan/{loan_id} silently no-ops on status changes.
        // The read-verify caught it — tell ops exactly what to do in LD.
        toast.warning(
          `Portal moved to ${confirmStage} — BUT LoanDisk was NOT updated. Open LoanDisk and change ${sfNumber} to ${targetLabel} (status_id=${targetStatusId}) manually.`,
          { duration: 15000 },
        );
      } else {
        toast.success(`Stage → ${confirmStage}`);
      }
      setConfirmStage(null);
      setPreview(null);
      await onChanged();
    } catch (e) {
      toast.error("Update failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const r = await retryLdStatusPush(caseRow.case_id);
      if (r.ok) {
        toast.success(`LoanDisk → ${r.ld_status_id} retry succeeded`);
      } else {
        toast.error("Retry failed: " + (r.message || r.reason || "unknown"));
      }
      await onChanged();
    } catch (e) {
      toast.error("Retry failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRetrying(false);
    }
  };

  // Out-of-sync detection — every stage maps to a LD status_id now
  // (open_loan→1, legal→3) so any mismatch is meaningful.
  const ldStatusId = caseRow.ld_status_id ?? caseRow.loandisk_status_id;
  const stageMaps = caseRow.stage === "open_loan" || caseRow.stage === "legal";
  const expected = caseRow.stage === "legal" ? 3 : 1;
  const inSync = !stageMaps || !ldStatusId || ldStatusId === expected;

  return (
    <div className="flex items-center gap-2">
      <Select
        value={caseRow.stage}
        onValueChange={requestChange}
        disabled={busy}
      >
        <SelectTrigger className="h-8 text-xs w-[140px]" data-testid="customer-file-stage-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {stages.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
        </SelectContent>
      </Select>

      {handOpen && <HandToLegalDialog caseRow={caseRow} journey={null} onClose={() => setHandOpen(false)} onDone={async () => { setHandOpen(false); await onChanged(); }} />}

      {/* LD status badge — shows if the LD side is out-of-sync */}
      {stageMaps && !inSync && ldStatusId && (
        <button
          onClick={handleRetry}
          disabled={retrying}
          className="text-[10px] uppercase font-semibold px-2 py-1 rounded border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          title="LoanDisk status doesn't match portal — click to re-push"
          data-testid="customer-file-ld-resync-btn"
        >
          {retrying ? "Retrying…" : "LD: Out of sync — retry"}
        </button>
      )}

      <Dialog open={!!confirmStage} onOpenChange={(v) => { if (!v) { setConfirmStage(null); setPreview(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="ld-status-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Change stage → {confirmStage}?</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded bg-slate-50 p-3 text-slate-700">
              <div><span className="font-medium">From:</span> {caseRow.stage || "—"}</div>
              <div><span className="font-medium">To:</span> {confirmStage}</div>
            </div>
            {preview?.will_push ? (
              <div className={`rounded border p-3 ${confirmStage === "legal" ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-900"}`}>
                <div className="font-semibold mb-1">
                  {confirmStage === "legal" ? "⚠️ This will escalate on LoanDisk:" : "This will update LoanDisk:"}
                </div>
                <div>Loan {preview.loan_id} → <code className="font-mono">loan_status_id={preview.ld_status_id}</code> ({preview.ld_status_label})</div>
                <div className="text-xs mt-2">{preview.reason}</div>
                {/* Heads-up: LoanDisk's API silently no-ops on status
                    updates. Set expectations up-front so ops doesn't
                    assume LD is in sync just because the portal moved. */}
                <div className="text-xs mt-3 pt-2 border-t border-current/20 opacity-90">
                  <strong>Heads-up:</strong> LoanDisk&apos;s API doesn&apos;t reliably persist status
                  changes. If the read-back check fails, you&apos;ll get a reminder to open
                  LoanDisk and change{" "}
                  <code className="font-mono">{caseRow.loan_application_id || caseRow.case_id}</code>{" "}
                  to <strong>{preview.ld_status_label}</strong> manually.
                </div>
              </div>
            ) : (
              <div className="rounded border border-slate-200 bg-slate-50 p-3 text-slate-700">
                <div className="font-medium mb-1">Portal-only change</div>
                <div className="text-xs">{preview?.reason}</div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setConfirmStage(null); setPreview(null); }}
              data-testid="ld-status-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmCommit}
              disabled={busy}
              className={confirmStage === "legal" ? "bg-red-600 hover:bg-red-700" : ""}
              data-testid="ld-status-confirm-btn"
            >
              {busy ? "Updating…" : preview?.will_push ? "Confirm & Push to LoanDisk" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function LegalFlagsButton({ caseRow, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [marker, setMarker] = useState(caseRow.handling_marker || "");
  const [firm, setFirm] = useState(caseRow.external_firm || "");
  const [jurisdiction, setJurisdiction] = useState(caseRow.jurisdiction || "");
  const openDialog = () => {
    setMarker(caseRow.handling_marker || "");
    setFirm(caseRow.external_firm || "");
    setJurisdiction(caseRow.jurisdiction || "");
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        handling_marker: marker,
        external_firm: firm,
      };
      if (jurisdiction) payload.jurisdiction = jurisdiction;
      await setLegalCaseFlags(caseRow.case_id, payload);
      toast.success("Case markers updated");
      setOpen(false);
      await onChanged();
    } catch (e) {
      toast.error("Markers failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const hasMarker = !!caseRow.handling_marker;
  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={
          hasMarker
            ? "h-8 px-3 text-xs rounded-md border border-indigo-300 bg-indigo-50 text-indigo-900 hover:bg-indigo-100"
            : "h-8 px-3 text-xs rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
        }
        data-testid="customer-file-legal-flags-btn"
        title="Case markers — jurisdiction, external handling"
      >
        {hasMarker ? "✓ Markers" : "+ Markers"}
      </button>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="legal-flags-dialog">
          <DialogHeader>
            <DialogTitle>Case markers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="text-xs font-medium mb-1">Handling marker</div>
              <select value={marker} onChange={(e) => setMarker(e.target.value)}
                className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                data-testid="legal-flags-marker-select">
                <option value="">None</option>
                <option value="out_of_jurisdiction">Out of Jurisdiction (Scottish / NI — limited actions)</option>
                <option value="external">External (instructed law firm handles it)</option>
              </select>
              {marker === "external" && (
                <input value={firm} onChange={(e) => setFirm(e.target.value)}
                  placeholder="Law firm name (optional)"
                  className="mt-2 w-full h-9 border rounded-md px-2 text-sm"
                  data-testid="legal-flags-firm-input" />
              )}
            </div>
            <div>
              <div className="text-xs font-medium mb-1">Jurisdiction</div>
              <select value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
                className="w-full h-9 border rounded-md px-2 text-sm bg-white"
                data-testid="legal-flags-jurisdiction-select">
                <option value="">— leave as is —</option>
                <option value="england_wales">England & Wales</option>
                <option value="scotland">Scotland</option>
                <option value="northern_ireland">Northern Ireland</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={save} disabled={busy} data-testid="legal-flags-save">
                {busy ? "Saving…" : "Save markers"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// "Close case" — Emma's closures + Dean's "Resolved via Collections" (dialog lives in pages/legal/CloseCaseDialog.jsx)
function CloseCaseButton({ caseRow, onChanged }) {
  const [open, setOpen] = useState(false);
  const closure = caseRow.legal_closure;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          closure
            ? "h-8 px-3 text-xs rounded-md border border-slate-400 bg-slate-100 text-slate-800 hover:bg-slate-200"
            : "h-8 px-3 text-xs rounded-md border border-rose-300 bg-rose-50 text-rose-900 hover:bg-rose-100"
        }
        data-testid="customer-file-close-case-btn"
        title="Close this legal file — write off, review later, fully paid, or resolved via Collections"
      >
        {closure ? "✓ Closed" : "Close case"}
      </button>
      {open && <CloseCaseDialog caseRow={caseRow} onClose={() => setOpen(false)} onChanged={onChanged} />}
    </>
  );
}

function CapitalAtRiskButton({ caseRow, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const isCAR = !!caseRow.capital_at_risk;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await setCapitalAtRisk(caseRow.case_id, {
        enabled: !isCAR,
        reason: !isCAR ? (reason.trim() || null) : null,
      });
      toast.success(isCAR ? "Capital at Risk cleared" : "Marked as Capital at Risk");
      setDialogOpen(false);
      setReason("");
      await onChanged();
    } catch (e) {
      toast.error("CAR toggle failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setDialogOpen(true)}
        disabled={busy}
        className={
          isCAR
            ? "h-8 px-3 text-xs rounded-md border border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100 disabled:opacity-50"
            : "h-8 px-3 text-xs rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        }
        data-testid="customer-file-car-toggle"
        title={isCAR ? "Capital at Risk — click to clear" : "Mark as Capital at Risk (portal-only)"}
      >
        {isCAR ? "✓ CAR" : "+ Mark CAR"}
      </button>

      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v) { setDialogOpen(false); setReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="car-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {isCAR ? "Clear Capital at Risk?" : "Mark as Capital at Risk?"}
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="rounded bg-orange-50 border border-orange-200 p-3 text-orange-900">
              <div className="font-semibold mb-1">Portal-only flag</div>
              <div className="text-xs">
                Capital at Risk is a management metric — it surfaces this loan on
                the Bad Debt Tracker as upcoming exposure. <strong>No update is
                sent to LoanDisk.</strong>
              </div>
            </div>
            {!isCAR && (
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">
                  Reason (optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Director not engaging, bounced 2× this month"
                  className="w-full h-8 text-sm px-2 border border-slate-300 rounded"
                  data-testid="car-reason-input"
                />
              </div>
            )}
            {isCAR && caseRow.capital_at_risk_reason && (
              <div className="text-xs text-slate-600">
                <span className="font-medium">Current reason:</span> {caseRow.capital_at_risk_reason}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDialogOpen(false); setReason(""); }} data-testid="car-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={busy}
              className={isCAR ? "" : "bg-orange-600 hover:bg-orange-700"}
              data-testid="car-confirm-btn"
            >
              {busy ? "Saving…" : (isCAR ? "Clear flag" : "Mark as CAR")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Legal Docs card — live generators (DocuPilot) + upcoming templates ────
const LEGAL_DOC_GENERATORS = [
  { kind: "pdn",     label: "Pre-Default Notice",         desc: "Initial notice of default" },
  { kind: "dn",      label: "Default Notice",             desc: "Formal default notice" },
  { kind: "lba",     label: "Letter Before Action",       desc: "Pre-litigation demand — one per guarantor" },
  { kind: "nsd72",   label: "Non-Statutory Demand (72 hour)", desc: "VERY URGENT company demand — cleared funds within 72 hours, by e-mail and first class post; issue from legal@ inside the wizard" },
  { kind: "sd2",     label: "Statutory Demand (England)", desc: "Served on the individual debtor — one per recipient" },
  { kind: "sd_scot", label: "Statutory Demand (Scotland)", desc: "Scottish statutory demand — one per recipient" },
  { kind: "prd",     label: "Payments Rescheduling Deed",  desc: "Deed built from the saved payment plan — then straight to DocuSign (guarantors first, Dean last); the signed deed files itself back here" },
];
// Emma's court & insolvency letters (Sep 2026) — pre-filled from the file's
// Issuing Court, hearing/petition/SD events, guarantors and LoanDisk balance.
const LEGAL_COURT_GENERATORS = [
  { kind: "n244_cover",      label: "N244 — Substituted Service Cover",    desc: "Covering letter to the court with the N244 + process server certificate" },
  { kind: "bp_cover",        label: "Bankruptcy Petition Cover",           desc: "Covering letter presenting the petition (copies, OR deposit, PBA fee)" },
  { kind: "iaa_cover",       label: "Form IAA — Vacate & Withdraw Cover",  desc: "Covering letter asking the court to vacate the hearing and withdraw the petition" },
  { kind: "n244_adjourn",    label: "N244 — Adjournment Cover",            desc: "Covering letter with the N244 asking to adjourn the hearing (e.g. payment plan agreed)" },
  { kind: "lpc_instruction", label: "LPC Instruction Letter",              desc: "Instruct the advocate for the bankruptcy hearing (bundle contents, service, opposition)" },
  { kind: "advocate_sheet",  label: "Advocate Instruction Sheet",          desc: "One-page hearing brief: court, debt, service history, relief sought, trustee nomination" },
];
const LEGAL_TEMPLATES_SOON = [
  { id: "n215",              label: "N215 — Certificate of Service",            desc: "Certificate of service for court documents" },
];
const LEGAL_BUNDLES_SOON = [
  { id: "bankruptcy_pack",   label: "Bankruptcy Trial Pack",                    desc: "Bundled trial pack for bankruptcy hearings" },
];

function ComingSoonPanel({ title, note, region = "AUS" }) {
  return (
    <div className="border rounded-lg bg-white p-8 text-center" data-testid={`${region.toLowerCase()}-coming-soon-panel`}>
      <Badge variant="outline" className="mb-2 bg-amber-50 text-amber-700 border-amber-300">Coming soon · {region}</Badge>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{note}</p>
    </div>
  );
}

function CourtCaseNumberField({ caseRow }) {
  const [val, setVal] = useState(caseRow.court_case_number || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(caseRow.court_case_number || ""); }, [caseRow.court_case_number]);
  const save = async () => {
    if ((val || "").trim() === (caseRow.court_case_number || "").trim()) return;
    setSaving(true);
    try {
      await setLegalCourtCaseNumber(caseRow.case_id, val);
      caseRow.court_case_number = val.trim();
      toast.success(val.trim() ? "Court case number saved — every court letter will pre-fill it" : "Court case number cleared");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 border rounded px-2 py-1 bg-violet-50/40" data-testid="legal-court-case-number">
      <Landmark className="w-3.5 h-3.5 text-violet-600 shrink-0" />
      <span className="text-[11px] text-slate-600 shrink-0">Court case no.</span>
      <Input value={val} onChange={e => setVal(e.target.value)} onBlur={save} onKeyDown={e => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="e.g. 1001 of 2026" className="h-6 text-[11px] flex-1 bg-white" disabled={saving} data-testid="legal-court-case-number-input" />
    </div>
  );
}

function LegalDocsCard({ caseRow }) {
  const [genKind, setGenKind] = useState(null);
  const [packOpen, setPackOpen] = useState(false);
  const [witnessOpen, setWitnessOpen] = useState(false);
  const [n244Open, setN244Open] = useState(false);
  if (isAusDesk()) {
    return (
      <Card data-testid="customer-file-legal-docs-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileSignature className="w-4 h-4 text-rose-600" />
            Legal documents
            <Badge variant="outline" className="ml-auto text-[9px] bg-emerald-50 text-emerald-700 border-emerald-300">AUS</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5 text-xs">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pt-0.5" data-testid="legal-docs-section-documents">Notices</div>
          <Button
            variant="outline" size="sm"
            className="w-full justify-start text-xs h-7 font-normal border-rose-200 hover:bg-rose-50"
            title="Pre-Default Notice on the Fixed Sum Loan Agreement — pre-filled from LoanDisk, rendered by DocuPilot, filed to the customer's OneDrive folder"
            onClick={() => setGenKind("pdn")}
            data-testid="customer-file-legal-pdn"
          >
            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0 text-rose-600" />
            <span className="truncate">Pre-Default Notice (PDN)</span>
            <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-rose-50 text-rose-700 border-rose-200">generate</Badge>
          </Button>
          <p className="text-[10px] text-muted-foreground pt-1">Default Notice, demands and court letters for AU will land here as the templates are ready.</p>
          {genKind && (
            <GenerateLegalDocDialog kind={genKind} caseRow={caseRow} onClose={() => setGenKind(null)} />
          )}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card data-testid="customer-file-legal-docs-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <FileSignature className="w-4 h-4 text-rose-600" />
          Legal documents
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 text-xs">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pt-0.5" data-testid="legal-docs-section-documents">Documents</div>
        {LEGAL_DOC_GENERATORS.map(t => (
          <Button
            key={t.kind}
            variant="outline" size="sm"
            className="w-full justify-start text-xs h-7 font-normal border-rose-200 hover:bg-rose-50"
            title={t.desc}
            onClick={() => setGenKind(t.kind)}
            data-testid={`customer-file-legal-${t.kind}`}
          >
            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0 text-rose-600" />
            <span className="truncate">{t.label}</span>
            <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-rose-50 text-rose-700 border-rose-200">generate</Badge>
          </Button>
        ))}
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pt-2 border-t mt-2" data-testid="legal-docs-section-court">Court &amp; insolvency</div>
        <CourtCaseNumberField caseRow={caseRow} />
        <CourtReferenceInline caseRow={caseRow} variant="field" />
        {LEGAL_COURT_GENERATORS.map(t => (
          <Button
            key={t.kind}
            variant="outline" size="sm"
            className="w-full justify-start text-xs h-7 font-normal border-violet-200 hover:bg-violet-50"
            title={t.desc}
            onClick={() => setGenKind(t.kind)}
            data-testid={`customer-file-legal-${t.kind}`}
          >
            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0 text-violet-600" />
            <span className="truncate">{t.label}</span>
            <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-violet-50 text-violet-700 border-violet-200">generate</Badge>
          </Button>
        ))}
        <Button
          variant="outline" size="sm"
          className="w-full justify-start text-xs h-7 font-normal border-violet-200 hover:bg-violet-50"
          title="Witness statement wizard — in support of a claim or petition, opposing a set-aside application, or answering a defence. GPT-5.5 drafts from the file; PD 32 exhibits (JA1…) bundled behind; PD 22 statement of truth. Saves .docx + PDF to OneDrive / Court."
          onClick={() => setWitnessOpen(true)}
          data-testid="customer-file-legal-witness"
        >
          <FileSignature className="w-3 h-3 mr-1.5 flex-shrink-0 text-violet-600" />
          <span className="truncate">Witness statement (CPR 32 / IA 1986)</span>
          <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-violet-50 text-violet-700 border-violet-200">AI wizard</Badge>
        </Button>
        <Button
          variant="outline" size="sm"
          className="w-full justify-start text-xs h-7 font-normal border-violet-200 hover:bg-violet-50"
          title="N244 Application Notice wizard — alternative service, adjournment, set aside a dismissal / restore the petition, or any other order. Fill with AI from the file(s) you tick; the official HMCTS form filled field by field, covering letter + draft order (Word), enclosures merged into one bundle, then Postworks or e-mail to the court."
          onClick={() => setN244Open(true)}
          data-testid="customer-file-legal-n244"
        >
          <Gavel className="w-3 h-3 mr-1.5 flex-shrink-0 text-violet-600" />
          <span className="truncate">N244 — Application Notice</span>
          <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-violet-50 text-violet-700 border-violet-200">AI wizard</Badge>
        </Button>
        {LEGAL_TEMPLATES_SOON.map(t => (
          <Button
            key={t.id}
            variant="outline" size="sm"
            className="w-full justify-start text-xs h-7 font-normal"
            title={t.desc}
            disabled
            data-testid={`customer-file-legal-${t.id}`}
          >
            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0" />
            <span className="truncate">{t.label}</span>
            <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0">soon</Badge>
          </Button>
        ))}
        <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 pt-2 border-t mt-2" data-testid="legal-docs-section-bundles">Bundles &amp; packs</div>
        <Button
          variant="outline" size="sm"
          className="w-full justify-start text-xs h-7 font-normal border-indigo-200 hover:bg-indigo-50"
          title="Bundle case documents into one PDF and email a process server / messenger-at-arms"
          onClick={() => setPackOpen(true)}
          data-testid="customer-file-service-pack"
        >
          <FileText className="w-3 h-3 mr-1.5 flex-shrink-0 text-indigo-600" />
          <span className="truncate">Service pack → process server</span>
          <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0 bg-indigo-50 text-indigo-700 border-indigo-200">bundle</Badge>
        </Button>
        {LEGAL_BUNDLES_SOON.map(t => (
          <Button
            key={t.id}
            variant="outline" size="sm"
            className="w-full justify-start text-xs h-7 font-normal"
            title={t.desc}
            disabled
            data-testid={`customer-file-legal-${t.id}`}
          >
            <FileText className="w-3 h-3 mr-1.5 flex-shrink-0" />
            <span className="truncate">{t.label}</span>
            <Badge variant="outline" className="ml-auto text-[9px] flex-shrink-0">soon</Badge>
          </Button>
        ))}
      </CardContent>
      {genKind && (
        <GenerateLegalDocDialog
          kind={genKind}
          caseRow={caseRow}
          onClose={() => setGenKind(null)}
        />
      )}
      {witnessOpen && <WitnessStatementDialog caseRow={caseRow} onClose={() => setWitnessOpen(false)} />}
      {n244Open && <N244Dialog caseRow={caseRow} onClose={() => setN244Open(false)} onDone={() => window.dispatchEvent(new CustomEvent("portal:files-changed", { detail: { case_id: caseRow.case_id, source: "n244" } }))} />}
      {packOpen && (
        <ServicePackDialog
          caseRow={caseRow}
          kind="sd2"
          onClose={() => setPackOpen(false)}
        />
      )}
    </Card>
  );
}

// ─── Legal notes — Airtable-imported entries (individual + CRUD) ───────────
// Renders each parsed `timeline[]` entry with the 4-field schema locked
// with ops (30 Jun 2026):
//   • note (narrative)
//   • entry_type (expiring / for_information / deadline / p2p / hearing)
//   • date_added (blank if Airtable didn't supply)
//   • effective_date (deadline / hearing date)
//
// Auto-routing happens server-side based on entry_type:
//   • p2p → also lands on collections_cases.p2ps[] (P2P tab)
//   • hearing/deadline/expiring + effective_date → also lands on legal_calendar_events
//   • all entries → unified Timeline tab (under category=legal_note)
//
// Editing is portal-only — Airtable stays the legacy archive. The
// stable `airtable_source_key` on imported rows ensures future re-syncs
// skip rows ops has touched here.

const ENTRY_TYPE_OPTIONS = [
  { value: "for_information", label: "For information",  cls: "border-slate-300 text-slate-700 bg-slate-50" },
  { value: "deadline",        label: "Deadline",          cls: "border-amber-300 text-amber-800 bg-amber-50" },
  { value: "expiring",        label: "Expiring",          cls: "border-rose-300 text-rose-700 bg-rose-50" },
  { value: "hearing",         label: "Hearing",           cls: "border-violet-300 text-violet-700 bg-violet-50" },
  { value: "p2p",             label: "Promise to pay",    cls: "border-emerald-300 text-emerald-700 bg-emerald-50" },
];
const ENTRY_TYPE_CLS = Object.fromEntries(ENTRY_TYPE_OPTIONS.map(o => [o.value, o.cls]));
const ENTRY_TYPE_LABEL = Object.fromEntries(ENTRY_TYPE_OPTIONS.map(o => [o.value, o.label]));

function LegalNotesCard({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // create | edit
  const [editorPayload, setEditorPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getLegalNotesBundle(caseRow.case_id);
      setData(r);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow.case_id]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await refreshAirtableNotes(caseRow.case_id);
      if (r?.ok === false) {
        toast.error(r?.error || "No matching Airtable record found");
      } else {
        const imp = r?.imported_count ?? 0;
        const dl  = r?.deadline_count ?? 0;
        const ev  = r?.calendar_events_created ?? 0;
        const p2p = r?.p2ps_created ?? 0;
        if (imp === 0 && dl === 0) {
          toast.info(r?.note || "Airtable matched but no new notes to import");
        } else {
          const bits = [`${imp} note${imp === 1 ? "" : "s"}`];
          if (p2p) bits.push(`${p2p} P2P${p2p === 1 ? "" : "s"}`);
          if (ev)  bits.push(`${ev} calendar event${ev === 1 ? "" : "s"}`);
          toast.success(`Imported ${bits.join(", ")} from Airtable`);
        }
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openCreate = () => {
    setEditorMode("create");
    setEditorPayload({
      notes:           "",
      entry_type:      "for_information",
      date_added:      deskDayKey(new Date().toISOString()),
      effective_date:  "",
      status:          "complete",
      audience:        "external",
    });
    setEditorOpen(true);
  };

  const openEdit = (entry) => {
    const noteId = entry.note_id || entry.airtable_source_key;
    setEditorMode("edit");
    setEditorPayload({
      _noteId:         noteId,
      _source:         entry.source,
      notes:           entry.notes || "",
      entry_type:      entry.entry_type || "for_information",
      date_added:      (entry.date_added || "").slice(0, 10),
      effective_date:  (entry.effective_date || "").slice(0, 10),
      status:          entry.status || "complete",
      audience:        entry.audience || "external",
    });
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!editorPayload?.notes?.trim()) {
      toast.error("Note text is required");
      return;
    }
    try {
      if (editorMode === "create") {
        const r = await createLegalNote(caseRow.case_id, {
          notes:          editorPayload.notes.trim(),
          entry_type:     editorPayload.entry_type,
          date_added:     editorPayload.date_added || null,
          effective_date: editorPayload.effective_date || null,
          status:         editorPayload.status,
          audience:       editorPayload.audience,
        });
        const sx = r?.side_effects || {};
        const extras = [];
        if (sx.p2p_id)            extras.push("P2P");
        if (sx.calendar_event_id) extras.push("calendar event");
        toast.success(`Note added${extras.length ? " · " + extras.join(" + ") + " also created" : ""}`);
      } else {
        await updateLegalNote(caseRow.case_id, editorPayload._noteId, {
          notes:          editorPayload.notes.trim(),
          entry_type:     editorPayload.entry_type,
          date_added:     editorPayload.date_added || null,
          effective_date: editorPayload.effective_date || null,
          status:         editorPayload.status,
          audience:       editorPayload.audience,
        });
        toast.success("Note updated");
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    }
  };

  const handleDelete = async (entry) => {
    const noteId = entry.note_id || entry.airtable_source_key;
    if (!noteId) {
      toast.error("This entry has no stable id — cannot delete safely");
      return;
    }
    if (!window.confirm("Delete this note? Imported notes will reappear on the next Airtable sync.")) return;
    try {
      await deleteLegalNote(caseRow.case_id, noteId);
      toast.success("Note deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Delete failed");
    }
  };

  const entries = data?.timeline || [];
  const timelineCount = data?.timeline_count ?? entries.length;
  const blob = (data?.case_notes || "").trim();

  // Sort by effective_date if present, else date_added, descending.
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => {
      const ad = a?.effective_date || a?.date_added || a?.date || "";
      const bd = b?.effective_date || b?.date_added || b?.date || "";
      if (ad && bd) return bd.localeCompare(ad);
      if (ad) return -1;
      if (bd) return 1;
      return 0;
    });
  }, [entries]);

  const fmtEntryDate = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  return (
    <Card data-testid="customer-file-legal-notes-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <Scale className="w-4 h-4 text-amber-700" />
            Legal notes
            {data?.found && (
              <Badge variant="outline" className="text-[10px] font-normal" data-testid="legal-notes-count-badge">
                {timelineCount} entries
              </Badge>
            )}
            {data?.found && (data?.deadline_count > 0) && (
              <Badge variant="outline" className="text-[10px] font-normal border-rose-300 text-rose-700">
                {data.deadline_count} deadline{data.deadline_count === 1 ? "" : "s"}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 text-[11px]"
              onClick={openCreate}
              data-testid="customer-file-legal-notes-add-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add note
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px]"
              onClick={handleSync}
              disabled={syncing}
              data-testid="customer-file-legal-notes-sync-btn"
              title="Pull historical CaseNotes from Airtable. Re-syncs are idempotent — edited rows stay edited."
            >
              {syncing
                ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                : <Download className="w-3 h-3 mr-1" />}
              Sync Airtable
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-xs">
        {err && <div className="text-red-700 text-[11px] mb-2">{err}</div>}
        {loading && !data && (
          <div className="flex items-center gap-2 text-muted-foreground py-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </div>
        )}
        {!loading && !err && !data?.found && (
          <div className="italic text-muted-foreground py-2">
            No legal case record found for <span className="font-mono">{caseRow.loan_application_id || caseRow.case_id}</span>.
            Click <span className="font-semibold">Sync Airtable</span> to import the historical CaseNotes column, or
            <span className="font-semibold"> Add note</span> to start fresh.
          </div>
        )}
        {!loading && data?.found && (
          <>
            {sortedEntries.length === 0 && !blob && (
              <div className="italic text-muted-foreground py-2">
                No notes on this case yet. Click <span className="font-semibold">Add note</span> to create one or
                <span className="font-semibold"> Sync Airtable</span> to pull the historical CaseNotes column.
              </div>
            )}

            {sortedEntries.length > 0 && (
              <ul className="divide-y border rounded-md bg-white max-h-[480px] overflow-y-auto" data-testid="legal-notes-entries-list">
                {sortedEntries.map((e, idx) => {
                  const etype       = e.entry_type || "for_information";
                  const isPortalOwn = e.source === "portal_legal";
                  const dateAdded   = fmtEntryDate(e.date_added);
                  const effective   = fmtEntryDate(e.effective_date);
                  const rawBy       = e.created_by_name || e.author || e.actor || e.created_by || "";
                  const by          = ["", "ops", "system", "portal", "unknown"].includes(String(rawBy).trim().toLowerCase()) ? "" : String(rawBy).trim();
                  return (
                    <li
                      key={e.note_id || e.airtable_source_key || `e-${idx}`}
                      className="px-3 py-2 group hover:bg-slate-50/60"
                      data-testid={`legal-notes-entry-${idx}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                          <Badge
                            variant="outline"
                            className={`text-[9px] uppercase tracking-wide font-semibold ${ENTRY_TYPE_CLS[etype] || ENTRY_TYPE_CLS.for_information}`}
                            data-testid={`legal-notes-entry-${idx}-type`}
                          >
                            {ENTRY_TYPE_LABEL[etype] || etype}
                          </Badge>
                          {isPortalOwn && (
                            <Badge variant="outline" className="text-[9px] border-blue-300 text-blue-700 bg-blue-50">
                              portal
                            </Badge>
                          )}
                          {by && (
                            <Badge
                              variant="outline"
                              className="text-[9px] border-emerald-300 text-emerald-700 bg-emerald-50 normal-case font-medium"
                              title={e.created_by_email ? `${by} · ${e.created_by_email}` : `Added by ${by}`}
                              data-testid={`legal-notes-entry-${idx}-author`}
                            >
                              {by}
                            </Badge>
                          )}
                          {e.status && e.status !== "complete" && (
                            <Badge variant="outline" className="text-[9px] border-slate-300 text-slate-700">
                              {e.status}
                            </Badge>
                          )}
                          {e.audience === "internal" && (
                            <Badge variant="outline" className="text-[9px] border-orange-300 text-orange-700 bg-orange-50">
                              internal
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-[10px] text-muted-foreground tabular-nums whitespace-nowrap">
                          {dateAdded && <span title="Date added">{dateAdded}</span>}
                          {effective && (
                            <span className="text-rose-700" title="Effective / deadline date">
                              · ➜ {effective}
                            </span>
                          )}
                          {e.source !== "portal_action" && (
                            <>
                          <button
                            type="button"
                            onClick={() => openEdit(e)}
                            className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-800"
                            data-testid={`legal-notes-entry-${idx}-edit`}
                            title="Edit note"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(e)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-800"
                            data-testid={`legal-notes-entry-${idx}-delete`}
                            title="Delete note"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                            </>
                          )}
                        </div>
                      </div>
                      {e.notes && (
                        <p className="text-[12px] text-slate-700 whitespace-pre-wrap leading-snug">{e.notes}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {blob && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowRaw(v => !v)}
                  className="text-[11px] text-amber-700 hover:underline"
                  data-testid="legal-notes-toggle-raw"
                >
                  {showRaw ? "Hide raw text" : "Show raw Airtable text (court-bundle view)"}
                </button>
                {showRaw && (
                  <div
                    data-testid="customer-file-legal-notes-blob"
                    className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 max-h-72 overflow-y-auto whitespace-pre-wrap leading-relaxed text-[12px]"
                  >
                    {blob}
                  </div>
                )}
              </div>
            )}

            <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground">
              {data?.matched_via && data.matched_via !== "direct" && (
                <span>
                  Matched via <span className="font-mono">{data.matched_via}</span>
                  {data?.sf_number && <> · SF <span className="font-mono">{data.sf_number}</span></>}
                </span>
              )}
              {data?.last_synced_at && (
                <span className="ml-auto">
                  Last sync: {new Date(data.last_synced_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Add / Edit dialog ─────────────────────────────────────────── */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="legal-note-editor-dialog">
          <DialogHeader>
            <DialogTitle>
              {editorMode === "create" ? "Add legal note" : "Edit legal note"}
            </DialogTitle>
          </DialogHeader>

          {editorPayload && (
            <div className="space-y-3 text-xs">
              <div>
                <Label className="text-[11px] uppercase">Note</Label>
                <Textarea
                  value={editorPayload.notes}
                  onChange={e => setEditorPayload(p => ({ ...p, notes: e.target.value }))}
                  rows={4}
                  placeholder="Narrative of what happened, what was sent, what's expected next…"
                  className="text-xs mt-1"
                  data-testid="legal-note-editor-notes"
                />
              </div>

              <div>
                <Label className="text-[11px] uppercase">Entry type</Label>
                <Select
                  value={editorPayload.entry_type}
                  onValueChange={v => setEditorPayload(p => ({ ...p, entry_type: v }))}
                >
                  <SelectTrigger className="h-8 text-xs mt-1" data-testid="legal-note-editor-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTRY_TYPE_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {editorPayload.entry_type === "p2p" && "Will also create a Promise to Pay on the P2P tab."}
                  {(editorPayload.entry_type === "hearing" || editorPayload.entry_type === "deadline" || editorPayload.entry_type === "expiring") && editorPayload.effective_date && "Will also create a Legal Calendar event on the effective date."}
                  {editorPayload.entry_type === "for_information" && "Adds to the case notes & unified timeline only."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase">Date added</Label>
                  <input
                    type="date"
                    value={editorPayload.date_added}
                    onChange={e => setEditorPayload(p => ({ ...p, date_added: e.target.value }))}
                    className="w-full mt-1 border rounded px-2 py-1 text-xs"
                    data-testid="legal-note-editor-date-added"
                  />
                </div>
                <div>
                  <Label className="text-[11px] uppercase">Effective date</Label>
                  <input
                    type="date"
                    value={editorPayload.effective_date}
                    onChange={e => setEditorPayload(p => ({ ...p, effective_date: e.target.value }))}
                    className="w-full mt-1 border rounded px-2 py-1 text-xs"
                    data-testid="legal-note-editor-effective-date"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px] uppercase">Status</Label>
                  <Select
                    value={editorPayload.status}
                    onValueChange={v => setEditorPayload(p => ({ ...p, status: v }))}
                  >
                    <SelectTrigger className="h-8 text-xs mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="complete">Complete</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="lapsed">Lapsed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="na">N/A</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase">Audience</Label>
                  <Select
                    value={editorPayload.audience}
                    onValueChange={v => setEditorPayload(p => ({ ...p, audience: v }))}
                  >
                    <SelectTrigger className="h-8 text-xs mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All (visible everywhere)</SelectItem>
                      <SelectItem value="external">External Only (court bundle)</SelectItem>
                      <SelectItem value="internal">Internal Only (ops only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)} data-testid="legal-note-editor-cancel">
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="legal-note-editor-save">
              {editorMode === "create" ? "Add note" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ─── Underwriting Notes — live Monday updates feed ─────────────────────────
function UnderwritingNotesCard({ caseRow, defaultExpanded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    (async () => {
      try {
        const r = await getAgeingUnderwritingNotes(caseRow.case_id);
        setData(r);
      } catch (e) { setErr(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [caseRow.case_id]);

  const notes = data?.notes || [];
  // Item 8: when surfaced in the Notes section we expand all entries by
  // default so they read as individual rows (mirrors LegalNotesCard).
  const initialCount = defaultExpanded ? notes.length : 3;
  const visible = expanded ? notes : notes.slice(0, initialCount);

  return (
    <Card data-testid="customer-file-uw-notes-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-pink-600" />
            Underwriting notes
            <Badge variant="outline" className="text-[10px] font-normal">{notes.length}</Badge>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className={`text-xs space-y-2 ${defaultExpanded ? "" : "max-h-[280px] overflow-y-auto"}`}>
        {err && <div className="text-red-700 text-[11px]">{err}</div>}
        {loading && <Loader2 className="w-3 h-3 animate-spin" />}
        {!loading && !err && notes.length === 0 && (
          <div className="italic text-muted-foreground">No underwriting notes on Monday yet.</div>
        )}
        {visible.map(n => (
          <div key={n.id} className="rounded border border-pink-200 bg-pink-50/30 px-3 py-2 hover:bg-pink-50/60 transition-colors">
            <div className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px] font-normal text-pink-700 border-pink-300">
                Underwriting
              </Badge>
              <span className="font-semibold text-slate-700">{n.creator || "system"}</span>
              <span>·</span>
              <span>{n.created_at ? new Date(n.created_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" }) : ""}</span>
            </div>
            <div className="text-[12px] text-slate-700 prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline-offset-2 [&_a:hover]:underline" dangerouslySetInnerHTML={{ __html: n.body }} />
          </div>
        ))}
        {!defaultExpanded && notes.length > 3 && (
          <button onClick={() => setExpanded(v => !v)} className="text-[11px] text-pink-700 hover:underline">
            {expanded ? `Show less` : `Show all ${notes.length} notes`}
          </button>
        )}
      </CardContent>
    </Card>
  );
}


// ─── Contacts (LoanDisk borrower + Monday + manually-added) ───────────────
// Source-of-truth for all the emails and phones associated with this case.
// Manually-added contacts (guarantor, accounts team, switchboard) are also
// what powers the M365 email matcher and Aircall call/SMS lookup.
//
// On first load, the backend kicks off a LoanDisk scan for guarantor records
// (named `<Company> N`). Once complete, they auto-appear here with the
// "LoanDisk Guarantor" source badge and role "Additional Guarantor".
function ContactsCard({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [roles, setRoles] = useState(["Director", "Guarantor", "Accounts", "Switchboard", "Spouse/Partner", "Other"]);
  const [open, setOpen] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [searchInboxOpen, setSearchInboxOpen] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const [c, r] = await Promise.all([
        getAgeingContacts(caseRow.case_id),
        getAgeingRoleOptions().catch(() => null),
      ]);
      setData(c);
      if (r?.roles) setRoles(r.roles);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [caseRow.case_id]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  // Auto-refresh once after 8 seconds — by then the background guarantor scan
  // should have finished and any new "LoanDisk Guarantor" entries will appear.
  useEffect(() => {
    const t = setTimeout(() => fetchContacts(), 8000);
    return () => clearTimeout(t);
  }, [caseRow.case_id, fetchContacts]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await syncAgeingGuarantors(caseRow.case_id);
      if (r.added > 0) {
        toast.success(`${r.added} new guarantor${r.added > 1 ? "s" : ""} found in LoanDisk`);
      } else {
        toast.success(`Scanned ${r.scanned} borrowers — no new guarantors found`);
      }
      fetchContacts();
    } catch (e) {
      toast.error("Refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  const contacts = data?.contacts || [];
  const ctCount  = contacts.length;
  const hasManualOrGuarantor = contacts.some(c => c.source === "Manual" || c.source === "LoanDisk Guarantor");

  return (
    <Card data-testid="customer-file-contacts">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => setOpen(v => !v)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-slate-600"
            data-testid="customer-file-contacts-toggle"
          >
            <Users className="w-4 h-4 text-violet-600" />
            <span>Contacts</span>
            <Badge variant="outline" className="text-[10px] font-normal">{ctCount}</Badge>
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
          {open && (
            <div className="flex items-center gap-1">
              <Button
                size="sm" variant="ghost" className="h-7 w-7 p-0"
                onClick={handleRefresh}
                disabled={refreshing}
                title="Re-scan LoanDisk for guarantors"
                data-testid="customer-file-contacts-refresh"
              >
                {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightCircle className="w-3.5 h-3.5" />}
              </Button>
              <Button
                size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                onClick={() => setDiscoverOpen(true)}
                title="Scan mailbox history for addresses that have communicated about this case"
                data-testid="customer-file-contacts-discover"
              >
                <Search className="w-3 h-3 mr-1" /> Discover
              </Button>
              <Button
                size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                onClick={() => setSearchInboxOpen(true)}
                title="Free-text search across the shared inbox — find an address by partial name/email and link it to this case"
                data-testid="customer-file-contacts-search-inbox"
              >
                <Search className="w-3 h-3 mr-1" /> Search inbox
              </Button>
              <Button
                size="sm" variant="default" className="h-7 px-2 text-[11px]"
                onClick={() => setAddOpen(true)}
                data-testid="customer-file-contacts-add"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="text-xs text-muted-foreground">Loading…</div>
          ) : (
            <ul className="divide-y" data-testid="customer-file-contacts-list">
              {contacts.length === 0 && (
                <li className="text-xs text-muted-foreground py-2">No contacts on file. Click "Add" or "Refresh" to pull from LoanDisk.</li>
              )}
              {contacts.map((c, idx) => (
                <ContactRow
                  key={c.id || `${c.source}-${idx}`}
                  contact={c}
                  caseId={caseRow.case_id}
                  onChanged={fetchContacts}
                />
              ))}
            </ul>
          )}
          {hasManualOrGuarantor && (
            <div className="mt-2 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
              Emails and calls from these contacts auto-match into this case&apos;s Outlook, Calls and SMS panels.
            </div>
          )}
        </CardContent>
      )}
      {addOpen && (
        <AddContactDialog
          caseId={caseRow.case_id}
          roles={roles}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); fetchContacts(); }}
        />
      )}
      {discoverOpen && (
        <DiscoverContactsDialog
          caseId={caseRow.case_id}
          roles={roles}
          onClose={() => setDiscoverOpen(false)}
          onAdded={() => { setDiscoverOpen(false); fetchContacts(); }}
        />
      )}
      {searchInboxOpen && (
        <LinkContactFromInboxDialog
          caseId={caseRow.case_id}
          roles={roles}
          onClose={() => setSearchInboxOpen(false)}
          onAdded={() => fetchContacts()}
        />
      )}
    </Card>
  );
}

function ContactRow({ contact, caseId, onChanged }) {
  const [deleting, setDeleting] = useState(false);
  const isManual = contact.source === "Manual" || contact.source === "LoanDisk Guarantor";
  const sourceTone = contact.source === "LoanDisk" ? "bg-amber-100 text-amber-800 border-amber-300"
                     : contact.source === "LoanDisk Guarantor" ? "bg-rose-100 text-rose-800 border-rose-300"
                     : contact.source === "Monday" ? "bg-purple-100 text-purple-800 border-purple-300"
                     : "bg-violet-100 text-violet-800 border-violet-300";

  const roleTone = {
    "Primary borrower":     "bg-slate-100 text-slate-800",
    "Director":             "bg-slate-100 text-slate-800",
    "Guarantor":            "bg-rose-100 text-rose-800",
    "Additional Guarantor": "bg-rose-100 text-rose-800",
    "Accounts":             "bg-emerald-100 text-emerald-800",
    "Switchboard":          "bg-cyan-100 text-cyan-800",
    "Spouse/Partner":       "bg-pink-100 text-pink-800",
  }[contact.role] || "bg-slate-100 text-slate-700";

  const handleDelete = async () => {
    if (!isManual || !contact.id) return;
    if (!window.confirm(`Remove ${contact.name || "this contact"} from this case?`)) return;
    setDeleting(true);
    try {
      await deleteAgeingAdditionalContact(caseId, contact.id);
      onChanged?.();
    } catch (e) {
      console.error(e);
      window.alert("Failed to remove contact: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <li className="py-2 group">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-medium text-xs text-slate-900 truncate" title={contact.name}>{contact.name || "—"}</span>
            <Badge variant="outline" className={`text-[9px] font-normal ${roleTone}`}>{contact.role}</Badge>
            <Badge variant="outline" className={`text-[9px] font-normal ${sourceTone}`}>{contact.source}</Badge>
          </div>
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="block text-[11px] text-blue-700 hover:underline truncate" title={contact.email}>
              {contact.email}
            </a>
          )}
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="block text-[11px] text-slate-600 hover:underline truncate font-mono">
              {contact.phone}
            </a>
          )}
          {contact.notes && (
            <div className="text-[10px] text-slate-500 mt-0.5 italic line-clamp-2" title={contact.notes}>{contact.notes}</div>
          )}
        </div>
        {isManual && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 self-start mt-0.5"
            title="Remove contact"
            data-testid={`customer-file-contact-delete-${contact.id}`}
          >
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          </button>
        )}
      </div>
    </li>
  );
}

function AddContactDialog({ caseId, roles, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("Other");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name && !email && !phone) {
      window.alert("Need at least a name, email or phone.");
      return;
    }
    setSaving(true);
    try {
      await addAgeingAdditionalContact(caseId, { name, role, email, phone, notes });
      onAdded?.();
    } catch (e) {
      console.error(e);
      window.alert("Failed to add contact: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="add-contact-dialog">
        <DialogHeader>
          <DialogTitle>Add contact</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-700">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="John Alan Gardner" data-testid="add-contact-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Role</label>
            <select
              value={role}
              onChange={e => setRole(e.target.value)}
              className="w-full border rounded h-9 px-2 text-sm"
              data-testid="add-contact-role"
            >
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@takeme.taxi" data-testid="add-contact-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Phone</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="07778974700" data-testid="add-contact-phone" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700">Notes</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Switchboard for 01604 626262 — ask for John" rows={2} data-testid="add-contact-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="add-contact-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Save contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Discover contacts — scan mailbox for unlinked addresses ──────────────
function DiscoverContactsDialog({ caseId, roles, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [scanned, setScanned] = useState(0);
  const [items, setItems] = useState([]);     // [{email, name, count, sample_subject, last_seen, _role, _checked}]
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const r = await discoverAgeingEmailContacts(caseId, { limit: 200 });
        setScanned(r.scanned || 0);
        setItems((r.suggestions || []).map(s => ({
          ...s,
          _role:    "Other",
          _checked: false,
        })));
      } catch (e) {
        toast.error("Discover failed: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
  }, [caseId]);

  const toggleAll = (checked) => setItems(items.map(i => ({ ...i, _checked: checked })));
  const toggleOne = (idx, checked) =>
    setItems(items.map((i, j) => j === idx ? { ...i, _checked: checked } : i));
  const setRole = (idx, role) =>
    setItems(items.map((i, j) => j === idx ? { ...i, _role: role } : i));

  const handleAddSelected = async () => {
    const picked = items.filter(i => i._checked);
    if (picked.length === 0) {
      toast.error("Tick at least one suggestion to link");
      return;
    }
    setSaving(true);
    try {
      // Sequential to avoid hammering the cases endpoint
      for (const p of picked) {
        await addAgeingAdditionalContact(caseId, {
          name:   p.name || null,
          email:  p.email,
          role:   p._role,
          source: "Email Discovery",
        });
      }
      toast.success(`Linked ${picked.length} new contact${picked.length > 1 ? "s" : ""}`);
      onAdded?.();
    } catch (e) {
      toast.error("Failed to link some contacts: " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const pickedCount = items.filter(i => i._checked).length;
  const allChecked = items.length > 0 && items.every(i => i._checked);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl" data-testid="discover-contacts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-4 h-4 text-indigo-600" />
            Discover email contacts
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Every email address that has communicated about this case but isn&apos;t yet linked.
            Tick the ones you want to add as additional contacts (spouse, accountant, secondary director, etc.) — emails from these addresses will then auto-appear in this case&apos;s timeline.
          </p>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
            Scanning mailbox for unlinked addresses…
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            All addresses found in {scanned} email{scanned !== 1 ? "s" : ""} are already linked
            (or belong to your own team).
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                Scanned <span className="font-semibold text-slate-700">{scanned}</span> emails ·{" "}
                <span className="font-semibold text-slate-700">{items.length}</span> unlinked address{items.length !== 1 ? "es" : ""}
              </span>
              <label className="inline-flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                  data-testid="discover-toggle-all"
                />
                <span>Select all</span>
              </label>
            </div>
            <div className="max-h-96 overflow-y-auto border rounded-md divide-y" data-testid="discover-contacts-list">
              {items.map((it, idx) => (
                <div
                  key={it.email}
                  className={`px-3 py-2 flex items-start gap-3 hover:bg-slate-50 ${it._checked ? "bg-indigo-50/40" : ""}`}
                  data-testid={`discover-row-${idx}`}
                >
                  <input
                    type="checkbox"
                    checked={it._checked}
                    onChange={(e) => toggleOne(idx, e.target.checked)}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-slate-800 truncate">{it.email}</span>
                      <Badge variant="outline" className="text-[10px] font-normal">{it.count} email{it.count !== 1 ? "s" : ""}</Badge>
                    </div>
                    {it.name && <div className="text-[11px] text-slate-600">{it.name}</div>}
                    {it.sample_subject && (
                      <div className="text-[11px] text-muted-foreground truncate italic">
                        Latest: {it.sample_subject}
                      </div>
                    )}
                  </div>
                  <select
                    value={it._role}
                    onChange={(e) => setRole(idx, e.target.value)}
                    className="text-[11px] border rounded h-7 px-1.5"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`discover-role-${idx}`}
                  >
                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleAddSelected}
            disabled={saving || pickedCount === 0}
            data-testid="discover-link-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Link {pickedCount > 0 ? `${pickedCount} ` : ""}contact{pickedCount !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Search the shared inbox by free-text to link an unknown contact ───────
// Used when a liquidator/accountant emails about an account but ops only
// remembers a partial name — they search the inbox, find the address, and
// link it to the case with a role.
function LinkContactFromInboxDialog({ caseId, roles, onClose, onAdded }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [linkOpen, setLinkOpen] = useState(null);  // {email, name}

  const runSearch = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await m365SearchMailbox({ q, limit: 50 });
      setEmails(r.emails || []);
    } catch (e) {
      toast.error("Search failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  // De-duplicate by email address, take the first encountered name + most recent
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of emails) {
      // Each email could be inbound (from) OR outbound (to recipients).
      // For the contact-discovery use case we surface every "person" address
      // that touched the email — inbound sender first, outbound recipients
      // next. The user picks the right one and clicks Link.
      const senderObj = (e.from || {}).emailAddress || {};
      const senderAddr = (senderObj.address || "").toLowerCase();
      const senderName = senderObj.name || "";
      if (senderAddr) {
        const ex = map.get(senderAddr) || { email: senderAddr, name: senderName, count: 0, latest: null, samples: [] };
        ex.count += 1;
        if (!ex.name && senderName) ex.name = senderName;
        const dt = e.receivedDateTime || e.sentDateTime;
        if (dt && (!ex.latest || dt > ex.latest)) ex.latest = dt;
        if (ex.samples.length < 2 && e.subject) ex.samples.push(e.subject);
        map.set(senderAddr, ex);
      }
      for (const r of (e.toRecipients || [])) {
        const ra = (((r.emailAddress) || {}).address || "").toLowerCase();
        const rn = ((r.emailAddress) || {}).name || "";
        if (!ra) continue;
        const ex = map.get(ra) || { email: ra, name: rn, count: 0, latest: null, samples: [] };
        ex.count += 1;
        if (!ex.name && rn) ex.name = rn;
        const dt = e.receivedDateTime || e.sentDateTime;
        if (dt && (!ex.latest || dt > ex.latest)) ex.latest = dt;
        if (ex.samples.length < 2 && e.subject) ex.samples.push(e.subject);
        map.set(ra, ex);
      }
    }
    return [...map.values()]
      // Drop our own internal addresses — they're not contacts to link
      .filter(g => !/@simplyfunded\.co\.uk$/i.test(g.email) && !/@morleycapital/i.test(g.email))
      .sort((a, b) => (b.latest || "").localeCompare(a.latest || ""));
  }, [emails]);

  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose?.()}>
        <DialogContent className="max-w-2xl" data-testid="link-from-inbox-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Search className="w-4 h-4 text-indigo-600" />
              Search inbox for a contact
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Free-text search across the shared accounts inbox — find a sender by partial name, email, subject or body keyword, then link them to this case (e.g. liquidator, accountant, solicitor).
            </p>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
              placeholder="e.g. 'liquidator', 'fred', 'frp', 'insolvency'…"
              className="flex-1"
              data-testid="link-from-inbox-q"
            />
            <Button onClick={runSearch} disabled={loading || !q.trim()} data-testid="link-from-inbox-search">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
              Search
            </Button>
          </div>

          <div className="max-h-[400px] overflow-y-auto border rounded-md mt-2" data-testid="link-from-inbox-results">
            {loading ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Searching mailbox…
              </div>
            ) : grouped.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                {emails.length === 0 ? "Type a keyword and press Enter to search." : "No external addresses found in these results."}
              </div>
            ) : (
              <ul className="divide-y">
                {grouped.map(g => (
                  <li key={g.email} className="px-3 py-2 flex items-start gap-3 hover:bg-slate-50" data-testid={`link-from-inbox-row-${g.email}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{g.name || g.email}</div>
                      <a
                        href={`mailto:${g.email}`}
                        className="block text-[11px] text-blue-700 hover:underline truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {g.email}
                      </a>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {g.count} email{g.count === 1 ? "" : "s"} · last seen {g.latest ? new Date(g.latest).toLocaleDateString("en-GB") : "—"}
                      </div>
                      {g.samples.length > 0 && (
                        <div className="text-[10px] text-slate-500 mt-0.5 italic line-clamp-1" title={g.samples.join(" · ")}>
                          {g.samples[0]}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm" variant="default" className="h-7 px-2 text-[11px]"
                      onClick={() => setLinkOpen({ email: g.email, name: g.name })}
                      data-testid={`link-from-inbox-link-${g.email}`}
                    >
                      <Plus className="w-3 h-3 mr-1" /> Link
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {linkOpen && (
        <LinkContactRolePrompt
          caseId={caseId}
          roles={roles}
          email={linkOpen.email}
          name={linkOpen.name}
          onClose={() => setLinkOpen(null)}
          onLinked={() => { setLinkOpen(null); onAdded?.(); }}
        />
      )}
    </>
  );
}

function LinkContactRolePrompt({ caseId, roles, email, name, onClose, onLinked }) {
  const ROLE_CHOICES = useMemo(() => {
    const base = ["Liquidator", "Accountant", "Solicitor", "Insolvency Practitioner"];
    return [...new Set([...(roles || []), ...base])];
  }, [roles]);
  const [role, setRole] = useState("Liquidator");
  const [displayName, setDisplayName] = useState(name || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await addAgeingAdditionalContact(caseId, {
        name:   displayName || null,
        email,
        role,
        notes:  notes || null,
        source: "Inbox Search",
      });
      toast.success(`Linked ${displayName || email} as ${role}`);
      onLinked?.();
    } catch (e) {
      toast.error("Link failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-sm" data-testid="link-from-inbox-role-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Link contact to this case</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div>
            <Label className="text-[10px] uppercase">Email</Label>
            <div className="text-xs font-mono bg-slate-50 border rounded px-2 py-1.5">{email}</div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display name (optional)"
              className="h-8 text-xs"
              data-testid="link-from-inbox-role-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="h-8 text-xs" data-testid="link-from-inbox-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLE_CHOICES.map(r => (
                  <SelectItem key={r} value={r} data-testid={`link-from-inbox-role-option-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. 'FRP Advisory acting for the liquidator on the SF-9000 administration'"
              rows={2}
              className="text-xs"
              data-testid="link-from-inbox-role-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="link-from-inbox-role-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Link contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Unified case files — OneDrive · LoanDisk · Monday · DocuSign · DocuPilot ──
// One card, one ranked list, source badge per row. Replaces the older
// split between FilesCard (Monday/LoanDisk/etc.) and OneDriveFilesCard.
//
// Click handling differs by source:
//   • OneDrive  → resolve a fresh SharePoint embed URL via /preview-url
//                 and open the FilePreviewModal with preview_kind=embedded
//                 (skips PDF / Office Online wrapping — SharePoint's
//                 embed.aspx already IS a viewer page).
//   • LoanDisk / Monday / DocuSign / DocuPilot → open the modal with
//                 the existing `url` + `proxy_url` fields from the
//                 aggregated /files endpoint.
function FilesCard({ caseRow, isLegalMode = false }) {
  const [aggData, setAggData] = useState(null);
  const [onedrive, setOnedrive] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());   // Emma #3 — tick & zip
  const [zipping, setZipping] = useState(false);

  const fetchFiles = useCallback(async () => {
    if (!caseRow?.case_id) return;
    setLoading(true);
    setErr(null);
    try {
      // Always pull from both feeds. Originally OneDrive was gated to
      // `isLegalMode`, but the moment a collections case has emails
      // saved (or a future portal-born case has files pushed) it needs
      // to see them too. The OneDrive endpoint silently returns []
      // for cases with no matched files, so the extra call is cheap.
      const [agg, od] = await Promise.all([
        getAgeingCaseFiles(caseRow.case_id).catch(() => ({ files: [] })),
        getLegalCaseFiles(caseRow.case_id).catch(() => ({ files: [] })),
      ]);
      setAggData(agg);
      setOnedrive(od.files || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [caseRow?.case_id]);
  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Listen for cross-component refresh signals (e.g. "Save to Files"
  // in the email reading pane). Filters on case_id to avoid wasted
  // refreshes when a different case fires it.
  useEffect(() => {
    const onChange = (evt) => {
      if (!evt.detail?.case_id || evt.detail.case_id === caseRow?.case_id) {
        fetchFiles();
      }
    };
    window.addEventListener("portal:files-changed", onChange);
    return () => window.removeEventListener("portal:files-changed", onChange);
  }, [fetchFiles, caseRow?.case_id]);

  const sourceTone = (src) =>
      src === "OneDrive"  ? "border-blue-300 text-blue-700"
    : src === "LoanDisk"  ? "border-emerald-300 text-emerald-700"
    : src === "Monday"    ? "border-pink-300 text-pink-700"
    : src === "DocuSign"  ? "border-amber-300 text-amber-700"
    : src === "DocuPilot" ? "border-violet-300 text-violet-700"
    : "border-slate-300 text-slate-700";

  // Normalise every file into a common shape so they can be sorted and
  // rendered together. Each entry keeps the bits we need to route the
  // click to the right preview flow.
  const unified = useMemo(() => {
    const rows = [];
    for (const f of (aggData?.files || [])) {
      rows.push({
        key:    `agg-${f.source}-${f.id}-${f.name}`,
        zipKey: `ext:${f.source}:${f.id}`,
        source: f.source,
        name:   f.name,
        size:   f.size,
        date:   f.received_at || f.created_at || f.date || null,
        origin: f,   // pass-through for the modal
        kind:   "aggregated",
      });
    }
    for (const f of onedrive) {
      rows.push({
        key:    `od-${f.file_id}`,
        zipKey: `od:${f.file_id}`,
        source: "OneDrive",
        name:   f.original_filename,
        size:   f.size,
        date:   f.last_modified,
        origin: f,
        kind:   "onedrive",
      });
    }
    // Sort newest-first when we have a date; undated rows fall to the end.
    rows.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });
    return rows;
  }, [aggData, onedrive]);

  const openPreview = async (row) => {
    if (row.kind === "onedrive") {
      setPreviewLoading(true);
      try {
        const r = await getLegalCaseFilePreviewUrl(row.origin.file_id);
        setPreviewFile({
          name:         row.origin.original_filename,
          source:       "OneDrive",
          url:          row.origin.web_url,
          preview_src:  r.url,
          preview_kind: "embedded",
        });
      } catch (e) {
        toast.error("Couldn't open preview: " + (e.response?.data?.detail || e.message));
      } finally { setPreviewLoading(false); }
    } else {
      setPreviewFile(row.origin);
    }
  };

  // Item 16: soft-delete a file from the portal (OneDrive only — the
  // aggregated LoanDisk / Monday / DocuSign / DocuPilot rows live in
  // their source systems and shouldn't be deleted from here).
  const handleDelete = async (row) => {
    if (row.kind !== "onedrive") {
      toast.error(
        `${row.source} files are managed in ${row.source} — delete them there.`,
      );
      return;
    }
    if (!window.confirm(`Remove "${row.name}" from the portal Files tab?\n\nThe file stays on OneDrive — only the portal link is hidden.`)) {
      return;
    }
    try {
      await deleteLegalCaseFile(row.origin.file_id);
      toast.success(`Removed: ${row.name}`);
      // Optimistic prune so the row disappears even if the refetch is
      // briefly slow.
      setOnedrive(prev => prev.filter(f => f.file_id !== row.origin.file_id));
      fetchFiles();
    } catch (e) {
      toast.error("Couldn't delete file: " + (e.response?.data?.detail || e.message));
    }
  };

  // Vanessa (f590067ebb) / Dean, 27 Oct 2026: rename OneDrive files in place — pencil on hover or right-click the row.
  // Monday & LoanDisk files can't be renamed at source (no API), so they get no pencil. Extension stays locked.
  const [renaming, setRenaming] = useState(null);          // { key, base, ext, saving }
  const splitName = (name) => {
    const m = /^(.*?)(\.[A-Za-z0-9]{1,8})$/.exec(name || "");
    return m ? { base: m[1], ext: m[2] } : { base: name || "", ext: "" };
  };
  const startRename = (row) => {
    if (row.kind !== "onedrive") return;
    const { base, ext } = splitName(row.name);
    setRenaming({ key: row.key, fileId: row.origin.file_id, base, ext, saving: false });
  };
  const commitRename = async () => {
    if (!renaming || renaming.saving) return;
    const base = renaming.base.trim();
    if (!base) { toast.error("Give the file a name"); return; }
    setRenaming(r => ({ ...r, saving: true }));
    try {
      const r = await renameLegalCaseFile(renaming.fileId, base + renaming.ext, undefined, [caseRow.case_id]);
      const newName = r.file?.original_filename || base + renaming.ext;
      setOnedrive(prev => prev.map(f => f.file_id === renaming.fileId ? { ...f, original_filename: newName, web_url: r.file?.web_url || f.web_url } : f));
      toast.success(r.unchanged ? "Name unchanged" : `Renamed to ${newName}`);
      setRenaming(null);
      fetchFiles();
    } catch (e) {
      toast.error("Couldn't rename: " + (e.response?.data?.detail || e.message));
      setRenaming(r => (r ? { ...r, saving: false } : r));
    }
  };

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of picked) {
      try {
        await uploadCaseFile(caseRow.case_id, f);
        ok++;
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || err.message}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    if (ok) {
      toast.success(`Uploaded ${ok} file${ok === 1 ? "" : "s"} to OneDrive (Portal Uploads)`);
      fetchFiles();
    }
  };

  const fmtBytes = (n) => {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  // Emma #3 — tick rows and download them together as one zip
  const toggleSelected = (zipKey) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(zipKey)) next.delete(zipKey); else next.add(zipKey);
    return next;
  });
  const allSelected = unified.length > 0 && unified.every(r => selected.has(r.zipKey));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(unified.map(r => r.zipKey)));
  const [zipProgress, setZipProgress] = useState(null);   // {done,total} while the zip builds in the background
  const downloadZip = async () => {
    if (!selected.size) return;
    setZipping(true);
    setZipProgress({ done: 0, total: selected.size });
    try {
      const { blob, fname: serverName, files, errors } = await downloadCaseFilesZip(caseRow.case_id, Array.from(selected), setZipProgress);
      const safe = (s) => String(s || "").replace(/[\\/:*?"<>|]+/g, " ").trim().replace(/\s+/g, "_");
      const fname = serverName || `${safe(caseRow.loan_application_id || caseRow.case_id)}-${safe(caseRow.customer_name || "customer")}-files.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (errors?.length) toast.warning(`${files} file${files === 1 ? "" : "s"} zipped — ${errors.length} couldn't be fetched (listed in _download-errors.txt inside the zip)`);
      else toast.success(`Downloading ${files || selected.size} file${(files || selected.size) === 1 ? "" : "s"} as ${fname}`);
      setSelected(new Set());
    } catch (e) {
      let msg = e?.response?.data?.detail || e?.message;
      try { if (e?.response?.data instanceof Blob) msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      toast.error("Zip download failed: " + msg);
    } finally { setZipping(false); setZipProgress(null); }
  };
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-GB", { year:"numeric", month:"short", day:"2-digit" }); }
    catch { return ""; }
  };

  // Counts per source for the header chip row — helps ops see at a
  // glance how many files came from each upstream system.
  const counts = useMemo(() => {
    const c = {};
    for (const r of unified) c[r.source] = (c[r.source] || 0) + 1;
    return c;
  }, [unified]);

  return (
    <>
      <Card data-testid="customer-file-files-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-600" />
              Files
              <Badge variant="outline" className="text-[10px] font-normal">{unified.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-1 flex-wrap">
              {Object.entries(counts).map(([src, n]) => (
                <Badge key={src} variant="outline" className={`text-[10px] font-normal ${sourceTone(src)}`}>
                  {src} · {n}
                </Badge>
              ))}
              <label
                className={`inline-flex items-center gap-1 text-[10px] border rounded px-1.5 py-0.5 cursor-pointer hover:bg-slate-50 text-slate-600 ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                title="Upload files to the customer's OneDrive folder (Portal Uploads)"
                data-testid="customer-file-upload-btn"
              >
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3 rotate-180" />}
                Upload
                <input type="file" multiple className="hidden" onChange={handleUpload} data-testid="customer-file-upload-input" />
              </label>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={fetchFiles} disabled={loading || previewLoading}>
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "↻"}
              </Button>
            </div>
          </div>
          {/* Emma #3 — selection bar */}
          {unified.length > 0 && (
            <div className="flex items-center gap-2 mt-2 text-[11px]" data-testid="customer-file-files-selectbar">
              <label className="inline-flex items-center gap-1.5 cursor-pointer text-slate-600">
                <input type="checkbox" className="h-3.5 w-3.5 accent-indigo-600" checked={allSelected} onChange={toggleAll} data-testid="customer-file-files-select-all" />
                {allSelected ? "Clear all" : "Select all"}
              </label>
              <span className="text-muted-foreground" data-testid="customer-file-files-selected-count">
                {selected.size} selected
              </span>
              <Button size="sm" variant={selected.size ? "default" : "outline"} className="h-6 px-2 text-[10px] ml-auto"
                onClick={downloadZip} disabled={!selected.size || zipping} data-testid="customer-file-files-download-zip">
                {zipping ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                {zipping ? (zipProgress && zipProgress.total ? `Zipping ${zipProgress.done}/${zipProgress.total}…` : "Zipping…") : `Download selected (.zip)`}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {err && <div className="text-red-700 text-[11px] px-3 py-2">{err}</div>}
          {loading && unified.length === 0 && (
            <div className="text-[10px] text-muted-foreground py-3 text-center">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> loading
            </div>
          )}
          {!loading && unified.length === 0 && !err && (
            <div className="italic text-muted-foreground text-xs px-3 py-3">
              No files found across LoanDisk · Monday · DocuPilot · DocuSign · OneDrive.
            </div>
          )}
          {/* Emma #4 — horizontal scroll so long file names stay readable */}
          <div className="max-h-[320px] overflow-y-auto overflow-x-auto px-3 space-y-1 py-1.5" data-testid="customer-file-files-scroll">
            {unified.map(row => (
              <div
                key={row.key}
                className="w-full min-w-max flex items-center gap-2 group text-left hover:bg-slate-50 rounded px-1.5 py-1"
                data-testid="customer-file-file-row"
                onContextMenu={(e) => { if (row.kind === "onedrive") { e.preventDefault(); startRename(row); } }}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 accent-indigo-600 flex-shrink-0"
                  checked={selected.has(row.zipKey)}
                  onChange={() => toggleSelected(row.zipKey)}
                  onClick={(e) => e.stopPropagation()}
                  title="Select for zip download"
                  data-testid="customer-file-file-select"
                />
                {renaming?.key === row.key ? (
                  <div className="flex-1 min-w-0 flex items-center gap-2" data-testid="customer-file-rename-editor">
                    <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${sourceTone(row.source)}`}>{row.source}</Badge>
                    <input
                      autoFocus
                      value={renaming.base}
                      disabled={renaming.saving}
                      onChange={(e) => setRenaming(r => ({ ...r, base: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitRename(); } if (e.key === "Escape") setRenaming(null); }}
                      onClick={(e) => e.stopPropagation()}
                      className="flex-1 min-w-[160px] h-6 text-[11px] px-1.5 border rounded bg-white"
                      data-testid="customer-file-rename-input"
                    />
                    {renaming.ext && <span className="text-[11px] text-slate-500 flex-shrink-0" data-testid="customer-file-rename-ext">{renaming.ext}</span>}
                    <button type="button" onClick={commitRename} disabled={renaming.saving} className="p-1 rounded text-emerald-700 hover:bg-emerald-50" title="Save (Enter)" data-testid="customer-file-rename-save">
                      {renaming.saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <button type="button" onClick={() => setRenaming(null)} disabled={renaming.saving} className="p-1 rounded text-slate-400 hover:text-slate-700" title="Cancel (Esc)" data-testid="customer-file-rename-cancel">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                <button
                  onClick={() => openPreview(row)}
                  disabled={previewLoading}
                  title={row.origin?.uploaded_by ? `${row.name} — uploaded by ${row.origin.uploaded_by}` : row.name}
                  className="flex-1 min-w-0 flex items-center gap-2 text-left"
                >
                  <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${sourceTone(row.source)}`}>{row.source}</Badge>
                  {row.origin?.loan_ref && row.origin.loan_ref !== caseRow?.loan_application_id && (
                    <span className="text-[9px] text-amber-800 flex-shrink-0" title="Held on an earlier loan in this restructure family" data-testid="customer-file-file-loan-ref">{row.origin.loan_ref}</span>
                  )}
                  <span className="whitespace-nowrap flex-1 text-[11px] text-slate-700 group-hover:text-slate-900">{row.name}</span>
                  {row.origin?.uploaded_by && (
                    <span
                      className="text-[10px] text-indigo-700 hidden md:inline truncate max-w-[110px]"
                      data-testid="customer-file-file-uploader"
                    >
                      by {row.origin.uploaded_by}
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground tabular-nums">{fmtBytes(row.size)}</span>
                  <span className="text-[10px] text-muted-foreground hidden sm:inline">{fmtDate(row.date)}</span>
                </button>
                )}
                {/* RHS hover actions: eye = preview · pencil = rename (OneDrive only) · bin = hide (OneDrive only) */}
                <button
                  onClick={(e) => { e.stopPropagation(); openPreview(row); }}
                  disabled={previewLoading}
                  title="Preview"
                  className="p-1 rounded hover:bg-indigo-50 text-indigo-600 opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  data-testid="customer-file-preview-file"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
                {row.kind === "onedrive" && renaming?.key !== row.key && (
                  <button
                    onClick={(e) => { e.stopPropagation(); startRename(row); }}
                    title="Rename (also right-click the row)"
                    className="p-1 rounded hover:bg-amber-50 text-slate-400 hover:text-amber-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    data-testid="customer-file-rename-file"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {/* Item 16: delete button — visible only for OneDrive
                    rows because the aggregated source rows live in
                    LoanDisk / Monday / DocuSign / DocuPilot. */}
                {row.kind === "onedrive" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                    title="Remove from portal Files tab (keeps the file on OneDrive)"
                    className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-700 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    data-testid="customer-file-delete-file"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
    </>
  );
}

// ─── File preview modal — inline iframe, no auto-download ──────────────────
// ─── Aircall Calls (compact — tighter typography, scrollable) ──────────────
function AircallCallsCompact({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [logStatus, setLogStatus] = useState(null);
  const [phoneFilter, setPhoneFilter] = useState("all");  // all | primary | additional | contact-id

  const fetchCalls = useCallback(async (filter = phoneFilter) => {
    try {
      setLoading(true);
      // contact-id filters need to be sent as phone_filter=contact&contact_id=...
      const params = filter && filter.startsWith("ct:") ?
        { phone_filter: "contact", contact_id: filter.slice(3) } :
        { phone_filter: filter || "all" };
      const r = await getAgeingAircallCalls(caseRow.case_id, { limit: 30, ...params });
      setData(r);
    } finally { setLoading(false); }
  }, [caseRow.case_id, phoneFilter]);

  useEffect(() => { fetchCalls(phoneFilter); }, [fetchCalls, phoneFilter]);

  const fetchLogStatus = useCallback(async () => {
    try {
      const r = await getAircallCallsLogStatus();
      setLogStatus(r);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { fetchLogStatus(); }, [fetchLogStatus]);

  const handleSyncHistory = async () => {
    const days = parseInt(window.prompt("Sync how many days of company-wide Aircall history?", "90") || "0", 10);
    if (!days || days < 1) return;
    setSyncing(true);
    try {
      const r = await syncAircallCallsLog({ days_back: days, max_pages: 200 });
      toast.success(`Aircall sync: ${r.fetched} fetched (${r.inserted} new, ${r.updated} updated)`);
      fetchCalls();
      fetchLogStatus();
    } catch (e) {
      toast.error("Aircall sync failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSyncing(false);
    }
  };

  const calls = data?.calls || [];
  const available = data?.available_phones || [];

  return (
    <Card data-testid="ageing-aircall-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-xs flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5 text-emerald-600" />
            Calls
            <Badge variant="outline" className="text-[9px] font-normal">{calls.length}</Badge>
          </CardTitle>
          <button
            onClick={handleSyncHistory}
            disabled={syncing}
            className="text-[10px] text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
            title={logStatus?.total_rows ? `Local mirror: ${logStatus.total_rows} calls. Last sync: ${logStatus?.last_sync?.ran_at?.slice(0,10) || "—"}` : "Bulk-import company-wide Aircall history"}
            data-testid="customer-file-sync-aircall-history"
          >
            {syncing ? <Loader2 className="w-3 h-3 animate-spin inline" /> : (<><ArrowRightCircle className="w-3 h-3 inline mr-0.5" /> Sync history</>)}
          </button>
        </div>
        {/* Phone filter dropdown — switch between primary borrower, guarantor/PG, or combined */}
        {available.length > 0 && (
          <div className="mt-1">
            <select
              value={phoneFilter}
              onChange={(e) => setPhoneFilter(e.target.value)}
              className="w-full text-[10px] border rounded h-6 px-1 bg-white"
              data-testid="customer-file-aircall-phone-filter"
            >
              <option value="all">All numbers combined ({available.length})</option>
              <option value="primary">Primary borrower only</option>
              {available.length > 1 && <option value="additional">All additional contacts</option>}
              <optgroup label="Specific contact">
                {available.map((p, i) => (
                  <option key={p.contact_id || `pri-${i}`} value={p.contact_id ? `ct:${p.contact_id}` : "primary"}>
                    {p.label} · {p.phone}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
        )}
        {logStatus?.total_rows > 0 && (
          <div className="text-[9px] text-muted-foreground mt-1">
            Mirror: {logStatus.total_rows.toLocaleString()} calls
            {logStatus.last_sync?.ran_at && <> · last sync {new Date(logStatus.last_sync.ran_at).toLocaleDateString("en-GB")}</>}
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-[10px] text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline" /> loading
          </div>
        ) : calls.length === 0 ? (
          <div className="text-[10px] text-muted-foreground italic py-3 px-3 text-center">No calls.</div>
        ) : (
          <div className="max-h-[260px] overflow-y-auto divide-y">
            {calls.map(c => (
              <div key={c.id} className="px-3 py-1.5 text-[10px] hover:bg-slate-50">
                <div className="flex items-center gap-2">
                  <span className={`w-1 h-1 rounded-full ${c.status === "missed" ? "bg-red-500" : c.direction === "inbound" ? "bg-emerald-500" : "bg-indigo-500"}`} />
                  <span className="text-muted-foreground whitespace-nowrap">{c.started_at ? new Date(c.started_at * 1000).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                  <span className="flex-1 truncate">{c.user_name || "—"}</span>
                  {c.duration > 0 && <span className="text-muted-foreground tabular-nums">{c.duration}s</span>}
                </div>
                {c.recording && (
                  <audio
                    controls
                    preload="none"
                    src={c.recording}
                    className="w-full h-7 mt-1"
                    data-testid={`call-audio-${c.id}`}
                    style={{ accentColor: "#4f46e5" }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Aircall SMS (compact + scrollable + opens phone iframe modal) ─────────
function AircallSmsCompact({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const fetchSms = useCallback(async () => {
    try {
      setLoading(true);
      const { getAgeingAircallSms } = await import("@/lib/api");
      const r = await getAgeingAircallSms(caseRow.case_id, { limit: 30 });
      setData(r);
    } finally { setLoading(false); }
  }, [caseRow.case_id]);
  useEffect(() => { fetchSms(); }, [fetchSms]);

  const messages = data?.messages || [];
  const phone = data?.phone_raw;

  return (
    <>
    <Card data-testid="ageing-aircall-sms-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-xs flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-sky-600" />
            SMS
            <Badge variant="outline" className="text-[9px] font-normal">{messages.length}</Badge>
          </CardTitle>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => setPhoneOpen(true)} data-testid="ageing-aircall-phone-btn">
            <Phone className="w-3 h-3 mr-1" /> Open Aircall
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-[10px] text-muted-foreground py-3 text-center">
            <Loader2 className="w-3 h-3 animate-spin inline" /> loading
          </div>
        ) : messages.length === 0 ? (
          <div className="text-[10px] text-muted-foreground italic py-3 px-3 text-center">
            Open Aircall to send SMS — they&apos;ll flow back here via webhook.
          </div>
        ) : (
          <div className="max-h-[200px] overflow-y-auto divide-y">
            {messages.map((m, i) => (
              <div key={m.message_id || i} className={`px-3 py-1.5 text-[10px] ${m.direction === "outbound" ? "bg-sky-50/40" : ""}`}>
                <div className="flex items-center gap-1.5 text-muted-foreground mb-0.5">
                  <span>{m.direction === "outbound" ? "→" : "←"}</span>
                  <span>{m.sent_at ? new Date(m.sent_at).toLocaleDateString("en-GB") : "—"}</span>
                </div>
                <div className="text-slate-800 line-clamp-2 text-[11px]">{m.body}</div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
    {/* Aircall Phone iframe modal */}
    <Dialog open={phoneOpen} onOpenChange={setPhoneOpen}>
      <DialogContent className="max-w-md p-0 sm:max-w-md">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm flex items-center gap-2">
            <Phone className="w-4 h-4 text-emerald-600" />
            Aircall Phone {phone && <Badge variant="outline" className="text-[10px] font-mono">{phone}</Badge>}
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Sign in with your Aircall credentials — calls + SMS sent here flow back into the case via webhook.
          </p>
        </DialogHeader>
        <iframe
          title="Aircall Phone"
          src={phone ? `https://phone.aircall.io/dial?phone_number=${encodeURIComponent(phone)}` : "https://phone.aircall.io/"}
          allow="microphone; autoplay; clipboard-read; clipboard-write"
          className="w-full"
          style={{ height: 600, border: 0 }}
        />
      </DialogContent>
    </Dialog>
    </>
  );
}


// ─── Loan Schedule view — replicates the LoanDisk "Loan Schedule" tab ─────
function LoanScheduleView({ caseRow }) {
  // Build the loan chain for this case (current iteration first, then previous).
  // For non-restructured loans this is just the single current loan.
  const [chain, setChain] = useState(null);     // borrower-loans response
  const [schedules, setSchedules] = useState({});  // sfNumber → { rows, loan_id, loading, err }
  const [expanded, setExpanded] = useState({});    // sfNumber → bool

  const currentSf = (caseRow.loan_application_id || "").trim();
  const baseSf = currentSf.replace(/\/\d+$/, "");

  // 1) Load the chain (borrower loans filtered to this SF# family)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAgeingBorrowerLoans(caseRow.case_id, { count: 50 });
        if (!cancelled) setChain(r);
      } catch (e) {
        if (!cancelled) setChain({ ok: false, loans: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [caseRow.case_id]);

  // Filter to this loan family, current first, then previous iterations (newest → oldest)
  const family = useMemo(() => {
    const all = chain?.loans || [];
    const matching = all.filter(l => {
      const sf = (l.loan_application_id || "").trim();
      return sf === baseSf || sf.startsWith(baseSf + "/");
    });
    // Sort: current (is_current=true) first; among the rest, by iteration suffix desc
    const suffixOf = (sf) => {
      const m = (sf || "").match(/\/(\d+)$/);
      return m ? parseInt(m[1], 10) : 1;
    };
    return matching.sort((a, b) => {
      if (a.is_current && !b.is_current) return -1;
      if (!a.is_current && b.is_current) return 1;
      return suffixOf(b.loan_application_id) - suffixOf(a.loan_application_id);  // /3 → /2 → base
    });
  }, [chain, baseSf]);

  // 2) Default-expand the current iteration; collapse the rest
  useEffect(() => {
    if (family.length > 0 && Object.keys(expanded).length === 0) {
      setExpanded({ [family[0].loan_application_id]: true });
    }
  }, [family, expanded]);

  // 3) Fetch schedules lazily — current iteration is fetched immediately,
  //    previous iterations only when the section is expanded.
  const fetchSchedule = useCallback(async (sf, isCurrentLoan) => {
    setSchedules(prev => {
      if (prev[sf]?.rows || prev[sf]?.loading) return prev;
      return { ...prev, [sf]: { loading: true } };
    });
    try {
      const r = isCurrentLoan
        ? await getAgeingLoanSchedule(caseRow.case_id, { kind: "adjusted", count: 500 })
        : await getAgeingBorrowerLoanSchedule(caseRow.case_id, sf, { kind: "adjusted", count: 500 });
      setSchedules(prev => ({ ...prev, [sf]: { rows: r.rows || [], loan_id: r.loan_id, loading: false } }));
    } catch (e) {
      setSchedules(prev => ({ ...prev, [sf]: { rows: [], loading: false, err: e.response?.data?.detail || e.message } }));
    }
  }, [caseRow.case_id]);

  // Pre-fetch the current iteration on mount + lazy-fetch the rest in the
  // background. The schedules cache feeds the variance rows so they show
  // the *actual* carried balance + interest rolled in (LoanDisk takes the
  // older iteration's full outstanding — principal + accrued interest —
  // as the new iteration's principal), not just an `original - paid` proxy.
  useEffect(() => {
    if (currentSf && !schedules[currentSf]) {
      fetchSchedule(currentSf, true);
    }
  }, [currentSf, schedules, fetchSchedule]);

  useEffect(() => {
    // Background-fetch previous iterations once the chain is known.
    for (const it of family) {
      if (!it.is_current && !schedules[it.loan_application_id]) {
        fetchSchedule(it.loan_application_id, false);
      }
    }
  }, [family, schedules, fetchSchedule]);

  // When user expands a previous iteration, lazy-fetch its schedule
  const toggleSection = (sf, isCurrentLoan) => {
    setExpanded(prev => ({ ...prev, [sf]: !prev[sf] }));
    if (!schedules[sf]) fetchSchedule(sf, isCurrentLoan);
  };

  // Money parser — LoanDisk returns "1,092.82" / "" / 0 / null. Normalise.
  const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const fmt = (v) => {
    const n = num(v);
    if (n === null) return "";
    return n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Live header strip — current iteration only
  const currentSched = schedules[currentSf];
  const headerStrip = useMemo(() => {
    const rows = currentSched?.rows || [];
    const last = rows[rows.length - 1];
    const principalBalance =
      num(last?.["Principal Balance Adjusted"]) ??
      num(last?.["Principal Balance"]) ??
      num(caseRow.total_principal_outstanding) ??
      null;
    return {
      loan_app:   caseRow.loan_application_id,
      released:   caseRow.released_date,
      maturity:   caseRow.loandisk_maturity_date || null,
      principal:  num(caseRow.principal_amount),
      arrears:    num(caseRow.arrears_amount),
      balance:    num(caseRow.balance_amount),
      principal_balance: principalBalance,
      status:     caseRow.loandisk_status_text || "—",
      dpd:        caseRow.days_past_due,
    };
  }, [caseRow, currentSched]);

  const isRestructured = family.length > 1;

  return (
    <div className="bg-white rounded-lg border shadow-sm" data-testid="loan-schedule-view">
      {/* Loan header strip — mirrors LoanDisk's loan detail header */}
      <div className="px-4 py-3 border-b bg-gradient-to-r from-indigo-50 via-white to-slate-50">
        <div className="grid grid-cols-7 gap-3 text-xs">
          <HeaderCell label="Loan#"      value={headerStrip.loan_app} mono accent="text-indigo-900" />
          <HeaderCell label="Released"   value={headerStrip.released} />
          <HeaderCell label="Maturity"   value={headerStrip.maturity} />
          <HeaderCell label="Principal"  value={headerStrip.principal !== null ? `${cur()}${fmt(headerStrip.principal)}` : "—"} mono />
          <HeaderCell label="Arrears"    value={headerStrip.arrears   !== null ? `${cur()}${fmt(headerStrip.arrears)}` : "—"} mono accent="text-rose-700" />
          <HeaderCell label="Balance"    value={headerStrip.balance   !== null ? `${cur()}${fmt(headerStrip.balance)}` : "—"} mono />
          <HeaderCell
            label="Status"
            value={
              <span className="inline-flex items-center gap-1.5">
                {headerStrip.status}
                {headerStrip.dpd > 0 && (
                  <span className="text-[10px] text-rose-700 font-semibold">{headerStrip.dpd}d</span>
                )}
              </span>
            }
          />
        </div>
      </div>

      {/* Restructure banner — only shown when this loan is part of a chain */}
      {isRestructured && (
        <div className="px-4 py-2 border-b bg-blue-50/40 flex items-center gap-2 text-[11px]" data-testid="loan-schedule-chain-banner">
          <RefreshCw className="w-3.5 h-3.5 text-blue-700 shrink-0" />
          <span className="text-blue-900 font-semibold">{family.length}× restructured</span>
          <span className="text-blue-700/80">
            chain: {family.map(f => f.loan_application_id).join(" → ")}
          </span>
          <span className="ml-auto text-[10px] text-muted-foreground italic">
            Click any iteration below to view its schedule.
          </span>
        </div>
      )}

      {/* Schedule sections — one per iteration in the chain */}
      <div>
        {family.length === 0 && currentSf ? (
          // Chain hasn't loaded yet → render single-section fallback for the current loan
          <ScheduleSection
            sf={currentSf}
            isCurrent
            isOnly
            sched={currentSched}
            expanded
            onToggle={() => {}}
            fmt={fmt}
            num={num}
          />
        ) : (
          family.map((it, idx) => {
            const older = family[idx + 1];   // newest → oldest, so older sits below
            return (
              <Fragment key={it.loan_application_id}>
                <ScheduleSection
                  sf={it.loan_application_id}
                  loan={it}
                  isCurrent={it.is_current}
                  isOnly={family.length === 1}
                  sched={schedules[it.loan_application_id]}
                  expanded={!!expanded[it.loan_application_id]}
                  onToggle={() => toggleSection(it.loan_application_id, it.is_current)}
                  fmt={fmt}
                  num={num}
                  indexInChain={idx}
                />
                {older && (
                  <RestructureVarianceRow
                    newer={it}
                    older={older}
                    olderSched={schedules[older.loan_application_id]}
                  />
                )}
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

// One collapsible iteration section — header is always visible, schedule
// table renders on expand. The current iteration is bold + auto-expanded.
function ScheduleSection({ sf, loan, isCurrent, isOnly, sched, expanded, onToggle, fmt, num, indexInChain }) {
  const rowCount = sched?.rows?.length || 0;
  const status = loan?.status_label || "—";

  return (
    <div className={isCurrent ? "" : "border-t border-slate-100"} data-testid={`schedule-section-${sf}`}>
      {/* Section header — clickable to expand/collapse (only when chain has >1 iteration) */}
      {!isOnly && (
        <button
          onClick={onToggle}
          className={`w-full flex items-center gap-2 px-4 py-2 hover:bg-slate-50 ${isCurrent ? "bg-amber-50/40" : "bg-white"}`}
          data-testid={`schedule-section-header-${sf}`}
        >
          <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? "" : "-rotate-90"}`} />
          <span className="font-mono text-xs font-semibold text-slate-800">{sf}</span>
          {isCurrent && <Badge className="bg-amber-500 text-white text-[9px] px-1.5 h-4">CURRENT</Badge>}
          <Badge variant="outline" className="text-[10px] font-normal">{status}</Badge>
          {indexInChain > 0 && <span className="text-[10px] text-slate-500 italic">previous iteration</span>}
          {loan?.released_date && (
            <span className="text-[10px] text-muted-foreground ml-auto">
              Released {ukDate(loan.released_date)}
              {loan.maturity_date && <> · Maturity {ukDate(loan.maturity_date)}</>}
              {loan.principal != null && <> · Principal {cur()}{Number(loan.principal).toLocaleString(undefined, { maximumFractionDigits: 0 })}</>}
              {rowCount > 0 && <> · {rowCount} rows</>}
            </span>
          )}
        </button>
      )}

      {/* Schedule table — adjusted only */}
      {expanded && (
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          {!sched || sched.loading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
              Pulling schedule from LoanDisk…
            </div>
          ) : sched.err ? (
            <div className="m-3 p-3 rounded-md border border-rose-200 bg-rose-50 text-sm text-rose-900">
              <div className="font-semibold mb-0.5">Could not load schedule</div>
              <div className="text-xs">{sched.err}</div>
            </div>
          ) : !sched.rows?.length ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No schedule rows returned by LoanDisk for {sf}.
            </div>
          ) : (
            <AdjustedScheduleTable rows={sched.rows} fmt={fmt} num={num} />
          )}
        </div>
      )}
    </div>
  );
}

function HeaderCell({ label, value, mono, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold mt-0.5 truncate ${mono ? "tabular-nums font-mono" : ""} ${accent || "text-slate-800"}`}>
        {value || "—"}
      </div>
    </div>
  );
}

// Adjusted schedule — interleaves Payment Received rows with scheduled repayments,
// matching the LoanDisk "Adjusted Loan Schedule" view shown in the user's screenshot.
function AdjustedScheduleTable({ rows, fmt, num }) {
  return (
    <table className="w-full text-xs">
      <thead className="sticky top-0 bg-slate-50 border-b z-10">
        <tr className="text-left">
          <th className="px-2 py-2 font-semibold text-slate-700 w-8">#</th>
          <th className="px-2 py-2 font-semibold text-slate-700">Date</th>
          <th className="px-2 py-2 font-semibold text-slate-700">Description</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Principal</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Interest</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Fees</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Penalty</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Due</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Paid</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Pending Due</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Total Due</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Principal Due</th>
          <th className="px-2 py-2 font-semibold text-slate-700 text-right">Principal Balance</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, idx) => {
          const desc = String(r.Description || "");
          const isPayment = desc.toLowerCase().includes("payment received");
          // Highlight payment rows like LoanDisk does
          const rowCls = isPayment
            ? "bg-emerald-50/60 font-semibold"
            : "border-b border-slate-100 hover:bg-slate-50";
          return (
            <tr key={idx} className={rowCls}>
              <td className="px-2 py-1.5 text-slate-500 tabular-nums">{r["#"] || ""}</td>
              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">{r.Date || ""}</td>
              <td className="px-2 py-1.5">{desc}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.Principal)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.Interest)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.Fees)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{num(r.Penalty) ? fmt(r.Penalty) : "0"}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.Due)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-medium">{fmt(r.Paid)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r["Pending Due"])}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r["Total Due"])}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r["Principal Due"])}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">{fmt(r["Principal Balance Adjusted"])}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Plain-English summary of how one iteration became the next.
// Sits between sections: shows the older→newer transition with the
// actual carried balance and the interest/fees that LoanDisk rolled
// into the new principal during the restructure.
function RestructureVarianceRow({ newer, older, olderSched }) {
  const num = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  // Find the principal balance at the point of restructure from the older
  // iteration's adjusted schedule. LoanDisk often zeros out the final row
  // when the loan is closed-by-restructure — so walk backwards looking for
  // the last NON-ZERO principal balance (that's the balance right before
  // restructure consumed it).
  const principalAtRestructure = useMemo(() => {
    const rows = olderSched?.rows || [];
    for (let i = rows.length - 1; i >= 0; i--) {
      const v = num(rows[i]?.["Principal Balance Adjusted"]) ?? num(rows[i]?.["Principal Balance"]);
      if (v !== null && v > 0.5) return v;
    }
    return null;
  }, [olderSched]);

  const olderPrincipal = Number(older.principal || 0);
  const olderPaid      = Number(older.total_paid || 0);
  const proxyCarried   = Math.max(0, olderPrincipal - olderPaid);

  // Total carried = new loan's principal (LoanDisk rolls the full older
  // outstanding into the new principal — that's the user's observation).
  const totalCarried = Number(newer.principal || 0);

  // Prefer the schedule's actual principal-at-restructure when we have it;
  // fall back to the chain-level proxy (older.principal - total_paid) when
  // the schedule wasn't conclusive.
  const haveAccurate = principalAtRestructure !== null && principalAtRestructure > 0.5;
  const principalPart = haveAccurate ? principalAtRestructure : proxyCarried;
  const interestRolled = Math.max(0, totalCarried - principalPart);

  const fmt0 = (n) => `${cur()}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div
      className="border-t border-b border-blue-100 bg-blue-50/30 px-4 py-1.5 text-[11px] flex items-center gap-x-2 gap-y-1 flex-wrap"
      data-testid={`schedule-variance-${older.loan_application_id}-to-${newer.loan_application_id}`}
    >
      <RefreshCw className="w-3 h-3 text-blue-700 shrink-0" />
      <span className="text-slate-800">
        <span className="font-mono font-semibold">{older.loan_application_id}</span>
        <span className="text-slate-500"> restructured into </span>
        <span className="font-mono font-semibold">{newer.loan_application_id}</span>
        {newer.released_date && (
          <>
            <span className="text-slate-500"> on </span>
            <span className="font-semibold">{ukDate(newer.released_date)}</span>
          </>
        )}
      </span>
      <span className="text-slate-400">—</span>
      <span>
        <span className="font-semibold text-slate-800">{fmt0(totalCarried)}</span>
        <span className="text-slate-600"> carried as new principal</span>
      </span>
      {totalCarried > 0 && (
        <span className="text-slate-500">
          {" ("}
          <span className="font-semibold text-slate-700">{fmt0(principalPart)}</span>
          <span> principal</span>
          {interestRolled > 0 && (
            <>
              <span> + </span>
              <span className="font-semibold text-amber-700">{fmt0(interestRolled)}</span>
              <span> interest/fees rolled in</span>
            </>
          )}
          {")"}
        </span>
      )}
      {!haveAccurate && (
        <span
          className="text-[9px] uppercase tracking-wide text-blue-700/70 italic ml-1"
          title="Loading older iteration's schedule for the exact principal/interest split…"
        >
          (approx)
        </span>
      )}
    </div>
  );
}


// ─── Previous loans tab — all LoanDisk loans against THIS borrower ────────
// Lists every loan LoanDisk has on file for the borrower entity behind
// this case, with click-to-expand drill-down into each loan's payments
// and schedule (without leaving the customer file).
function BorrowerLoansView({ caseRow }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});  // loan_app_id → true

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true); setErr(null);
        const r = await getAgeingBorrowerLoans(caseRow.case_id);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseRow.case_id]);

  if (loading) {
    return (
      <Card data-testid="customer-file-borrower-loans"><CardContent className="py-12 text-center text-sm text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Loading borrower loan history from LoanDisk…
      </CardContent></Card>
    );
  }
  if (err) return <Card data-testid="customer-file-borrower-loans"><CardContent className="py-6 text-rose-700 text-sm">{err}</CardContent></Card>;
  if (!data?.ok || !(data?.loans?.length)) {
    return (
      <Card data-testid="customer-file-borrower-loans"><CardContent className="py-12 text-center text-sm text-muted-foreground">
        {data?.reason || "No other loans found against this borrower."}
      </CardContent></Card>
    );
  }

  const loans = data.loans;
  const totalCount = loans.length;
  const totalPaid = loans.reduce((s, l) => s + (l.total_paid || 0), 0);
  const currentCount = loans.filter(l => l.is_current).length;

  return (
    <Card data-testid="customer-file-borrower-loans">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-600" />
            Previous loans
            <Badge variant="outline" className="text-[10px] font-normal">{totalCount}</Badge>
          </CardTitle>
          <div className="text-[11px] text-muted-foreground">
            Borrower ID <span className="font-mono">{data.borrower_id}</span> ·{" "}
            Total repaid across loans <span className="font-semibold text-emerald-700">{cur()}{totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
            {currentCount > 0 && <> · <span className="text-amber-700">{currentCount} current</span></>}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loans.map(loan => (
          <BorrowerLoanRow
            key={loan.loan_application_id || loan.loan_id}
            caseId={caseRow.case_id}
            loan={loan}
            expanded={!!expanded[loan.loan_application_id]}
            onToggle={() => setExpanded(e => ({ ...e, [loan.loan_application_id]: !e[loan.loan_application_id] }))}
          />
        ))}
      </CardContent>
    </Card>
  );
}

const LOAN_STATUS_TONE = {
  Current:       "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Past Maturity":"bg-amber-100 text-amber-900 border-amber-300",
  Arrears:       "bg-rose-100 text-rose-800 border-rose-300",
  Closed:        "bg-slate-200 text-slate-700 border-slate-300",
  "Written-off": "bg-slate-300 text-slate-800 border-slate-400",
  Restructured:  "bg-blue-100 text-blue-800 border-blue-300",
  Legal:         "bg-red-200 text-red-900 border-red-400",
};

function BorrowerLoanRow({ caseId, loan, expanded, onToggle }) {
  const isRestructured = /\/\d+$/.test((loan.loan_application_id || "").trim());
  const tone = LOAN_STATUS_TONE[loan.status_label] || "bg-slate-100 text-slate-700 border-slate-300";

  return (
    <div className={`border rounded-md ${loan.is_current ? "border-amber-300 bg-amber-50/30" : "border-slate-200 bg-white"}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-start justify-between gap-3 text-left hover:bg-slate-50"
        data-testid={`borrower-loan-row-${loan.loan_application_id || loan.loan_id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-xs font-semibold text-slate-800">
              {loan.loan_application_id || `#${loan.loan_id}`}
            </span>
            <Badge variant="outline" className={`text-[10px] ${tone}`}>{loan.status_label}</Badge>
            {loan.is_current && <Badge className="bg-amber-500 text-white text-[9px] px-1.5 h-4">CURRENT</Badge>}
            {isRestructured && <Badge className="bg-red-600 text-white text-[9px] px-1.5 h-4">RESTRUCTURED</Badge>}
            {loan.days_past_due > 0 && <span className="text-[10px] text-rose-700 font-semibold">{loan.days_past_due} dpd</span>}
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Released <span className="text-slate-700">{loan.released_date || "—"}</span>
            {" · "}Maturity <span className="text-slate-700">{loan.maturity_date || "—"}</span>
            {loan.interest_rate != null && <> · {loan.interest_rate}% {loan.interest_period || ""}</>}
          </div>
        </div>
        <div className="text-right shrink-0 text-[11px]">
          <div className="text-muted-foreground">Principal</div>
          <div className="font-semibold text-slate-900 tabular-nums">{cur()}{Number(loan.principal || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="mt-0.5 text-muted-foreground">Balance</div>
          <div className="font-semibold tabular-nums">{cur()}{Number(loan.balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && <BorrowerLoanDetail caseId={caseId} loan={loan} />}
    </div>
  );
}

function BorrowerLoanDetail({ caseId, loan }) {
  const [tab, setTab] = useState("payments");  // payments | schedule | terms
  return (
    <div className="border-t border-slate-200 bg-slate-50/50 p-3" data-testid={`borrower-loan-detail-${loan.loan_application_id}`}>
      <div className="flex items-center gap-1 mb-2.5">
        {["payments", "schedule", "terms"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-2.5 py-1 text-[11px] rounded ${tab === t ? "bg-slate-900 text-white font-semibold" : "text-slate-700 hover:bg-slate-200"}`}
            data-testid={`borrower-loan-detail-tab-${t}`}
          >
            {t === "payments" ? "Payments" : t === "schedule" ? "Schedule" : "Terms"}
          </button>
        ))}
      </div>
      {tab === "payments"  && <BorrowerLoanPayments caseId={caseId} loanAppId={loan.loan_application_id} />}
      {tab === "schedule"  && <BorrowerLoanSchedule caseId={caseId} loanAppId={loan.loan_application_id} />}
      {tab === "terms"     && <BorrowerLoanTerms loan={loan} />}
    </div>
  );
}

function BorrowerLoanTerms({ loan }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
      <TermStat label="Principal"           value={`${cur()}${Number(loan.principal || 0).toLocaleString()}`} />
      <TermStat label="Balance"             value={`${cur()}${Number(loan.balance || 0).toLocaleString()}`} />
      <TermStat label="Total paid"          value={`${cur()}${Number(loan.total_paid || 0).toLocaleString()}`} />
      <TermStat label="Interest"            value={loan.interest_rate != null ? `${loan.interest_rate}% ${loan.interest_period || ""}` : "—"} />
      <TermStat label="Repayment freq"      value={loan.repayment_frequency || "—"} />
      <TermStat label="# of repayments"     value={loan.num_of_repayments != null ? String(loan.num_of_repayments) : "—"} />
      <TermStat label="Released"            value={loan.released_date || "—"} />
      <TermStat label="Maturity"            value={loan.maturity_date || "—"} />
    </div>
  );
}

function TermStat({ label, value }) {
  return (
    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xs font-semibold tabular-nums text-slate-800 truncate">{value}</div>
    </div>
  );
}

function BorrowerLoanPayments({ caseId, loanAppId }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getAgeingBorrowerLoanRepayments(caseId, loanAppId);
        if (!cancelled) setRows(r);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, loanAppId]);
  if (err) return <div className="text-xs text-rose-700">{err}</div>;
  if (!rows) return <div className="text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading payments…</div>;
  const items = rows.repayments || [];
  if (items.length === 0) return <div className="text-xs text-muted-foreground italic">No payments recorded on this loan.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead className="text-[9px] uppercase text-muted-foreground border-b">
          <tr>
            <th className="text-left py-1 pr-2">Date</th>
            <th className="text-right pr-2">Amount</th>
            <th className="text-right pr-2">Principal</th>
            <th className="text-right pr-2">Interest</th>
            <th className="text-right pr-2">Fees</th>
            <th className="text-right pr-2">Running paid</th>
          </tr>
        </thead>
        <tbody>
          {items.slice().reverse().map((r, idx) => (
            <tr key={r.id || idx} className="border-b last:border-0">
              <td className="py-1 pr-2 font-mono">{r.date || "—"}</td>
              <td className="text-right pr-2 tabular-nums font-semibold text-emerald-700">{cur()}{Number(r.amount || 0).toLocaleString()}</td>
              <td className="text-right pr-2 tabular-nums">{cur()}{Number(r.principal || 0).toLocaleString()}</td>
              <td className="text-right pr-2 tabular-nums">{cur()}{Number(r.interest || 0).toLocaleString()}</td>
              <td className="text-right pr-2 tabular-nums">{cur()}{Number(r.fees || 0).toLocaleString()}</td>
              <td className="text-right pr-2 tabular-nums text-slate-700">{cur()}{Number(r.running_paid || 0).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-muted-foreground mt-1.5">
        {rows.count} payment{rows.count === 1 ? "" : "s"} · Total {cur()}{Number(rows.total_paid || 0).toLocaleString()}
      </div>
    </div>
  );
}

function BorrowerLoanSchedule({ caseId, loanAppId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [kind, setKind] = useState("adjusted");
  useEffect(() => {
    let cancelled = false;
    setData(null);
    (async () => {
      try {
        const r = await getAgeingBorrowerLoanSchedule(caseId, loanAppId, { kind });
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId, loanAppId, kind]);
  if (err) return <div className="text-xs text-rose-700">{err}</div>;
  if (!data) return <div className="text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Loading schedule…</div>;
  const rows = data.rows || [];
  return (
    <div>
      <div className="flex items-center gap-1 mb-2 bg-white inline-flex rounded p-0.5 border border-slate-200">
        {["original", "adjusted"].map(k => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`px-2 py-0.5 text-[10px] rounded ${kind === k ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            data-testid={`borrower-loan-schedule-kind-${k}`}
          >
            {k}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground mb-1">{rows.length} row{rows.length === 1 ? "" : "s"}</div>
      <div className="overflow-x-auto max-h-72 overflow-y-auto">
        <table className="w-full text-[10px] border-collapse">
          <thead className="bg-slate-100 text-[9px] uppercase text-slate-700 sticky top-0">
            <tr>
              <th className="text-left p-1">#</th>
              <th className="text-left p-1">Date</th>
              <th className="text-left p-1">Description</th>
              <th className="text-right p-1">Principal</th>
              <th className="text-right p-1">Interest</th>
              <th className="text-right p-1">Total Due</th>
              <th className="text-right p-1">Paid</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isPayment = (row.Description || "").toLowerCase().includes("payment");
              return (
                <tr key={idx} className={`border-b ${isPayment ? "bg-emerald-50 font-medium" : ""}`}>
                  <td className="p-1 font-mono text-slate-500">{row["#"] ?? idx + 1}</td>
                  <td className="p-1 font-mono">{row.Date || "—"}</td>
                  <td className="p-1">{row.Description || "—"}</td>
                  <td className="p-1 text-right tabular-nums">{row.Principal || ""}</td>
                  <td className="p-1 text-right tabular-nums">{row.Interest || ""}</td>
                  <td className="p-1 text-right tabular-nums">{row["Total Due"] || ""}</td>
                  <td className="p-1 text-right tabular-nums text-emerald-700">{row.Paid || ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Contact Pattern view — Outlook-style alternative centre stage ─────────
function ContactPatternView({ caseRow }) {
  const [events, setEvents] = useState([]);
  const [readReceipts, setReadReceipts] = useState(0);
  const [repayments, setRepayments] = useState([]);
  const [repaymentMeta, setRepaymentMeta] = useState({ total_paid: 0, count: 0 });
  const [scheduleDues, setScheduleDues] = useState([]);   // [{ts, amount}] — scheduled instalments
  const [timelineEvents, setTimelineEvents] = useState([]); // unified timeline (incl. PDN, P2P, stage_change)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { getAgeingAircallSms, getAgeingRepaymentHistory, getAgeingTimeline } = await import("@/lib/api");
        const [emailsR, callsR, smsR, repR, schedR, tlR] = await Promise.allSettled([
          getAgeingM365Emails(caseRow.case_id, { limit: 200 }),
          getAgeingAircallCalls(caseRow.case_id, { limit: 100 }),
          getAgeingAircallSms(caseRow.case_id, { limit: 100 }),
          getAgeingRepaymentHistory(caseRow.case_id),
          getAgeingLoanSchedule(caseRow.case_id, { kind: "adjusted", count: 500 }),
          getAgeingTimeline(caseRow.case_id, { limit: 500, include_repayments: false }),
        ]);
        const combined = [];
        let receipts = 0;
        if (emailsR.status === "fulfilled") {
          for (const e of emailsR.value.emails || []) {
            const isReceipt =
              e.is_read_receipt ||
              (e.subject || "").trim().toLowerCase().startsWith("read:");
            if (isReceipt) {
              receipts += 1;
              continue;  // tracked separately, excluded from engagement KPI
            }
            const folder = e.folder_name || "";
            const dir = folder.startsWith("Sent") ? "outbound" : "inbound";
            const t = e.receivedDateTime ? new Date(e.receivedDateTime) : null;
            if (t) combined.push({ type: "email", direction: dir, ts: t });
          }
        }
        if (callsR.status === "fulfilled") {
          for (const c of callsR.value.calls || []) {
            const t = c.started_at ? new Date(c.started_at * 1000) : null;
            if (t) combined.push({ type: "call", direction: c.direction, ts: t, missed: c.status === "missed", duration: c.duration });
          }
        }
        if (smsR.status === "fulfilled") {
          for (const m of smsR.value.messages || []) {
            const t = m.sent_at ? new Date(m.sent_at) : null;
            if (t) combined.push({ type: "sms", direction: m.direction, ts: t });
          }
        }
        combined.sort((a, b) => a.ts - b.ts);
        setEvents(combined);
        setReadReceipts(receipts);

        if (repR.status === "fulfilled") {
          const reps = (repR.value?.repayments || [])
            .filter(r => r.date)
            .map(r => ({
              ts:           new Date(r.date),
              amount:       r.amount || 0,
              principal:    r.principal || 0,
              interest:     r.interest || 0,
              fees:         r.fees || 0,
              penalty:      r.penalty || 0,
              running_paid: r.running_paid || 0,
            }));
          setRepayments(reps);
          setRepaymentMeta({
            total_paid: repR.value?.total_paid || 0,
            count:      repR.value?.count || 0,
          });
        }

        // Scheduled instalment due dates (from LoanDisk's adjusted schedule).
        // We exclude "Payment received" rows — those are actual repayments
        // already mirrored in the repR set. What we want here is the
        // SCHEDULED side of the calendar so the user can spot mismatches.
        //
        // IMPORTANT: LoanDisk's "Total Due" column is a CUMULATIVE running
        // total (3,000 → 3,650 → 3,670 → … building up arrears), NOT the
        // per-row instalment amount. Use the "Due" column (or Principal+
        // Interest as a derivation) instead — Total Due is the last fallback
        // and only when Due is missing AND we have nothing else.
        if (schedR.status === "fulfilled") {
          const dues = [];
          const num = (v) => {
            if (v == null) return 0;
            const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
            return Number.isFinite(n) ? n : 0;
          };
          for (const row of (schedR.value?.rows || [])) {
            const desc = (row.Description || "").toLowerCase();
            if (desc.includes("payment received")) continue;  // actual payment, not a due
            // DD/MM/YYYY → JS Date
            const dStr = (row.Date || "").trim();
            const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(dStr);
            if (!m) continue;
            const [, dd, mm, yy] = m;
            const year = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
            const ts = new Date(year, parseInt(mm, 10) - 1, parseInt(dd, 10));
            const dueAmt = num(row.Due);
            const piAmt  = num(row.Principal) + num(row.Interest);
            // Prefer the per-row "Due" column. If missing, fall back to
            // Principal+Interest (still per-row). Never use Total Due —
            // it's cumulative across the entire schedule.
            const amt = dueAmt > 0 ? dueAmt : (piAmt > 0 ? piAmt : 0);
            if (amt <= 0) continue;
            dues.push({ ts, amount: amt, description: row.Description || "Instalment due" });
          }
          setScheduleDues(dues);
        }

        if (tlR.status === "fulfilled") {
          setTimelineEvents(tlR.value?.events || []);
        }
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [caseRow.case_id]);

  if (loading) {
    return (
      <Card><CardContent className="py-16 text-center text-sm text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading contact data across emails, calls, SMS and repayments…
      </CardContent></Card>
    );
  }
  if (err) return <Card><CardContent className="py-8 text-red-700">{err}</CardContent></Card>;

  return <ContactPatternCharts events={events} readReceipts={readReceipts} repayments={repayments} repaymentMeta={repaymentMeta} scheduleDues={scheduleDues} timelineEvents={timelineEvents} caseRow={caseRow} />;
}

function ContactPatternCharts({ events, readReceipts = 0, repayments, repaymentMeta, scheduleDues = [], timelineEvents = [], caseRow }) {
  // KPIs
  const totals = useMemo(() => {
    const t = {
      call:  { inbound: 0, outbound: 0, missed: 0, answered: 0 },
      email: { inbound: 0, outbound: 0 },
      sms:   { inbound: 0, outbound: 0 },
    };
    for (const e of events) {
      const k = t[e.type];
      if (!k) continue;
      if (e.direction === "inbound") k.inbound += 1;
      else if (e.direction === "outbound") k.outbound += 1;
      if (e.type === "call") {
        if (e.missed) k.missed += 1;
        else k.answered += 1;
      }
    }
    return t;
  }, [events]);

  // Longest silence (gap between any two consecutive events) + last contact
  const silence = useMemo(() => {
    if (events.length < 2) return { longestDays: 0, lastDaysAgo: events[0] ? Math.floor((Date.now() - events[0].ts) / 86400000) : null };
    let longestMs = 0;
    let longestPair = null;
    for (let i = 1; i < events.length; i++) {
      const gap = events[i].ts - events[i - 1].ts;
      if (gap > longestMs) { longestMs = gap; longestPair = [events[i - 1].ts, events[i].ts]; }
    }
    return {
      longestDays: Math.round(longestMs / 86400000),
      lastDaysAgo: Math.floor((Date.now() - events[events.length - 1].ts) / 86400000),
      gapStart: longestPair?.[0],
      gapEnd: longestPair?.[1],
    };
  }, [events]);

  // Response rate: of every outbound (any channel), did an inbound (any channel)
  // happen within 7 days afterwards?
  const responseRate = useMemo(() => {
    const outbound = events.filter(e => e.direction === "outbound");
    const inbound = events.filter(e => e.direction === "inbound");
    if (outbound.length === 0) return { rate: null, n: 0 };
    let responded = 0;
    let totalResponseMs = 0;
    let respondedTimed = 0;
    for (const o of outbound) {
      const r = inbound.find(i => i.ts > o.ts && (i.ts - o.ts) < 7 * 86400000);
      if (r) {
        responded += 1;
        totalResponseMs += (r.ts - o.ts);
        respondedTimed += 1;
      }
    }
    return {
      rate: Math.round(100 * responded / outbound.length),
      n:    outbound.length,
      avgResponseHrs: respondedTimed > 0 ? Math.round(totalResponseMs / respondedTimed / 3600000) : null,
    };
  }, [events]);

  // Hour x DayOfWeek matrix — only INBOUND events (when customer reached out)
  const heatmap = useMemo(() => {
    const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
    for (const e of events) {
      if (e.direction !== "inbound") continue;
      grid[e.ts.getDay()][e.ts.getHours()] += 1;
    }
    const max = Math.max(1, ...grid.flat());
    return { grid, max };
  }, [events]);

  // Best inbound hour
  const bestHour = useMemo(() => {
    const counts = Array(24).fill(0);
    for (const e of events) {
      if (e.direction === "inbound") counts[e.ts.getHours()] += 1;
    }
    let bi = 0; for (let i = 0; i < 24; i++) if (counts[i] > counts[bi]) bi = i;
    return { hour: bi, count: counts[bi] };
  }, [events]);

  // Best contact channel — for each channel, count outbound → inbound-reply within 48h
  // and compute the average reply time. Pick the channel with the highest reply rate.
  // Ties broken by faster average reply time. Falls back to channel with most inbound
  // activity if no outbound attempts exist yet.
  const bestChannel = useMemo(() => {
    const channels = ["call", "sms", "email"];
    const stats = channels.map(ch => {
      const outbound = events.filter(e => e.type === ch && e.direction === "outbound");
      const inbound = events.filter(e => e.type === ch && e.direction === "inbound");
      let replied = 0; let totalReplyMs = 0;
      for (const o of outbound) {
        const reply = inbound.find(i => i.ts > o.ts && (i.ts - o.ts) <= 48 * 3600000);
        if (reply) { replied += 1; totalReplyMs += (reply.ts - o.ts); }
      }
      return {
        channel: ch,
        outbound: outbound.length,
        inbound: inbound.length,
        replyRate: outbound.length ? Math.round(100 * replied / outbound.length) : null,
        avgReplyHrs: replied ? +(totalReplyMs / replied / 3600000).toFixed(1) : null,
      };
    });
    // Sort: prefer non-null replyRate (highest); then by replied count; then inbound activity.
    const usable = stats.filter(s => s.replyRate !== null && s.outbound >= 2);
    if (usable.length === 0) {
      // Fall back to channel with most inbound touches
      const fb = [...stats].sort((a, b) => b.inbound - a.inbound)[0];
      return { winner: fb.inbound > 0 ? fb : null, all: stats, hasOutboundData: false };
    }
    usable.sort((a, b) => (b.replyRate - a.replyRate) || ((a.avgReplyHrs ?? 999) - (b.avgReplyHrs ?? 999)));
    return { winner: usable[0], all: stats, hasOutboundData: true };
  }, [events]);

  const channelLabel = (ch) => ch === "call" ? "phone call" : ch === "sms" ? "SMS" : "email";
  const channelEmoji = (ch) => ch === "call" ? "📞" : ch === "sms" ? "💬" : "📧";

  return (
    <Card data-testid="customer-file-contact-pattern">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          📊 Contact pattern
          <Badge variant="outline" className="text-[10px] font-normal">{events.length} touches</Badge>
        </CardTitle>
        <p className="text-[11px] text-muted-foreground">
          When does {caseRow.customer_name?.split(" ").slice(0, 3).join(" ") || "the customer"} engage with us? Use the heatmap to plan outreach windows.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">

        {/* KPIs row */}
        <div className="grid grid-cols-4 gap-3">
          <PatternKPI label="Total touches" value={events.length} sub={`${totals.email.inbound + totals.call.inbound + totals.sms.inbound} in · ${totals.email.outbound + totals.call.outbound + totals.sms.outbound} out`} />
          <PatternKPI label="Response rate"
            value={responseRate.rate != null ? `${responseRate.rate}%` : "—"}
            sub={responseRate.rate != null ? `${responseRate.n} outbound · avg ${responseRate.avgResponseHrs ?? "—"}h to reply` : "no outbound"}
            tone={responseRate.rate != null ? (responseRate.rate >= 60 ? "good" : responseRate.rate >= 30 ? "warn" : "bad") : null}
          />
          <PatternKPI label="Last contact"
            value={silence.lastDaysAgo != null ? `${silence.lastDaysAgo}d ago` : "—"}
            sub={events.length ? events[events.length - 1].ts.toLocaleDateString("en-GB") : null}
            tone={silence.lastDaysAgo > 14 ? "bad" : silence.lastDaysAgo > 7 ? "warn" : "good"}
          />
          <PatternKPI label="Longest silence"
            value={silence.longestDays != null ? `${silence.longestDays}d` : "—"}
            sub={silence.gapStart ? `${silence.gapStart.toLocaleDateString("en-GB")} → ${silence.gapEnd.toLocaleDateString("en-GB")}` : null}
          />
        </div>

        {/* Channel breakdown — emails split: in / out / read-receipts.
            Calls split: in / out / answered / missed. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ChannelStat icon="📧" label="Emails"
                       total={totals.email.inbound + totals.email.outbound}
                       inbound={totals.email.inbound}
                       outbound={totals.email.outbound}
                       receipts={readReceipts} />
          <ChannelStat icon="📞" label="Calls"
                       total={totals.call.inbound + totals.call.outbound}
                       inbound={totals.call.inbound}
                       outbound={totals.call.outbound}
                       answered={totals.call.answered}
                       missed={totals.call.missed} />
          <ChannelStat icon="💬" label="SMS"
                       total={totals.sms.inbound + totals.sms.outbound}
                       inbound={totals.sms.inbound}
                       outbound={totals.sms.outbound} />
        </div>

        {/* Repayments — 4-month rolling calendar with green payment dots
            + red due-date dots from the adjusted schedule + analytics. */}
        {((repayments?.length > 0) || (scheduleDues?.length > 0)) && (
          <RepaymentsCalendar repayments={repayments} meta={repaymentMeta} scheduleDues={scheduleDues} />
        )}

        {/* Effort vs Payment — does ramping up contact actually drive
            payment? Swim-lane timeline of every touch (emails, calls,
            SMS, P2Ps, PDN, stage changes) overlaid with a payment-amount
            bar chart on the same X axis. Surfaces "PDN → pay" and
            "constant calls → drip-feed" patterns at a glance. */}
        {(timelineEvents?.length > 0 || repayments?.length > 0) && (
          <EffortVsPaymentChart timelineEvents={timelineEvents} repayments={repayments} />
        )}

        {/* Hour × DayOfWeek heatmap */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">When the customer reaches us</h4>
            <div className="text-[11px] text-muted-foreground">
              Best hour: <span className="font-semibold text-slate-700">{String(bestHour.hour).padStart(2, "0")}:00</span> ({bestHour.count} inbound touches)
            </div>
          </div>
          <Heatmap heatmap={heatmap} />
        </div>

        {/* Best communication method recommendation */}
        <div data-testid="customer-file-best-channel" className="rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-3">
          <div className="flex items-start gap-3">
            <div className="text-2xl">💡</div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-amber-800 font-semibold">Best communication method</div>
              {bestChannel.winner ? (
                <>
                  <div className="text-sm text-slate-900 mt-0.5">
                    <span className="text-lg mr-1">{channelEmoji(bestChannel.winner.channel)}</span>
                    <strong className="font-semibold">{channelLabel(bestChannel.winner.channel).toUpperCase()}</strong>
                    {bestChannel.hasOutboundData ? (
                      <> — <span className="font-semibold text-emerald-700">{bestChannel.winner.replyRate}%</span> reply rate
                      {bestChannel.winner.avgReplyHrs != null && <> · avg <span className="font-semibold">{bestChannel.winner.avgReplyHrs}h</span> to reply</>}
                      </>
                    ) : (
                      <> — most inbound activity ({bestChannel.winner.inbound} touches)</>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-700 mt-1">
                    Try reaching out around <strong>{String(bestHour.hour).padStart(2, "0")}:00</strong>
                    {bestChannel.hasOutboundData && <> via {channelLabel(bestChannel.winner.channel)} for the best chance of a response.</>}
                  </div>
                  {bestChannel.hasOutboundData && (
                    <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                      {bestChannel.all.map(s => (
                        <Badge
                          key={s.channel}
                          variant="outline"
                          className={`font-normal ${s.channel === bestChannel.winner.channel ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white text-slate-600"}`}
                        >
                          {channelEmoji(s.channel)} {channelLabel(s.channel)}:
                          {s.replyRate !== null ? <> <span className="font-semibold ml-1">{s.replyRate}%</span></> : <> <span className="text-muted-foreground ml-1">— no outbound</span></>}
                        </Badge>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm text-slate-700 mt-0.5">
                  Not enough data yet — once a few outbound attempts have been logged, we&apos;ll surface the channel with the best response rate.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Activity timeline scatter */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700 mb-2">Timeline · last 90 days</h4>
          <ActivityScatter events={events} />
        </div>

        {/* Efforts vs Repayments overlay — see how outreach correlates with payments */}
        {(repayments?.length > 0 || events.length > 0) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-700">Efforts vs Repayments</h4>
              {repaymentMeta?.count > 0 && (
                <div className="text-[11px] text-muted-foreground">
                  {repaymentMeta.count} repayments · total paid <span className="font-semibold text-slate-700">{cur()}{(repaymentMeta.total_paid || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              )}
            </div>
            <EffortsVsRepaymentsChart events={events} repayments={repayments || []} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PatternKPI({ label, value, sub, tone }) {
  const toneClass = tone === "good" ? "border-emerald-300 bg-emerald-50/50"
    : tone === "warn" ? "border-amber-300 bg-amber-50/50"
    : tone === "bad" ? "border-red-300 bg-red-50/50"
    : "border-slate-200 bg-slate-50/40";
  return (
    <div className={`rounded border-2 ${toneClass} px-3 py-2`}>
      <div className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</div>
      <div className="text-xl font-bold tabular-nums leading-tight text-slate-900">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function ChannelStat({ icon, label, total, inbound, outbound, missed, answered, receipts }) {
  const inPct = total > 0 ? Math.round(100 * inbound / total) : 0;
  return (
    <div className="rounded border bg-white px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-lg">{icon}</span>
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <span className="ml-auto text-sm font-bold tabular-nums">{total}</span>
      </div>
      <div className="flex items-center h-1.5 bg-slate-100 rounded overflow-hidden">
        <div className="bg-emerald-500 h-full" style={{ width: `${inPct}%` }} title={`Inbound ${inbound}`} />
        <div className="bg-indigo-500 h-full" style={{ width: `${100 - inPct}%` }} title={`Outbound ${outbound}`} />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
        <span>← {inbound} in</span>
        <span>{outbound} out →</span>
      </div>
      {/* Channel-specific extras */}
      {(answered != null || missed != null) && (
        <div className="flex justify-between text-[10px] mt-1 pt-1 border-t border-slate-100">
          {answered != null && <span className="text-emerald-700">✓ {answered} answered</span>}
          {missed != null && missed > 0 && <span className="text-rose-700">✗ {missed} missed</span>}
        </div>
      )}
      {receipts != null && receipts > 0 && (
        <div className="text-[10px] mt-1 pt-1 border-t border-slate-100 text-slate-500 flex items-center gap-1">
          <Eye className="w-2.5 h-2.5" />
          <span><span className="font-semibold text-slate-700 tabular-nums">{receipts}</span> read receipt{receipts !== 1 ? "s" : ""}</span>
          <span className="text-[9px] italic ml-auto">(excluded from KPIs)</span>
        </div>
      )}
    </div>
  );
}

// ─── Repayments — rolling 4-month calendar + analytics ─────────────────────
// Shows the last 4 months ending at the current month with each payment day
// highlighted in green (intensity by amount), and each scheduled instalment
// due date highlighted in red. Days with BOTH render green with a red ring.
// Hover shows the exact due / paid amounts and any shortfall (in amber).
function RepaymentsCalendar({ repayments, meta, scheduleDues = [] }) {
  // Sort oldest-first for gap calculations
  const sorted = useMemo(() => {
    return [...(repayments || [])].sort((a, b) => a.ts - b.ts);
  }, [repayments]);

  // Build the 4-month window ending at current month, oldest → newest
  const months = useMemo(() => {
    const today = new Date();
    const out = [];
    for (let i = 3; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      out.push({ year: d.getFullYear(), month: d.getMonth() });
    }
    return out;
  }, []);

  // Helper to make "YYYY-MM-DD" from a Date
  const _key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Map "YYYY-MM-DD" → { count, amount } for the window — payments received
  const dayMap = useMemo(() => {
    const m = {};
    const winStart = new Date(months[0].year, months[0].month, 1);
    const winEnd = new Date(months[3].year, months[3].month + 1, 0, 23, 59, 59);
    for (const r of sorted) {
      if (r.ts < winStart || r.ts > winEnd) continue;
      const key = _key(r.ts);
      const cur = m[key] || { count: 0, amount: 0 };
      cur.count  += 1;
      cur.amount += r.amount || 0;
      m[key] = cur;
    }
    return m;
  }, [sorted, months]);

  // Map "YYYY-MM-DD" → { count, amount, description } — scheduled dues
  const dueMap = useMemo(() => {
    const m = {};
    const winStart = new Date(months[0].year, months[0].month, 1);
    const winEnd = new Date(months[3].year, months[3].month + 1, 0, 23, 59, 59);
    for (const d of (scheduleDues || [])) {
      if (!d.ts || d.ts < winStart || d.ts > winEnd) continue;
      const key = _key(d.ts);
      const cur = m[key] || { count: 0, amount: 0, description: null };
      cur.count  += 1;
      cur.amount += d.amount || 0;
      if (!cur.description) cur.description = d.description;
      m[key] = cur;
    }
    return m;
  }, [scheduleDues, months]);

  const maxAmt = useMemo(() => {
    let mx = 0;
    for (const v of Object.values(dayMap)) if (v.amount > mx) mx = v.amount;
    return mx || 1;
  }, [dayMap]);

  // Analytics
  const analytics = useMemo(() => {
    if (sorted.length === 0) return null;
    const totalCount = meta?.count || sorted.length;
    const total = meta?.total_paid || sorted.reduce((s, r) => s + (r.amount || 0), 0);
    const avgAmount = total / Math.max(1, totalCount);

    // Gaps between consecutive payments
    let totalGap = 0;
    let longestGap = 0;
    let longestPair = null;
    for (let i = 1; i < sorted.length; i++) {
      const gap = Math.round((sorted[i].ts - sorted[i - 1].ts) / 86400000);
      totalGap += gap;
      if (gap > longestGap) {
        longestGap = gap;
        longestPair = [sorted[i - 1].ts, sorted[i].ts];
      }
    }
    const avgGap = sorted.length > 1 ? Math.round(totalGap / (sorted.length - 1)) : null;

    // % of monthly buckets in the last 6 months that had ≥ 1 payment
    const today = new Date();
    let monthsWithPayment = 0;
    for (let i = 0; i < 6; i++) {
      const mDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const next = new Date(today.getFullYear(), today.getMonth() - i + 1, 1);
      if (sorted.some(r => r.ts >= mDate && r.ts < next)) monthsWithPayment += 1;
    }
    const monthsHitPct = Math.round((monthsWithPayment / 6) * 100);

    // Last payment date and days ago
    const last = sorted[sorted.length - 1];
    const daysSinceLast = Math.floor((Date.now() - last.ts.getTime()) / 86400000);

    return {
      total, totalCount, avgAmount, avgGap, longestGap, longestPair,
      monthsHitPct, last, daysSinceLast,
    };
  }, [sorted, meta]);

  return (
    <div
      className="rounded-lg border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/40 to-white p-3"
      data-testid="customer-file-repayments-calendar"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Banknote className="w-4 h-4 text-emerald-700" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-emerald-900">Repayments — last 4 months</h4>
          <Badge variant="outline" className="text-[10px] font-normal border-emerald-300 bg-white text-emerald-700">
            {meta?.count || sorted.length} payments
          </Badge>
          {scheduleDues?.length > 0 && (
            <Badge variant="outline" className="text-[10px] font-normal border-rose-300 bg-white text-rose-700" data-testid="customer-file-repayments-dues-badge">
              {Object.keys(dueMap).length} due dates
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          {/* Legend */}
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-400" /> paid</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-rose-200 border border-rose-400" /> due</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-400 ring-1 ring-rose-500 ring-offset-0" /> paid + due</span>
          <span className="text-slate-300">·</span>
          <span>Total <span className="font-semibold text-emerald-700">{cur()}{(meta?.total_paid || analytics?.total || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></span>
        </div>
      </div>

      {/* 4 mini calendars side by side */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {months.map(({ year, month }) => (
          <MiniMonthCalendar
            key={`${year}-${month}`}
            year={year}
            month={month}
            dayMap={dayMap}
            dueMap={dueMap}
            maxAmt={maxAmt}
          />
        ))}
      </div>

      {/* Analytics strip */}
      {analytics && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-[11px]" data-testid="customer-file-repayments-analytics">
          <AnalyticStat label="Total received"    value={`${cur()}${(analytics.total).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} tone="emerald" />
          <AnalyticStat label="Avg payment"       value={`${cur()}${Math.round(analytics.avgAmount).toLocaleString()}`} />
          <AnalyticStat label="Avg gap"           value={analytics.avgGap != null ? `${analytics.avgGap}d` : "—"} />
          <AnalyticStat label="Longest gap"       value={`${analytics.longestGap}d`} tone={analytics.longestGap > 60 ? "rose" : analytics.longestGap > 30 ? "amber" : null} />
          <AnalyticStat label="Months hit (6m)"   value={`${analytics.monthsHitPct}%`} tone={analytics.monthsHitPct >= 80 ? "emerald" : analytics.monthsHitPct >= 50 ? "amber" : "rose"} />
          <AnalyticStat label="Last payment"      value={`${analytics.daysSinceLast}d ago`} sub={analytics.last.ts.toLocaleDateString("en-GB")} tone={analytics.daysSinceLast > 60 ? "rose" : analytics.daysSinceLast > 30 ? "amber" : null} />
        </div>
      )}
    </div>
  );
}

function AnalyticStat({ label, value, sub, tone }) {
  const toneClass = tone === "emerald" ? "text-emerald-700"
                  : tone === "amber"   ? "text-amber-700"
                  : tone === "rose"    ? "text-rose-700"
                  : "text-slate-800";
  return (
    <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${toneClass}`}>{value}</div>
      {sub && <div className="text-[9px] text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}

function MiniMonthCalendar({ year, month, dayMap, dueMap = {}, maxAmt }) {
  const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // Build Mon-first grid
  const first = new Date(year, month, 1);
  const firstDow = (first.getDay() + 6) % 7; // 0 = Mon
  const daysIn = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;

  // Sum amount for this month — count paid + due separately for the header summary
  let monthPaid = 0;
  let monthPaidCount = 0;
  let monthDueCount = 0;
  for (let d = 1; d <= daysIn; d++) {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const paid = dayMap[key];
    if (paid) {
      monthPaid += paid.amount;
      monthPaidCount += paid.count;
    }
    if (dueMap[key]) monthDueCount += dueMap[key].count;
  }

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysIn; d++) cells.push(d);

  // Format amount as £X,000 (no decimals)
  const fmtGBP0 = (n) => `${cur()}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="rounded border border-slate-200 bg-white p-2" data-testid={`mini-calendar-${year}-${month + 1}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] font-semibold text-slate-800">
          {MONTH_NAMES[month]} {String(year).slice(2)}
          {isCurrentMonth && <span className="ml-1 text-[9px] uppercase text-amber-700">this</span>}
        </div>
        <div className="text-[9px] text-muted-foreground tabular-nums" title={`${monthPaidCount} payment${monthPaidCount !== 1 ? "s" : ""} · ${monthDueCount} due date${monthDueCount !== 1 ? "s" : ""} · paid ${fmtGBP0(monthPaid)}`}>
          {monthPaidCount > 0
            ? `${monthPaidCount}p · ${fmtGBP0(monthPaid)}`
            : monthDueCount > 0
              ? `${monthDueCount} due`
              : "—"}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-[2px] text-[9px] text-center text-muted-foreground mb-0.5">
        {["M","T","W","T","F","S","S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-[2px]">
        {cells.map((d, idx) => {
          if (d == null) return <div key={`e${idx}`} />;
          const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const paid = dayMap[key];
          const due  = dueMap[key];
          const isToday = isCurrentMonth && today.getDate() === d;

          // Tooltip composition
          let title = `${d} ${MONTH_NAMES[month]} ${year}`;
          if (due && paid) {
            const short = due.amount - paid.amount;
            if (short > 0.5) {
              title += ` · Due ${fmtGBP0(due.amount)} · Paid ${fmtGBP0(paid.amount)} (short ${fmtGBP0(short)})`;
            } else if (short < -0.5) {
              title += ` · Due ${fmtGBP0(due.amount)} · Paid ${fmtGBP0(paid.amount)} (over ${fmtGBP0(-short)})`;
            } else {
              title += ` · Due ${fmtGBP0(due.amount)} · Paid ${fmtGBP0(paid.amount)}`;
            }
          } else if (due) {
            title += ` · Due ${fmtGBP0(due.amount)}`;
          } else if (paid) {
            title += ` · Paid ${fmtGBP0(paid.amount)}`;
            if (paid.count > 1) title += ` (${paid.count} payments)`;
          }

          // Visual: paid wins for background, due adds a red ring
          let bg = "bg-slate-50";
          let textCol = "text-slate-500";
          let ring = "";
          let testid = "mini-calendar-day-empty";

          if (paid) {
            const intensity = Math.min(1, paid.amount / maxAmt);
            bg = intensity > 0.66 ? "bg-emerald-600"
               : intensity > 0.33 ? "bg-emerald-400"
               : "bg-emerald-200";
            textCol = intensity > 0.5 ? "text-white" : "text-emerald-900";
            testid = "mini-calendar-day-paid";
            if (due) {
              // Partial-payment escalation: amber ring when short, rose when full short
              const short = due.amount - paid.amount;
              ring = short > 0.5
                ? "ring-2 ring-amber-500"          // partial — short
                : "ring-2 ring-rose-500";          // due met (or over) — show due-ring anyway so user sees it was a scheduled day
              testid = short > 0.5 ? "mini-calendar-day-partial" : "mini-calendar-day-paid-and-due";
            }
          } else if (due) {
            bg = "bg-rose-200 border border-rose-400";
            textCol = "text-rose-900";
            testid = "mini-calendar-day-due";
          }

          const todayRing = isToday ? "outline outline-1 outline-amber-500 outline-offset-1" : "";

          return (
            <div
              key={key}
              className={`aspect-square rounded-sm flex items-center justify-center text-[10px] font-medium tabular-nums ${bg} ${textCol} ${ring} ${todayRing}`}
              title={title}
              data-testid={testid}
            >
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Effort vs Payment — does ramping up contact actually drive pay? ───────
// Swim-lane timeline of every touch (emails, calls, SMS, P2Ps, PDN, stage
// changes) overlaid with a payment-amount bar chart on the same X axis.
// Surfaces patterns like "PDN → pay" vs "constant calls → drip-feed".
const EVP_CATEGORIES = [
  // category id, label, [{type, dotColor, ring?}]
  { id: "email", label: "Emails", types: {
      email_in:  { color: "#16a34a", label: "Email in" },
      email_out: { color: "#2563eb", label: "Email out" },
  }},
  { id: "call",  label: "Calls",  types: {
      call_in:       { color: "#16a34a", label: "Call in" },
      call_out:      { color: "#2563eb", label: "Call out" },
      missed_call:   { color: "#f97316", label: "Missed call" },
      voicemail:     { color: "#a855f7", label: "Voicemail" },
  }},
  { id: "sms",   label: "SMS",    types: {
      sms_in:  { color: "#16a34a", label: "SMS in" },
      sms_out: { color: "#2563eb", label: "SMS out" },
  }},
  { id: "p2p",   label: "P2Ps",   types: {
      p2p_created:      { color: "#d97706", label: "P2P created" },
      p2p_kept:         { color: "#059669", label: "P2P kept" },
      p2p_broken:       { color: "#e11d48", label: "P2P broken" },
      p2p_cancelled:    { color: "#64748b", label: "P2P cancelled" },
      p2p_rescheduled:  { color: "#d97706", label: "P2P rescheduled" },
  }},
  { id: "risk",  label: "Risk",   types: {
      pdn_issued:       { color: "#dc2626", label: "PDN issued", ring: true },
      stage_change:     { color: "#7c3aed", label: "Stage change" },
      handler_change:   { color: "#64748b", label: "Handler change" },
      note:             { color: "#0891b2", label: "Note" },
  }},
];

function evpCategoryFor(eventType) {
  for (const c of EVP_CATEGORIES) if (c.types[eventType]) return c;
  return null;
}

function EffortVsPaymentChart({ timelineEvents, repayments }) {
  const [windowDays, setWindowDays] = useState(60);
  const [hover, setHover] = useState(null);     // {x, y, label, sub, color}

  // Time window — ends at end-of-today so today's events show
  const { winStart, winEnd, days } = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - (windowDays - 1) * 86400000);
    start.setHours(0, 0, 0, 0);
    return {
      winStart: start,
      winEnd:   end,
      days:     windowDays,
    };
  }, [windowDays]);

  // Slot events into categories, with x positions in [0..1]
  const lanes = useMemo(() => {
    const out = EVP_CATEGORIES.map(c => ({ ...c, points: [] }));
    const span = winEnd.getTime() - winStart.getTime();
    for (const ev of (timelineEvents || [])) {
      if (!ev?.at) continue;
      const t = new Date(ev.at);
      if (t < winStart || t > winEnd) continue;
      // Skip pure note events authored by 'system' for P2P status — they
      // duplicate the dedicated P2P events. Keep human-authored notes.
      if (ev.type === "note" && ev.actor === "system") continue;
      const cat = evpCategoryFor(ev.type);
      if (!cat) continue;
      const typeMeta = cat.types[ev.type];
      const x = (t.getTime() - winStart.getTime()) / span;
      out.find(l => l.id === cat.id).points.push({
        x,
        ts: t,
        type: ev.type,
        color: typeMeta.color,
        ring: !!typeMeta.ring,
        title: typeMeta.label,
        summary: ev.title || ev.summary || "",
      });
    }
    return out;
  }, [timelineEvents, winStart, winEnd]);

  // Daily payments inside the window
  const paymentBars = useMemo(() => {
    const buckets = {};
    for (const r of (repayments || [])) {
      if (!r.ts) continue;
      const d = r.ts instanceof Date ? r.ts : new Date(r.ts);
      if (d < winStart || d > winEnd) continue;
      const dayIdx = Math.floor((d.getTime() - winStart.getTime()) / 86400000);
      buckets[dayIdx] = (buckets[dayIdx] || 0) + (r.amount || 0);
    }
    const bars = [];
    let max = 0;
    for (const [idx, amt] of Object.entries(buckets)) {
      const day = parseInt(idx, 10);
      if (amt > max) max = amt;
      const dayDate = new Date(winStart.getTime() + day * 86400000);
      bars.push({ day, amount: amt, ts: dayDate });
    }
    return { bars, max: max || 1, total: bars.reduce((s, b) => s + b.amount, 0) };
  }, [repayments, winStart, winEnd]);

  // ─── SVG geometry ────────────────────────────────────────────────────────
  // Use viewBox + responsive container; width = 1000, lanes ~ 18px each
  const VB_W = 1000;
  const LEFT_PAD = 70;
  const RIGHT_PAD = 12;
  const PLOT_W = VB_W - LEFT_PAD - RIGHT_PAD;
  const LANE_H = 22;
  const LANE_PAD_TOP = 14;
  const LANES_H = LANE_H * EVP_CATEGORIES.length;
  const AXIS_H = 18;
  const PAY_H = 80;
  const TOTAL_H = LANE_PAD_TOP + LANES_H + AXIS_H + PAY_H + 8;

  // X scale → SVG x
  const xOf = (xNorm) => LEFT_PAD + xNorm * PLOT_W;
  // Today position
  const todayX = xOf(1);

  // Generate ~6 date ticks evenly spaced across the window
  const ticks = useMemo(() => {
    const N = days <= 30 ? 6 : days <= 60 ? 6 : days <= 90 ? 6 : 7;
    const out = [];
    for (let i = 0; i < N; i++) {
      const norm = i / (N - 1);
      const dt = new Date(winStart.getTime() + norm * (winEnd.getTime() - winStart.getTime()));
      out.push({ x: xOf(norm), label: dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) });
    }
    return out;
  }, [winStart, winEnd, days]);

  // Headline KPI strip — counts in window per category + total payment £
  const kpis = useMemo(() => {
    return {
      total_touches: lanes.reduce((s, l) => s + l.points.length, 0),
      by_cat: Object.fromEntries(lanes.map(l => [l.id, l.points.length])),
      total_paid: paymentBars.total,
      payment_days: paymentBars.bars.length,
    };
  }, [lanes, paymentBars]);

  const fmtGBP0 = (n) => `${cur()}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div
      className="rounded-lg border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/30 to-white p-3"
      data-testid="customer-file-effort-vs-payment"
    >
      <div className="flex items-center justify-between mb-2.5 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-indigo-700" />
          <h4 className="text-xs font-semibold uppercase tracking-wide text-indigo-900">Effort vs payment</h4>
          <span className="text-[10px] text-muted-foreground italic hidden sm:inline">
            does ramping up contact actually trigger payment?
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-[10px] text-muted-foreground">
            {kpis.total_touches} touches · {fmtGBP0(kpis.total_paid)} paid in {kpis.payment_days} day{kpis.payment_days === 1 ? "" : "s"}
          </div>
          <div className="inline-flex rounded bg-white border border-slate-200 p-0.5" data-testid="evp-window-toggle">
            {[30, 60, 90, 180].map(n => (
              <button
                key={n}
                onClick={() => setWindowDays(n)}
                className={`px-2 py-0.5 text-[10px] rounded ${windowDays === n ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
                data-testid={`evp-window-${n}`}
              >
                {n}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${VB_W} ${TOTAL_H}`}
          className="w-full"
          style={{ height: "auto" }}
          preserveAspectRatio="none"
          data-testid="evp-svg"
        >
          {/* Lane background stripes */}
          {EVP_CATEGORIES.map((c, i) => (
            <rect
              key={c.id}
              x={LEFT_PAD - 4}
              y={LANE_PAD_TOP + i * LANE_H}
              width={PLOT_W + 4}
              height={LANE_H}
              fill={i % 2 === 0 ? "#f8fafc" : "#ffffff"}
              stroke="#e2e8f0"
              strokeWidth="0.5"
            />
          ))}
          {/* Lane labels */}
          {EVP_CATEGORIES.map((c, i) => (
            <g key={`label-${c.id}`}>
              <text
                x={LEFT_PAD - 8}
                y={LANE_PAD_TOP + i * LANE_H + LANE_H / 2 + 3}
                textAnchor="end"
                fontSize="10"
                fontWeight="600"
                fill="#475569"
                data-testid={`evp-lane-label-${c.id}`}
              >
                {c.label}
              </text>
              <text
                x={LEFT_PAD - 8}
                y={LANE_PAD_TOP + i * LANE_H + LANE_H / 2 + 12}
                textAnchor="end"
                fontSize="8"
                fill="#94a3b8"
              >
                {kpis.by_cat[c.id] || 0}
              </text>
            </g>
          ))}
          {/* Today vertical guideline */}
          <line x1={todayX} x2={todayX} y1={LANE_PAD_TOP} y2={TOTAL_H - AXIS_H + 8} stroke="#f59e0b" strokeWidth="1" strokeDasharray="3,2" />
          <text x={todayX - 3} y={LANE_PAD_TOP - 4} textAnchor="end" fontSize="9" fill="#b45309" fontWeight="600">today</text>

          {/* Event dots */}
          {lanes.map((lane, i) => (
            <g key={lane.id} data-testid={`evp-lane-${lane.id}`}>
              {lane.points.map((p, idx) => {
                const cx = xOf(p.x);
                const cy = LANE_PAD_TOP + i * LANE_H + LANE_H / 2;
                const r = p.ring ? 5.5 : 3.5;
                const tooltipText = `${p.ts.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })} · ${p.title}${p.summary ? " — " + p.summary : ""}`;
                return (
                  <g key={`${lane.id}-${idx}`} className="cursor-pointer">
                    {p.ring && (
                      <circle cx={cx} cy={cy} r={r + 2} fill="none" stroke={p.color} strokeWidth="1.2" opacity="0.45" />
                    )}
                    <circle
                      cx={cx}
                      cy={cy}
                      r={r}
                      fill={p.color}
                      stroke="#fff"
                      strokeWidth="0.75"
                      onMouseEnter={() => setHover({ x: cx, y: cy, label: p.title, sub: `${p.ts.toLocaleDateString("en-GB")} ${p.summary ? "· " + p.summary : ""}`, color: p.color })}
                      onMouseLeave={() => setHover(null)}
                      data-testid={`evp-event-${p.type}`}
                    >
                      <title>{tooltipText}</title>
                    </circle>
                  </g>
                );
              })}
            </g>
          ))}

          {/* Axis ticks between the swim-lanes and the payment chart */}
          <line x1={LEFT_PAD} x2={LEFT_PAD + PLOT_W} y1={LANE_PAD_TOP + LANES_H + 2} y2={LANE_PAD_TOP + LANES_H + 2} stroke="#cbd5e1" strokeWidth="0.75" />
          {ticks.map((t, i) => (
            <g key={`tick-${i}`}>
              <line x1={t.x} x2={t.x} y1={LANE_PAD_TOP + LANES_H + 2} y2={LANE_PAD_TOP + LANES_H + 6} stroke="#94a3b8" strokeWidth="0.75" />
              <text x={t.x} y={LANE_PAD_TOP + LANES_H + AXIS_H - 2} textAnchor="middle" fontSize="9" fill="#64748b">{t.label}</text>
            </g>
          ))}

          {/* Payment bars — bottom chart */}
          <g>
            <rect
              x={LEFT_PAD - 4}
              y={LANE_PAD_TOP + LANES_H + AXIS_H}
              width={PLOT_W + 4}
              height={PAY_H}
              fill="#ecfdf5"
              stroke="#a7f3d0"
              strokeWidth="0.5"
            />
            <text x={LEFT_PAD - 8} y={LANE_PAD_TOP + LANES_H + AXIS_H + 12} textAnchor="end" fontSize="10" fontWeight="600" fill="#047857">
              Paid
            </text>
            <text x={LEFT_PAD - 8} y={LANE_PAD_TOP + LANES_H + AXIS_H + 24} textAnchor="end" fontSize="8" fill="#64748b">
              max {fmtGBP0(paymentBars.max)}
            </text>
            {paymentBars.bars.map((b) => {
              const xNorm = (b.day + 0.5) / days;
              const cx = xOf(xNorm);
              const h = (b.amount / paymentBars.max) * (PAY_H - 14);
              const y = LANE_PAD_TOP + LANES_H + AXIS_H + PAY_H - h - 4;
              return (
                <g key={`bar-${b.day}`} className="cursor-pointer" data-testid="evp-payment-bar">
                  <rect
                    x={cx - 2.5}
                    y={y}
                    width={5}
                    height={Math.max(2, h)}
                    fill="#10b981"
                    stroke="#047857"
                    strokeWidth="0.5"
                    rx="1"
                    onMouseEnter={() => setHover({ x: cx, y, label: "Payment received", sub: `${b.ts.toLocaleDateString("en-GB")} · ${fmtGBP0(b.amount)}`, color: "#10b981" })}
                    onMouseLeave={() => setHover(null)}
                  >
                    <title>{`${b.ts.toLocaleDateString("en-GB")} · ${fmtGBP0(b.amount)}`}</title>
                  </rect>
                </g>
              );
            })}
            {paymentBars.bars.length === 0 && (
              <text x={LEFT_PAD + PLOT_W / 2} y={LANE_PAD_TOP + LANES_H + AXIS_H + PAY_H / 2 + 4} textAnchor="middle" fontSize="10" fill="#94a3b8" fontStyle="italic">
                No payments in this window
              </text>
            )}
          </g>
        </svg>

        {/* HTML tooltip overlay — pinned to last hovered position */}
        {hover && (
          <div
            className="pointer-events-none absolute rounded bg-slate-900 text-white text-[10px] px-2 py-1 shadow-lg max-w-xs"
            style={{
              left: `${(hover.x / VB_W) * 100}%`,
              top: 0,
              transform: "translate(-50%, -110%)",
            }}
          >
            <div className="font-semibold" style={{ color: hover.color }}>{hover.label}</div>
            {hover.sub && <div className="text-slate-200">{hover.sub}</div>}
          </div>
        )}
      </div>

      {/* Legend strip — one chip per event subtype, grouped by category */}
      <div className="mt-2 flex items-start gap-3 flex-wrap text-[9.5px] text-slate-600">
        {EVP_CATEGORIES.map(c => (
          <div key={c.id} className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[9px] uppercase tracking-wide text-slate-400 font-semibold">{c.label}:</span>
            {Object.values(c.types).map(t => (
              <span key={t.label} className="inline-flex items-center gap-0.5">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: t.color, boxShadow: t.ring ? `0 0 0 1.5px ${t.color}55` : undefined }} />
                {t.label}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


function Heatmap({ heatmap }) {
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return (
    <div className="overflow-x-auto">
      <table className="text-[10px] text-muted-foreground border-collapse">
        <thead>
          <tr>
            <th className="px-1 text-right pr-2 w-10"></th>
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="text-center font-normal w-5">{h % 6 === 0 ? String(h).padStart(2, "0") : ""}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {heatmap.grid.map((row, d) => (
            <tr key={d}>
              <td className="text-right pr-2 font-semibold text-slate-700">{DAYS[d]}</td>
              {row.map((v, h) => {
                const alpha = v / heatmap.max;
                const bg = v === 0
                  ? "bg-slate-100"
                  : alpha < 0.34 ? "bg-emerald-200"
                  : alpha < 0.67 ? "bg-emerald-400"
                  : "bg-emerald-600";
                return (
                  <td key={h} className={`w-5 h-5 ${bg} border border-white`} title={`${DAYS[d]} ${h}:00 — ${v} inbound touches`}>
                    {v > 0 && <span className={`text-[9px] flex items-center justify-center h-full ${alpha > 0.5 ? "text-white" : "text-slate-700"}`}>{v}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivityScatter({ events }) {
  // Show last 90 days
  const cutoff = Date.now() - 90 * 86400000;
  const recent = events.filter(e => e.ts.getTime() > cutoff);
  if (recent.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-6 text-center">No activity in the last 90 days.</div>;
  }

  // Recharts ScatterChart with X = days-ago, Y = hour-of-day
  // We'll build it inline with imports
  const data = recent.map(e => ({
    daysAgo: -Math.floor((Date.now() - e.ts.getTime()) / 86400000),
    hour: e.ts.getHours() + e.ts.getMinutes() / 60,
    type: e.type,
    direction: e.direction,
    label: `${e.type} ${e.direction} · ${e.ts.toLocaleString()}`,
  }));

  // 6 series: 3 types × 2 directions — colour-mapped: emerald=inbound, blue=outbound, red=missed call
  const series = [
    { id: "email in",  fill: "#10b981", shape: "circle",   filter: e => e.type === "email" && e.direction === "inbound" },
    { id: "call in",   fill: "#059669", shape: "triangle", filter: e => e.type === "call"  && e.direction === "inbound" && !e.missed },
    { id: "sms in",    fill: "#14b8a6", shape: "diamond",  filter: e => e.type === "sms"   && e.direction === "inbound" },
    { id: "email out", fill: "#3b82f6", shape: "circle",   filter: e => e.type === "email" && e.direction === "outbound" },
    { id: "call out",  fill: "#6366f1", shape: "triangle", filter: e => e.type === "call"  && e.direction === "outbound" && !e.missed },
    { id: "sms out",   fill: "#8b5cf6", shape: "diamond",  filter: e => e.type === "sms"   && e.direction === "outbound" },
    { id: "missed",    fill: "#ef4444", shape: "triangle", filter: e => e.type === "call"  && e.missed },
  ];

  return (
    <>
      <ScatterPlot data={data} series={series} />
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mt-2 text-[10px]">
        {series.map(s => (
          <div key={s.id} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.fill }} />
            <span className="text-muted-foreground">{s.id.replace("-", " ")}</span>
          </div>
        ))}
      </div>
    </>
  );
}

function ScatterPlot({ data, series }) {
  // Lazy-load recharts to keep the bundle slim if Contact Pattern isn't opened
  const [Recharts, setRecharts] = useState(null);
  useEffect(() => {
    let alive = true;
    import("recharts").then(m => { if (alive) setRecharts(m); });
    return () => { alive = false; };
  }, []);
  if (!Recharts) return <Loader2 className="w-4 h-4 animate-spin" />;
  const { ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip } = Recharts;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <ScatterChart margin={{ top: 10, right: 12, bottom: 6, left: 6 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis type="number" dataKey="daysAgo" name="days ago" domain={[-90, 0]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}d`} />
        <YAxis type="number" dataKey="hour" name="hour" domain={[0, 24]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${String(Math.floor(v)).padStart(2, "0")}:00`} />
        <Tooltip cursor={{ strokeDasharray: "3 3" }} content={({ payload }) => {
          if (!payload || !payload[0]) return null;
          return <div className="bg-white border rounded shadow-lg p-2 text-[10px]">{payload[0].payload.label}</div>;
        }} />
        {series.map(s => (
          <Scatter key={s.id} name={s.id} data={data.filter(s.filter)} fill={s.fill} shape={s.shape} />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
}


// ─── Efforts vs Repayments — overlay payments and outreach on one timeline ──
// Daily aggregation:
//   • Bar (emerald)   = repayment £ paid that day
//   • Line (indigo)   = cumulative paid (right axis)
//   • Dot (rose)      = outbound effort count that day (× 50 so it scales visually)
//   • Dot (sky-300)   = inbound response count that day
// This lets ops see how their outreach correlates with actual payments — e.g.
// "we made 3 calls in week 2 → £500 payment cleared the following Monday".
function EffortsVsRepaymentsChart({ events, repayments }) {
  const [Recharts, setRecharts] = useState(null);
  useEffect(() => {
    let alive = true;
    import("recharts").then(m => { if (alive) setRecharts(m); });
    return () => { alive = false; };
  }, []);

  // Build a day-keyed dataset spanning the earliest event to today
  const data = useMemo(() => {
    const buckets = {};
    const dayKey = (d) => {
      const x = new Date(d); x.setHours(0, 0, 0, 0);
      return x.getTime();
    };
    for (const e of events) {
      const k = dayKey(e.ts);
      if (!buckets[k]) buckets[k] = { ts: k, outbound: 0, inbound: 0, repayment: 0, cumulative: 0 };
      if (e.direction === "outbound") buckets[k].outbound += 1;
      else if (e.direction === "inbound") buckets[k].inbound += 1;
    }
    let running = 0;
    // Repayments are pre-sorted oldest-first by the backend
    for (const r of repayments) {
      const k = dayKey(r.ts);
      if (!buckets[k]) buckets[k] = { ts: k, outbound: 0, inbound: 0, repayment: 0, cumulative: 0 };
      buckets[k].repayment += r.amount;
    }
    // Now compute cumulative across ALL day buckets in order
    const sorted = Object.values(buckets).sort((a, b) => a.ts - b.ts);
    for (const b of sorted) {
      running += b.repayment;
      b.cumulative = Math.round(running);
    }
    return sorted;
  }, [events, repayments]);

  if (!Recharts) return <Loader2 className="w-4 h-4 animate-spin" />;
  if (data.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-6 text-center">No repayments or contact events to plot yet.</div>;
  }

  const { ResponsiveContainer, ComposedChart, Bar, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend } = Recharts;

  const fmtDate = (ts) => new Date(ts).toLocaleDateString("en-GB", { month: "short", day: "numeric", year: "2-digit" });
  const fmtMoney = (v) => v ? `${cur()}${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—";

  return (
    <>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 10, right: 12, bottom: 6, left: 6 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="ts"
            type="number"
            domain={["dataMin", "dataMax"]}
            scale="time"
            tickFormatter={fmtDate}
            tick={{ fontSize: 10 }}
          />
          <YAxis yAxisId="left" tick={{ fontSize: 10 }} tickFormatter={fmtMoney} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} tickFormatter={fmtMoney} />
          <Tooltip
            labelFormatter={(v) => new Date(v).toLocaleDateString("en-GB")}
            formatter={(value, name) => {
              if (name === "Repayment" || name === "Cumulative paid") return [fmtMoney(value), name];
              return [value, name];
            }}
            contentStyle={{ fontSize: 11 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
          <Bar yAxisId="left"  dataKey="repayment"  name="Repayment"        fill="#10b981" radius={[3, 3, 0, 0]} />
          <Line yAxisId="right" type="monotone" dataKey="cumulative" name="Cumulative paid" stroke="#6366f1" strokeWidth={2} dot={false} />
          <Scatter yAxisId="left" data={data.filter(d => d.outbound > 0)} dataKey="outbound" name="Outbound efforts" fill="#f43f5e" shape="circle" />
          <Scatter yAxisId="left" data={data.filter(d => d.inbound > 0)}  dataKey="inbound"  name="Inbound replies"  fill="#0ea5e9" shape="diamond" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="text-[10px] text-muted-foreground italic mt-1">
        Bars = repayment amount · indigo line = cumulative paid · rose dots = outbound efforts that day · sky dots = inbound replies.
      </div>
    </>
  );
}




// ─── Outlook split-pane — list (left) + reading pane (right) ───────────────
function OutlookSplitPane({ caseRow, isLegalMode = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [folderFilter, setFolderFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);   // {messageId, kind, originalSubject, originalFrom}
  // Legal stream can also work the legal@ mailbox — collections stays on accounts@.
  const [mailbox, setMailbox] = useState("accounts");

  const fetchEmails = useCallback(async () => {
    try {
      setLoading(true);
      const r = await getAgeingM365Emails(caseRow.case_id, { limit: 200, mailbox });
      setData(r);
      setSelectedId(prev => prev && (r.emails || []).some(e => e.id === prev)
        ? prev
        : (r.emails && r.emails.length > 0 ? r.emails[0].id : null));
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRow.case_id, mailbox]);
  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const switchMailbox = (mb) => {
    if (mb === mailbox) return;
    setMailbox(mb);
    setSelectedId(null);
    setReplyTo(null);
    setComposeOpen(false);
    setErr(null);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await syncAgeingM365History(caseRow.case_id, { limit: 500, mailbox });
      toast.success(`Synced ${r.matched} emails (${r.new_notes} new to timeline)`);
      await fetchEmails();
    } catch (e) {
      toast.error("Sync failed: " + (e.response?.data?.detail || e.message));
    } finally { setSyncing(false); }
  };

  const filteredEmails = useMemo(() => {
    const all = data?.emails || [];
    if (folderFilter === "all") return all;
    if (folderFilter === "Inbox")      return all.filter(e => (e.folder_name || "") === "Inbox");
    if (folderFilter === "Done")       return all.filter(e => (e.folder_name || "").startsWith("Inbox / Done"));
    if (folderFilter === "Sent Items") return all.filter(e => (e.folder_name || "").startsWith("Sent Items"));
    return all;
  }, [data, folderFilter]);

  return (
    <Card className="h-full flex flex-col" data-testid="customer-file-outlook-pane">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-sm flex flex-wrap items-center gap-2 min-w-0">
            <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <span className="flex-shrink-0">Outlook</span>
            {isLegalMode && (
              <span className="flex items-center gap-0.5 border rounded-md p-0.5 bg-slate-50" data-testid="inbox-mailbox-switcher">
                {[{ id: "accounts", label: "accounts@" }, { id: "legal", label: "legal@" }].map(mb => (
                  <button
                    key={mb.id}
                    onClick={() => switchMailbox(mb.id)}
                    className={`px-2 py-0.5 text-[10px] rounded transition ${mailbox === mb.id ? "bg-slate-900 text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
                    data-testid={`inbox-mailbox-${mb.id}`}
                  >
                    {mb.label}
                  </button>
                ))}
              </span>
            )}
            <Badge variant="outline" className="text-[10px] font-normal">{(data?.emails || []).length} emails</Badge>
            {data?.borrower_email && <Badge variant="outline" className="text-[10px] font-mono truncate max-w-[180px]" title={data.borrower_email}>{data.borrower_email}</Badge>}
          </CardTitle>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setSearchOpen(true)} data-testid="customer-file-email-search" title="Search inbox">
              <Search className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={handleSync} disabled={syncing} data-testid="customer-file-email-sync" title="Sync history">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRightCircle className="w-3.5 h-3.5" />}
            </Button>
            <Button size="sm" variant="default" className="h-7 px-2 text-[11px]" onClick={() => { setComposeOpen(true); setIssueOpen(false); setReplyTo(null); }} data-testid="customer-file-email-compose" title="Compose new email">
              <Mail className="w-3 h-3 mr-1" /> Compose
            </Button>
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50" onClick={() => { setIssueOpen(true); setComposeOpen(false); setReplyTo(null); }} data-testid="customer-file-issue-document" title="Issue a formal notice (PDN / DN / Redemption / Settlement…) — template email + case-file attachments">
              <FileText className="w-3 h-3 mr-1" /> Issue document
            </Button>
          </div>
        </div>
        {/* Folder tabs */}
        <div className="flex items-center gap-1 mt-2 text-[11px]">
          {["all", "Inbox", "Done", "Sent Items"].map(f => {
            let cnt;
            if (f === "all") cnt = (data?.emails || []).length;
            else if (f === "Inbox") cnt = (data?.emails || []).filter(e => (e.folder_name || "") === "Inbox").length;
            else if (f === "Done") cnt = (data?.emails || []).filter(e => (e.folder_name || "").startsWith("Inbox / Done")).length;
            else cnt = (data?.emails || []).filter(e => (e.folder_name || "").startsWith(f)).length;
            return (
              <button
                key={f}
                onClick={() => setFolderFilter(f)}
                className={`px-2.5 py-1 rounded ${folderFilter === f ? "bg-blue-100 text-blue-900 font-semibold" : "text-muted-foreground hover:bg-slate-100"}`}
                data-testid={`customer-file-folder-${f.replace(/\s/g, "-")}`}
              >
                {f === "all" ? "All" : f} ({cnt})
              </button>
            );
          })}
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0 min-h-[640px]">
        {err && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded mx-4 my-2 px-3 py-2">{err}</div>}
        {/* Two-pane: list | reading */}
        <div className="grid grid-cols-5 h-full border-t" style={{ minHeight: 600 }}>
          {/* LIST PANE */}
          <div className="col-span-2 border-r overflow-y-auto max-h-[640px]" data-testid="customer-file-email-list">
            {loading ? (
              <div className="text-xs text-muted-foreground py-6 text-center">
                <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Loading…
              </div>
            ) : filteredEmails.length === 0 ? (
              <div className="text-xs text-muted-foreground italic py-6 px-4 text-center">
                No emails in this folder. Click <strong>Sync history</strong> to scan further.
              </div>
            ) : (
              filteredEmails.map(e => (
                <button
                  key={e.id}
                  onClick={() => { setSelectedId(e.id); setReplyTo(null); setComposeOpen(false); setIssueOpen(false); }}
                  className={`w-full text-left px-3 py-2 border-b text-xs hover:bg-slate-50 ${selectedId === e.id ? "bg-blue-50 border-l-4 border-l-blue-500" : ""}`}
                  data-testid={`customer-file-email-${e.id.slice(-8)}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`truncate ${!e.isRead ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                      {(e.from?.emailAddress?.address) || "—"}
                    </span>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleDateString("en-GB") : ""}
                    </span>
                  </div>
                  <div className={`truncate ${!e.isRead ? "font-medium text-slate-900" : "text-slate-700"}`}>
                    {!e.isRead && <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mr-1.5 align-middle" />}
                    {e.subject || "(no subject)"}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className="text-[10px] text-muted-foreground truncate">{e.bodyPreview || ""}</span>
                    <Badge variant="outline" className={`text-[9px] flex-shrink-0 ${(e.folder_name || "").startsWith("Sent") ? "border-indigo-300 text-indigo-700" : "border-emerald-300 text-emerald-700"}`}>
                      {(e.folder_name || "").startsWith("Sent") ? "Sent" : (e.folder_name || "").startsWith("Inbox / Done") ? "Done" : "Inbox"}
                    </Badge>
                  </div>
                </button>
              ))
            )}
          </div>
          {/* READING PANE */}
          <div className="col-span-3 overflow-y-auto max-h-[640px] bg-slate-50/20" data-testid="customer-file-email-reading">
            {issueOpen ? (
              <IssueDocumentComposer
                caseRow={caseRow}
                mailbox={mailbox}
                borrowerEmail={data?.borrower_email}
                onClose={() => setIssueOpen(false)}
                onSent={async () => { setIssueOpen(false); await fetchEmails(); }}
              />
            ) : composeOpen ? (
              <EmailComposerInline
                caseRow={caseRow}
                mode="new"
                mailbox={mailbox}
                borrowerEmail={data?.borrower_email}
                sfSearched={data?.sf_searched}
                onClose={() => setComposeOpen(false)}
                onSent={async () => { setComposeOpen(false); await fetchEmails(); }}
              />
            ) : replyTo ? (
              <EmailComposerInline
                caseRow={caseRow}
                mode={replyTo}
                mailbox={mailbox}
                borrowerEmail={data?.borrower_email}
                sfSearched={data?.sf_searched}
                onClose={() => setReplyTo(null)}
                onSent={async () => { setReplyTo(null); await fetchEmails(); }}
              />
            ) : selectedId ? (
              <EmailReadingPane
                caseRow={caseRow}
                messageId={selectedId}
                mailbox={mailbox}
                onReply={(kind) => {
                  const e = (data?.emails || []).find(x => x.id === selectedId);
                  if (!e) return;
                  setReplyTo({ kind: "reply", messageId: e.id, originalSubject: e.subject, originalFrom: e.from?.emailAddress?.address, replyAll: kind === "replyAll" });
                }}
              />
            ) : (
              <div className="text-xs text-muted-foreground py-8 text-center">
                Select an email to read it here.
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <SearchInboxDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </Card>
  );
}

// ─── Reading pane — shows ONE email full HTML body + Reply / Reply-all ────
function EmailReadingPane({ caseRow, messageId, onReply }) {
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingToFiles, setSavingToFiles] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await getAgeingM365EmailFull(caseRow.case_id, messageId);
        if (alive) { setMsg(r.message); setErr(null); }
      } catch (e) {
        if (alive) setErr(e.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [caseRow.case_id, messageId]);

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground py-8 text-center">
        <Loader2 className="w-3 h-3 animate-spin inline mr-1.5" /> Loading…
      </div>
    );
  }
  if (err) return <div className="text-[12px] text-red-700 bg-red-50 m-4 px-3 py-2 rounded">{err}</div>;
  if (!msg) return null;

  const sender = msg.from?.emailAddress;
  const to = (msg.toRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
  const cc = (msg.ccRecipients || []).map(r => r.emailAddress?.address).filter(Boolean);
  const bodyHtml = msg.body?.contentType === "html" ? msg.body.content : `<pre style="white-space: pre-wrap; font-family: inherit;">${(msg.body?.content || "").replace(/[<>]/g, "")}</pre>`;

  return (
    <div className="p-4 space-y-3" data-testid="customer-file-email-body">
      <div className="border-b pb-3">
        <h3 className="text-base font-semibold mb-2">{msg.subject || "(no subject)"}</h3>
        <div className="text-xs space-y-0.5">
          <div><span className="text-muted-foreground">From: </span><span className="font-medium">{sender?.name} &lt;{sender?.address}&gt;</span></div>
          <div><span className="text-muted-foreground">To: </span>{to.join(", ")}</div>
          {cc.length > 0 && <div><span className="text-muted-foreground">Cc: </span>{cc.join(", ")}</div>}
          <div><span className="text-muted-foreground">Date: </span>{msg.receivedDateTime ? new Date(msg.receivedDateTime).toLocaleString("en-GB") : "—"}</div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onReply("reply")} data-testid="customer-file-email-reply">
          ↩ Reply
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => onReply("replyAll")} data-testid="customer-file-email-reply-all">
          ↩↩ Reply all
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
          disabled={savingToFiles}
          onClick={async () => {
            setSavingToFiles(true);
            try {
              const r = await saveEmailToFiles(caseRow.case_id, messageId);
              const att = r.files.filter(f => f.original_filename && !f.original_filename.endsWith(".pdf")).length;
              const created = r.created_folder ? " · created customer folder on OneDrive" : "";
              const fails = (r.failures || []).length;
              toast.success(
                `Saved to Files — 1 PDF${att ? ` + ${att} attachment${att === 1 ? "" : "s"}` : ""}${created}`,
                { description: fails ? `${fails} attachment${fails === 1 ? "" : "s"} failed — see backend logs` : undefined, duration: 8000 },
              );
              // Nudge the Files card (sibling) to refresh — sibling
              // components don't share state; this CustomEvent is
              // listened for in FilesCard's effect.
              window.dispatchEvent(new CustomEvent("portal:files-changed", {
                detail: { case_id: caseRow.case_id, source: "email_save" },
              }));
            } catch (e) {
              toast.error("Save to Files failed: " + (e.response?.data?.detail || e.message));
            } finally {
              setSavingToFiles(false);
            }
          }}
          data-testid="customer-file-email-save-to-files"
          title="Render this email + attachments to PDF, push to the customer's OneDrive folder, and show them in the Files tab."
        >
          {savingToFiles
            ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            : <FileText className="w-3 h-3 mr-1" />}
          Save to Files
        </Button>
        {msg.webLink && (
          <a href={msg.webLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs ml-auto">
            Open in Outlook ↗
          </a>
        )}
      </div>

      <div className="bg-white border rounded p-4 text-xs prose prose-sm max-w-none overflow-x-auto" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </div>
  );
}

// ─── Email recipient picker — mirrors PhonePicker for SMS ─────────────────
// Auto-populates from borrower + additional_contacts on the case (server
// resolves the fresh list on every open). Falls back to a plain text
// input if no contacts exist so ops can still send to an ad-hoc address.
function EmailPicker({ caseId, value, onChange, testId }) {
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customMode, setCustomMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await getCaseContactEmails(caseId);
        setContacts(r?.contacts || []);
      } catch {
        setContacts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [caseId]);

  const norm = (e) => String(e || "").trim().toLowerCase();
  const currentMatch = (contacts || []).find(c => norm(c.email) === norm(value));

  if (customMode || (!loading && (contacts || []).length === 0)) {
    return (
      <div className="flex items-center gap-1">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="recipient@example.com (comma-separated for multiple)"
          className="h-9 text-xs flex-1"
          data-testid={testId}
        />
        {(contacts || []).length > 0 && (
          <Button variant="ghost" size="sm" className="h-9 px-2 text-[10px]" onClick={() => setCustomMode(false)}>
            ← from list
          </Button>
        )}
      </div>
    );
  }

  return (
    <Select
      value={currentMatch ? norm(currentMatch.email) : "__custom__"}
      onValueChange={(v) => {
        if (v === "__custom__") { setCustomMode(true); return; }
        const opt = (contacts || []).find(c => norm(c.email) === v);
        if (opt) onChange(opt.email);
      }}
      disabled={loading}
    >
      <SelectTrigger className="h-9 text-xs" data-testid={testId}>
        <SelectValue placeholder={loading ? "Loading contacts…" : "Pick a recipient"}>
          {currentMatch ? (
            <span className="truncate">
              <span className="font-medium">{currentMatch.label}</span>
              <span className="text-muted-foreground"> · {currentMatch.role}</span>
              <span className="text-muted-foreground"> · {currentMatch.email}</span>
            </span>
          ) : value ? (
            <span>📝 Custom · {value}</span>
          ) : null}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="max-w-[520px]">
        {(contacts || []).map(c => (
          <SelectItem key={norm(c.email)} value={norm(c.email)} className="text-xs">
            <div className="flex items-center gap-2 max-w-full">
              <span className="font-medium truncate">{c.label}</span>
              <span className="text-[10px] uppercase text-muted-foreground tracking-wide">{c.role}</span>
              <span className="text-muted-foreground truncate">{c.email}</span>
              {c.primary && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1 rounded">primary</span>}
            </div>
          </SelectItem>
        ))}
        <SelectItem value="__custom__" className="text-xs italic">
          📝 Custom recipient — type manually
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

// ─── Inline email composer (for reading-pane mode) ─────────────────────────
// Mirrors the SMS composer:
//   • Chaser email template dropdown (grouped 1-4, tones a/b/c, editable
//     labels persisted in Admin Settings — same shape as SMS).
//   • Placeholder chip strip → cursor-aware insertion into subject OR body,
//     whichever was last focused.
//   • Live "as customer will see it" preview, server-side rendered so
//     preview + send never drift. Warns on unsupported `{tokens}`.
//   • Reply mode keeps the original threaded subject (Re: …) — picking a
//     template updates the body only, per user spec.
function EmailComposerInline({ caseRow, mode, mailbox = "accounts", borrowerEmail, sfSearched, onClose, onSent }) {
  const isReply = mode?.kind === "reply";
  const [to, setTo] = useState(borrowerEmail || "");
  const [subject, setSubject] = useState(
    isReply
      ? (mode.originalSubject?.startsWith("Re:") ? mode.originalSubject : `Re: ${mode.originalSubject || ""}`)
      : (sfSearched ? `Re: ${sfSearched} — ` : "")
  );
  const [body, setBody] = useState("");
  const [replyAll, setReplyAll] = useState(mode?.replyAll || false);
  const [sending, setSending] = useState(false);
  const [originalBody, setOriginalBody] = useState("");

  // Template + placeholder state — same shape as the SMS composer.
  const [templates, setTemplates]           = useState([]);
  const [placeholders, setPlaceholders]     = useState([]);
  const [groupLabels, setGroupLabels]       = useState({});
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [pickedTemplateId, setPickedTemplateId] = useState("");

  // Cursor-aware chip insertion — remembers which of the two textareas
  // (subject / body) was last focused so chip clicks land in the right one.
  const subjectRef = useRef(null);
  const bodyRef    = useRef(null);
  const [lastFocused, setLastFocused] = useState("body");

  // Debounced preview state — hydrated against this case server-side.
  const [preview, setPreview]         = useState({ subject: "", body: "", unsupported: [] });
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    if (!isReply || !mode.messageId) return;
    (async () => {
      try {
        const r = await getAgeingM365EmailFull(caseRow.case_id, mode.messageId, mailbox);
        setOriginalBody(r.message?.body?.content || "");
      } catch (e) { /* non-fatal */ }
    })();
  }, [isReply, mode, caseRow.case_id, mailbox]);

  // Fetch templates once — used by both compose + reply modes.
  useEffect(() => {
    (async () => {
      try {
        const r = await listChaserEmailTemplates({ onlyActive: true });
        setTemplates(r.templates || []);
        setPlaceholders(r.placeholders || []);
        setGroupLabels(r.group_labels || {});
      } catch (e) {
        console.warn("Could not load email templates:", e?.message);
      } finally {
        setTemplatesLoading(false);
      }
    })();
  }, []);

  // Debounced live preview — hydrates {placeholders} in subject + body
  // via the same backend renderer used at send-time.
  useEffect(() => {
    const rawSubj = subject.trim();
    const rawBody = body.trim();
    if (!rawSubj && !rawBody) {
      setPreview({ subject: "", body: "", unsupported: [] });
      return;
    }
    let cancelled = false;
    setPreviewBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await previewChaserEmailForCase(caseRow.case_id, { subject, body });
        if (cancelled) return;
        setPreview({
          subject:     r.subject || "",
          body:        r.body    || "",
          unsupported: r.unsupported_tokens || [],
        });
      } catch (e) {
        if (!cancelled) console.warn("Email preview failed:", e?.message);
      } finally {
        if (!cancelled) setPreviewBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [subject, body, caseRow.case_id]);

  // Group templates by chaser # for the dropdown. Sort inside each group
  // by tone (a/b/c). Matches the SMS composer exactly.
  const groupedTemplates = useMemo(() => {
    const groups = {};
    (templates || []).forEach(t => {
      const g = t.group || "?";
      if (!groups[g]) groups[g] = [];
      groups[g].push(t);
    });
    Object.values(groups).forEach(arr => arr.sort((a, b) => (a.tone || "").localeCompare(b.tone || "")));
    return Object.entries(groups).sort(([a], [b]) => Number(a) - Number(b));
  }, [templates]);

  // ── Template pick — server-side render against this case, drop body
  // into the textarea. In compose mode we also replace the subject; in
  // reply mode we DON'T overwrite the threaded "Re: …" subject (per user).
  const handleTemplatePick = async (templateId) => {
    if (!templateId) return;
    setPickedTemplateId(templateId);
    try {
      const r = await renderChaserEmailForCase(caseRow.case_id, templateId);
      setBody(r.body || "");
      if (!isReply) setSubject(r.subject || "");
      toast.success(`Loaded ${r.name}${isReply ? " (body only — thread subject kept)" : ""}`);
    } catch (e) {
      toast.error("Failed to load template: " + (e?.response?.data?.detail || e?.message));
      setPickedTemplateId("");
    }
  };

  // Insert a placeholder token at the cursor of whichever textarea was
  // last focused. Both subject and body support placeholders.
  const insertPlaceholder = (token) => {
    const which = lastFocused === "subject" && !isReply ? "subject" : "body";
    const el    = which === "subject" ? subjectRef.current : bodyRef.current;
    const cur   = which === "subject" ? subject : body;
    const setter = which === "subject" ? setSubject : setBody;
    if (!el) { setter(prev => prev + token); return; }
    const start = el.selectionStart ?? cur.length;
    const end   = el.selectionEnd   ?? cur.length;
    setter(cur.slice(0, start) + token + cur.slice(end));
    setTimeout(() => {
      if (el && document.body.contains(el)) {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleSend = async () => {
    if (isReply) {
      if (!body.trim()) { toast.error("Reply body is empty."); return; }
      setSending(true);
      try {
        await replyAgeingM365Email(caseRow.case_id, mode.messageId, { body, reply_all: replyAll, actor: "Ops" });
        toast.success(`Reply${replyAll ? "-All" : ""} sent`);
        await onSent();
      } catch (e) {
        toast.error("Reply failed: " + (e.response?.data?.detail || e.message));
      } finally { setSending(false); }
    } else {
      if (!to.trim() || !subject.trim() || !body.trim()) { toast.error("To, subject and body are all required."); return; }
      setSending(true);
      try {
        await sendAgeingM365Email(caseRow.case_id, {
          to: to.split(",").map(s => s.trim()).filter(Boolean),
          subject, body, content_type: "HTML", actor: "Ops", mailbox,
        });
        toast.success("Email sent");
        await onSent();
      } catch (e) {
        toast.error("Send failed: " + (e.response?.data?.detail || e.message));
      } finally { setSending(false); }
    }
  };

  return (
    <div className="p-4 space-y-3" data-testid="customer-file-email-composer">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">
          {isReply ? (replyAll ? "Reply all" : "Reply") + ` to ${mode.originalFrom}` : "New email"}
        </h3>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </div>

      {!isReply && (
        <>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">To</label>
            <EmailPicker
              caseId={caseRow.case_id}
              value={to}
              onChange={setTo}
              testId="customer-file-email-to"
            />
          </div>
          <Input
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => setLastFocused("subject")}
            placeholder="Subject"
            className="h-9 text-xs"
            data-testid="customer-file-email-subject"
          />
        </>
      )}
      {isReply && (
        <div className="text-xs"><span className="text-muted-foreground">Subject: </span><span className="font-mono">{subject}</span></div>
      )}

      {/* ── Template picker + placeholder chips ────────────────────── */}
      <div className="space-y-1.5 rounded-md border border-blue-200 bg-blue-50/40 p-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
            Use template
          </label>
          <Select value={pickedTemplateId} onValueChange={handleTemplatePick}>
            <SelectTrigger
              className="h-7 text-[11px] w-[260px] bg-white"
              data-testid="email-template-picker"
            >
              <SelectValue placeholder={templatesLoading ? "Loading…" : "— Pick an email chaser —"} />
            </SelectTrigger>
            <SelectContent>
              {groupedTemplates.map(([grp, tpls]) => (
                <div key={grp}>
                  <div className="px-2 py-1 text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">
                    {groupLabels[String(grp)] || `Chaser ${grp}`}
                  </div>
                  {tpls.map(t => (
                    <SelectItem
                      key={t.template_id}
                      value={t.template_id}
                      data-testid={`email-template-opt-${t.template_id}`}
                    >
                      {t.name} · <span className="capitalize text-muted-foreground">{t.tone}</span>
                    </SelectItem>
                  ))}
                </div>
              ))}
              {!templatesLoading && templates.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-muted-foreground italic">
                  No templates yet — set them up in Admin Settings.
                </div>
              )}
            </SelectContent>
          </Select>
          {pickedTemplateId && (
            <button
              type="button"
              onClick={() => { setPickedTemplateId(""); setBody(""); if (!isReply) setSubject(""); }}
              className="text-[10px] text-slate-500 hover:text-rose-700 underline underline-offset-2"
              data-testid="email-template-clear"
            >
              clear
            </button>
          )}
          {isReply && (
            <span className="text-[10px] text-muted-foreground italic">
              (thread subject kept · template updates body only)
            </span>
          )}
        </div>

        {placeholders.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap" data-testid="email-placeholder-chips">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">Insert:</span>
            {placeholders.map(p => (
              <button
                key={p.key}
                type="button"
                onClick={() => insertPlaceholder(p.key)}
                className="px-1.5 py-0.5 rounded border border-blue-200 bg-white hover:bg-blue-100 text-[10px] font-mono text-blue-700 transition-colors"
                title={`${p.label} — example: ${p.example}${isReply ? " · inserts into body" : " · click to insert into whichever field is focused"}`}
                data-testid={`email-placeholder-${p.key.replace(/[{}]/g, "")}`}
              >
                {p.key}
              </button>
            ))}
          </div>
        )}
      </div>

      <Textarea
        ref={bodyRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        onFocus={() => setLastFocused("body")}
        rows={10}
        placeholder={isReply ? "Type your reply…" : "Message body… (Arial 11pt when delivered)"}
        className="text-xs bg-white"
        style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "11pt", lineHeight: 1.4 }}
        data-testid="customer-file-email-body-input"
      />

      {/* ── Live preview — server-side rendered ─────────────────────── */}
      {(subject.trim() || body.trim()) && (
        <div
          className="rounded-md border border-blue-200 bg-white/70 px-2.5 py-2 space-y-1.5"
          data-testid="email-preview-panel"
        >
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold">
              Preview — as customer will see it
            </span>
            {previewBusy && <Loader2 className="w-3 h-3 animate-spin text-blue-400" />}
          </div>
          {!isReply && (
            <div className="text-[11px]" data-testid="email-preview-subject">
              <span className="text-muted-foreground uppercase tracking-wide text-[9px] mr-1">Subject:</span>
              <span className="font-semibold text-slate-800">{preview.subject || subject}</span>
            </div>
          )}
          <div
            className="text-[11px] leading-snug text-slate-800 whitespace-pre-wrap border-t border-blue-100 pt-1.5"
            style={{ fontFamily: "Arial, Helvetica, sans-serif", fontSize: "11pt", lineHeight: 1.4 }}
            data-testid="email-preview-body"
          >
            {preview.body || <span className="italic text-slate-400">rendering…</span>}
          </div>
          {preview.unsupported.length > 0 && (
            <div
              className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-1"
              data-testid="email-preview-unsupported-warning"
            >
              ⚠️ Unsupported token{preview.unsupported.length > 1 ? "s" : ""}:{" "}
              {preview.unsupported.map(t => (
                <code key={t} className="font-mono">{`{${t}}`}</code>
              )).reduce((prev, curr) => prev === null ? [curr] : [...prev, ", ", curr], null)}
              . These will send literally — use the chips above instead.
            </div>
          )}
        </div>
      )}

      {isReply && (
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} className="rounded" />
          Reply to all recipients
        </label>
      )}
      {isReply && originalBody && (
        <details className="border rounded p-2 bg-white text-xs">
          <summary className="cursor-pointer text-muted-foreground text-[11px]">Show original message</summary>
          <div className="mt-2 max-h-[200px] overflow-y-auto" dangerouslySetInnerHTML={{ __html: originalBody }} />
        </details>
      )}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] text-muted-foreground">Sending as <span className="font-mono">accounts@simplyfunded.co.uk</span> · Arial 11pt · signature auto-appended</span>
        <Button size="sm" onClick={handleSend} disabled={sending} className="h-9 text-xs" data-testid="customer-file-email-send">
          {sending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
          {isReply ? (replyAll ? "Send reply-all" : "Send reply") : "Send email"}
        </Button>
      </div>
    </div>
  );
}

// ─── Unified Audit Log (full width, filterable by type) ────────────────────
const TYPE_META = {
  note:            { icon: "📝", color: "bg-slate-100 text-slate-700" },
  call:            { icon: "📞", color: "bg-emerald-100 text-emerald-700" },
  sms:             { icon: "💬", color: "bg-sky-100 text-sky-700" },
  email:           { icon: "📧", color: "bg-blue-100 text-blue-700" },
  stage_change:    { icon: "🚦", color: "bg-amber-100 text-amber-800" },
  handler_change:  { icon: "👤", color: "bg-indigo-100 text-indigo-700" },
};

function AuditLogPanel({ caseRow, onChanged }) {
  const [syncingCalls, setSyncingCalls] = useState(false);
  const [hideSystem, setHideSystem] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const handleSyncCalls = async () => {
    setSyncingCalls(true);
    try {
      const r = await syncAgeingAircallCalls(caseRow.case_id, { limit: 100 });
      toast.success(`Synced ${r.fetched} calls (${r.new_notes} new to timeline)`);
      await onChanged();
    } catch (e) {
      toast.error("Call sync failed: " + (e.response?.data?.detail || e.message));
    } finally { setSyncingCalls(false); }
  };

  const handleDelete = async (noteId) => {
    if (!noteId) return;
    setDeleting(noteId);
    try {
      await deleteAgeingNote(caseRow.case_id, noteId);
      await onChanged();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    } finally { setDeleting(null); }
  };

  const notes = caseRow.notes || [];
  const stageHistory = (caseRow.stage_history || []).map(s => ({
    id:        `stage-${s.set_at}`,
    timestamp: s.set_at,
    author:    s.set_by || "system",
    type:      "stage_change",
    text:      `Stage set to "${s.stage}"`,
    _system:   true,
  }));
  const combined = useMemo(() => {
    const all = [...notes, ...stageHistory].sort((a, b) =>
      String(b.timestamp || "").localeCompare(String(a.timestamp || "")));
    return hideSystem ? all.filter(n => n.author !== "system" && !n._system) : all;
  }, [notes, stageHistory, hideSystem]);

  const [filter, setFilter] = useState("all");
  const counts = useMemo(() => {
    const c = { all: combined.length };
    for (const n of combined) c[n.type] = (c[n.type] || 0) + 1;
    return c;
  }, [combined]);

  const visible = filter === "all" ? combined : combined.filter(n => n.type === filter);

  return (
    <Card data-testid="customer-file-audit-log">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <Filter className="w-4 h-4 text-violet-600" />
            Audit log <Badge variant="outline" className="text-[10px] font-normal">{combined.length} events</Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer text-muted-foreground hover:text-foreground">
              <input type="checkbox" checked={hideSystem} onChange={(e) => setHideSystem(e.target.checked)} className="rounded" data-testid="customer-file-audit-hide-system" />
              Hide system events
            </label>
            <Button size="sm" variant="outline" onClick={handleSyncCalls} disabled={syncingCalls} className="h-7 px-2 text-[11px]" data-testid="customer-file-audit-sync-calls">
              {syncingCalls ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
              Sync calls
            </Button>
          </div>
        </div>
        {/* Type filters */}
        <div className="flex items-center gap-1 mt-2 text-[11px] flex-wrap">
          {["all", "email", "call", "sms", "note", "stage_change", "handler_change"].map(t => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-2.5 py-1 rounded ${filter === t ? "bg-violet-100 text-violet-900 font-semibold" : "text-muted-foreground hover:bg-slate-100"}`}
              data-testid={`customer-file-audit-filter-${t}`}
            >
              {t === "all" ? "All" : t.replace("_", " ")} ({counts[t] || 0})
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {visible.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-4 text-center">No events of this type yet.</div>
        ) : (
          <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {visible.map(n => {
              const meta = TYPE_META[n.type] || TYPE_META.note;
              const isStageHist = n._system;
              return (
                <li key={n.id} className="ml-4 relative group" data-testid={`customer-file-audit-event-${n.type}`}>
                  <span className={`absolute -left-[26px] flex items-center justify-center w-5 h-5 rounded-full text-[10px] ${meta.color} border-2 border-white`}>
                    {meta.icon}
                  </span>
                  <div className="text-xs">
                    <div className="flex items-center gap-2 mb-0.5">
                      <Badge variant="outline" className={`text-[10px] ${meta.color} border-current`}>{n.type.replace("_", " ")}</Badge>
                      <span className="text-muted-foreground">{n.author}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">{n.timestamp ? new Date(n.timestamp).toLocaleString("en-GB") : "—"}</span>
                      {!isStageHist && (
                        <button
                          onClick={() => handleDelete(n.id)}
                          disabled={deleting === n.id}
                          className="ml-auto opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700"
                          title="Delete this event"
                          data-testid="customer-file-audit-delete"
                        >
                          {deleting === n.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                    <div className="text-slate-800 whitespace-pre-wrap">{n.text}</div>
                    {n.recording && (
                      <audio
                        controls
                        preload="none"
                        src={n.recording}
                        className="w-full h-7 mt-1.5 max-w-[480px]"
                        data-testid={`audit-call-audio-${n.id}`}
                        style={{ accentColor: "#4f46e5" }}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}


// ── P2P Calendar — month-grid showing P2P promises by date ────────────────
function P2PCalendar({ caseRow }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const p2ps = caseRow.p2ps || [];

  // Group P2Ps by ISO date (YYYY-MM-DD). Use local-time formatting — the
  // `promised_at` strings on the case are already local YYYY-MM-DD, so
  // toISOString() on grid Date objects would shift the key by 1 day in
  // any UTC+N timezone. Build the key from local Y-M-D instead.
  const localKey = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const byDate = useMemo(() => {
    const m = {};
    for (const p of p2ps) {
      const k = (p.promised_at || "").slice(0, 10);
      if (!k) continue;
      (m[k] ||= []).push(p);
    }
    return m;
  }, [p2ps]);

  // Build grid days starting from the Sunday of week-1 of the month
  const monthStart = cursor;
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const startDow = monthStart.getDay();  // 0=Sun
  const gridStart = new Date(year, month, 1 - startDow);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const monthLabel = monthStart.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const todayKey = localKey(new Date());
  const prevMonth = () => setCursor(new Date(year, month - 1, 1));
  const nextMonth = () => setCursor(new Date(year, month + 1, 1));

  // Status counts per date for tone selection
  const toneForDate = (key) => {
    const arr = byDate[key] || [];
    if (arr.length === 0) return null;
    if (arr.some(p => (p.status || "pending") === "broken"))    return "bg-rose-500 text-white";
    if (arr.every(p => (p.status || "pending") === "kept"))     return "bg-emerald-500 text-white";
    if (arr.some(p => (p.status || "pending") === "cancelled")) return "bg-slate-400 text-white";
    return "bg-amber-500 text-white";  // pending (default)
  };

  return (
    <Card data-testid="customer-file-p2p-calendar">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            P2P Calendar
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={prevMonth} data-testid="p2p-cal-prev">‹</Button>
            <span className="text-xs font-medium text-slate-700 w-32 text-center">{monthLabel}</span>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={nextMonth} data-testid="p2p-cal-next">›</Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-3">
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wide text-muted-foreground text-center mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d, i) => {
            const inMonth = d.getMonth() === month;
            const key = localKey(d);
            const tone = toneForDate(key);
            const arr = byDate[key] || [];
            const isToday = key === todayKey;
            return (
              <div
                key={i}
                title={arr.length ? arr.map(p => `${(p.status || "pending").toUpperCase()} · ${cur()}${(p.promised_amount || 0).toLocaleString()} (${p.method || "-"})`).join("\n") : ""}
                className={`aspect-square rounded text-[11px] flex items-center justify-center font-medium relative ${
                  tone || (inMonth ? "bg-slate-50 text-slate-700" : "bg-transparent text-slate-300")
                } ${isToday && !tone ? "ring-1 ring-blue-400" : ""}`}
                data-testid={arr.length ? `p2p-cal-day-${key}` : undefined}
              >
                {d.getDate()}
                {arr.length > 1 && (
                  <span className="absolute top-0 right-0.5 text-[8px] bg-white/80 text-slate-900 rounded-full px-1 font-bold">{arr.length}</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center justify-center gap-3 text-[10px] text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-amber-500" /> Pending</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-emerald-500" /> Kept</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-rose-500" /> Broken</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded bg-slate-400" /> Cancelled</span>
        </div>
      </CardContent>
    </Card>
  );
}


// ── P2P Card — left rail in CustomerFile, sibling of Contacts / Notes ──
const P2P_STATUS_META = {
  pending:   { tone: "bg-amber-100 text-amber-900 border-amber-300",       icon: CalendarClock,   label: "Pending" },
  kept:      { tone: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2,    label: "Kept" },
  broken:    { tone: "bg-red-100 text-red-900 border-red-300",             icon: AlertOctagon,    label: "Broken" },
  cancelled: { tone: "bg-slate-100 text-slate-700 border-slate-300",       icon: Ban,             label: "Cancelled" },
};

const fmtGBP_p2p = (v) => fmtMoney(v || 0, 0);

function P2PCard({ caseRow, onChanged }) {
  const [showAdd, setShowAdd] = useState(false);
  const [date,    setDate]    = useState("");
  const [amount,  setAmount]  = useState("");
  const [method,  setMethod]  = useState("bank_transfer");
  const [note,    setNote]    = useState("");
  const [busy,    setBusy]    = useState(false);

  const p2ps = [...(caseRow.p2ps || [])].sort((a, b) => (a.promised_at || "").localeCompare(b.promised_at || ""));
  const today = new Date().toISOString().slice(0, 10);

  const reset = () => { setDate(""); setAmount(""); setMethod("bank_transfer"); setNote(""); setShowAdd(false); };

  const add = async () => {
    if (!date || !amount) { toast.error("Date and amount required"); return; }
    try {
      setBusy(true);
      await createAgeingP2P(caseRow.case_id, {
        promised_at:     date,
        promised_amount: parseFloat(amount),
        method,
        notes:           note,
        created_by:      "Ops",
      });
      toast.success("Promise to Pay scheduled");
      reset();
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="customer-file-p2p-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-amber-600" />
            Promises to Pay
            {p2ps.length > 0 && <Badge variant="outline" className="text-[10px] font-normal">{p2ps.length}</Badge>}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2.5 text-sm">
        {!showAdd ? (
          <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => { setShowAdd(true); setDate(today); }} data-testid="p2p-add-btn">
            <CalendarClock className="w-3.5 h-3.5 mr-1.5" /> Schedule a P2P
          </Button>
        ) : (
          <div className="border rounded-md p-3 bg-amber-50/30 space-y-2" data-testid="p2p-add-form">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] uppercase">Promised date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="p2p-date" />
              </div>
              <div>
                <Label className="text-[10px] uppercase">Amount ({cur()})</Label>
                <Input type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2500" className="h-8 text-xs" data-testid="p2p-amount" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase">Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="h-8 text-xs" data-testid="p2p-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Online transfer</SelectItem>
                  <SelectItem value="direct_debit">Direct Debit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Optional context" className="text-xs" data-testid="p2p-note" />
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={reset} className="text-xs">Cancel</Button>
              <Button size="sm" onClick={add} disabled={!date || !amount || busy} className="text-xs flex-1" data-testid="p2p-save-btn">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Send className="w-3 h-3 mr-1.5" />} Save P2P
              </Button>
            </div>
          </div>
        )}

        {p2ps.length === 0 ? (
          <div className="text-center py-3 text-[11px] text-muted-foreground italic">No promises to pay logged yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="p2p-list">
            {p2ps.map((p) => (
              <P2PRow key={p.id} p={p} caseId={caseRow.case_id} today={today} onChanged={onChanged} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}


// ── Single P2P row with status dropdown + edit + delete + reschedule ───────
function P2PRow({ p, caseId, today, onChanged }) {
  const [editOpen, setEditOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const status = p.status || "pending";
  const meta = P2P_STATUS_META[status];
  const Icon = meta.icon;
  const overdue = status === "pending" && p.promised_at < today;

  const setStatus = async (newStatus) => {
    try {
      setBusy(true);
      const patch = { status: newStatus };
      if (newStatus === "kept") patch.kept_amount = p.promised_amount;
      await updateAgeingP2P(caseId, p.id, patch);
      toast.success(`P2P marked ${newStatus}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this P2P (${cur()}${p.promised_amount?.toLocaleString()} on ${p.promised_at})? This is permanent.`)) return;
    try {
      await deleteAgeingP2P(caseId, p.id);
      toast.success("P2P deleted");
      onChanged && onChanged();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <li className={`border rounded-md p-2 text-xs ${meta.tone}`} data-testid={`p2p-${p.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <Icon className="w-3 h-3 shrink-0" />
            <span className="font-semibold">{fmtGBP_p2p(p.promised_amount)}</span>
            <span className="text-[10px] opacity-80">on {ukDate(p.promised_at)}</span>
            {overdue && <Badge className="bg-red-600 text-white text-[9px] px-1.5 h-4">overdue</Badge>}
            {p.rescheduled_from && <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[9px] px-1.5 h-4" title="Rescheduled from a missed P2P">↻ rescheduled</Badge>}
          </div>
          <div className="text-[10px] opacity-70 mt-0.5">
            via {p.method?.replace("_", " ")} · {meta.label}
            {p.kept_amount != null && status === "kept" && <> · received {fmtGBP_p2p(p.kept_amount)}</>}
          </div>
          {p.notes && <div className="text-[11px] mt-1 italic opacity-90">&ldquo;{p.notes}&rdquo;</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={busy} className="h-6 text-[10px] px-1.5 bg-white" data-testid={`p2p-${p.id}-status-menu`}>
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Status <ChevronDown className="w-2.5 h-2.5 ml-0.5" /></>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px]">Mark this P2P as…</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setStatus("kept")} className="text-[11px]" data-testid={`p2p-${p.id}-mark-kept`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Kept — paid as promised
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatus("broken")} className="text-[11px]" data-testid={`p2p-${p.id}-mark-broken`}>
                <XCircle className="w-3.5 h-3.5 mr-2 text-rose-600" /> Missed — no reschedule
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setRescheduleOpen(true)} className="text-[11px]" data-testid={`p2p-${p.id}-reschedule`}>
                <RefreshCw className="w-3.5 h-3.5 mr-2 text-amber-600" /> Missed → New P2P
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setStatus("cancelled")} className="text-[11px]" data-testid={`p2p-${p.id}-mark-cancelled`}>
                <Ban className="w-3.5 h-3.5 mr-2 text-slate-500" /> Cancel — customer withdrew
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {status === "pending" && (
                <DropdownMenuItem onClick={() => setEditOpen(true)} className="text-[11px]" data-testid={`p2p-${p.id}-edit`}>
                  <Pencil className="w-3.5 h-3.5 mr-2 text-slate-700" /> Edit details
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={remove} className="text-[11px] text-rose-700" data-testid={`p2p-${p.id}-delete`}>
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {editOpen && (
        <P2PEditDialog p={p} caseId={caseId} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onChanged && onChanged(); }} />
      )}
      {rescheduleOpen && (
        <P2PRescheduleDialog p={p} caseId={caseId} onClose={() => setRescheduleOpen(false)} onSaved={() => { setRescheduleOpen(false); onChanged && onChanged(); }} />
      )}
    </li>
  );
}


// ── Edit P2P (pending only) ───────────────────────────────────────────────
function P2PEditDialog({ p, caseId, onClose, onSaved }) {
  const [date,   setDate]   = useState(p.promised_at || "");
  const [amount, setAmount] = useState(String(p.promised_amount ?? ""));
  const [method, setMethod] = useState(p.method || "bank_transfer");
  const [notes,  setNotes]  = useState(p.notes || "");
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!date || !amount) { toast.error("Date and amount required"); return; }
    try {
      setBusy(true);
      await updateAgeingP2P(caseId, p.id, {
        promised_at:     date,
        promised_amount: parseFloat(amount),
        method,
        notes,
      });
      toast.success("P2P updated");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`p2p-edit-dialog-${p.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm">Edit Promise to Pay</DialogTitle>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">Promised date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="p2p-edit-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">Amount ({cur()})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-xs" data-testid="p2p-edit-amount" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Online transfer</SelectItem>
                <SelectItem value="direct_debit">Direct Debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes" className="text-xs" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="p2p-edit-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ── Reschedule P2P (Missed → New P2P) ─────────────────────────────────────
function P2PRescheduleDialog({ p, caseId, onClose, onSaved }) {
  const [date,   setDate]   = useState("");
  const [amount, setAmount] = useState(String(p.promised_amount ?? ""));
  const [method, setMethod] = useState(p.method || "bank_transfer");
  const [notes,  setNotes]  = useState("");
  const [busy,   setBusy]   = useState(false);

  const save = async () => {
    if (!date) { toast.error("New promise date required"); return; }
    try {
      setBusy(true);
      await rescheduleAgeingP2P(caseId, p.id, {
        promised_at:     date,
        promised_amount: parseFloat(amount) || undefined,
        method,
        notes,
        actor:           "Ops",
      });
      toast.success("P2P rescheduled — original marked broken, new promise logged");
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-sm" data-testid={`p2p-reschedule-dialog-${p.id}`}>
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-amber-600" />
            Reschedule missed P2P
          </DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            The original promise ({cur()}{p.promised_amount?.toLocaleString()} on {p.promised_at}) will be marked <strong>broken</strong>, and a new promise created. Both events appear in the timeline.
          </p>
        </DialogHeader>
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase">New promise date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 text-xs" data-testid="p2p-reschedule-date" />
            </div>
            <div>
              <Label className="text-[10px] uppercase">New amount ({cur()})</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-8 text-xs" data-testid="p2p-reschedule-amount" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase">Method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Online transfer</SelectItem>
                <SelectItem value="direct_debit">Direct Debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Reason for reschedule (e.g. 'customer agreed new date after follow-up call')" className="text-xs" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !date} data-testid="p2p-reschedule-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Reschedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

